/**
 * CrazyCribbageApp - Main Application Orchestrator Class
 * Encapsulates game state, lifecycle, subsystem wiring, and DOM binding.
 */

import { Card } from '../models/Card.js';
import { Deck } from '../models/Deck.js';
import { Hand } from '../models/Hand.js';
import { Player, SEAT_COLORS } from '../models/Player.js';
import { Roster } from '../models/Roster.js';
import { CribbageScorer } from '../scoring/CribbageScorer.js';
import * as BoardGeometry from '../board/BoardGeometry.js';
import { BoardRenderer } from '../board/BoardRenderer.js';
import { PegTracker } from '../board/PegTracker.js';
import { GameState } from '../game/GameState.js';
import { GameEngine } from '../game/GameEngine.js';
import { PintsManager, PINTS_OPTIONS_DEFS } from '../variants/PintsManager.js';
import { AdultManager, ADULT_OPTIONS_DEFS } from '../variants/AdultManager.js';
import { ClashManager } from '../variants/ClashManager.js';
import { CribbageAI } from '../ai/CribbageAI.js';
import { ObstacleGeometry } from '../board/ObstacleGeometry.js';
import { AudioManager } from '../audio/AudioManager.js';
import { NetworkManager, ROOM_CODE_CHARS } from '../net/NetworkManager.js';
import { ModalManager } from '../ui/ModalManager.js';
import { SettingsManager } from '../ui/SettingsManager.js';
import { UIController } from '../ui/UIController.js';
import { PintsSettings } from '../variants/PintsSettings.js';
import { AdultSettings } from '../variants/AdultSettings.js';
import { ClashSettings } from '../variants/ClashSettings.js';
import { ClashPlacementModal } from '../variants/ClashPlacementModal.js';
import { ShowModal } from '../ui/ShowModal.js';
import { CelebrationFX } from '../ui/CelebrationFX.js';
import { AchievementManager } from '../achievements/AchievementManager.js';
import { LobbyController } from '../ui/LobbyController.js';
import { ClashMiniGames } from '../variants/ClashMiniGames.js';
import { TableRenderer } from '../ui/TableRenderer.js';
import { DropdownHelpManager } from '../ui/DropdownHelpManager.js';
import { CoachController } from '../ui/CoachController.js';
import { CrazyManager, CRAZY_OPTIONS_DEFS } from '../variants/CrazyManager.js';
import { CrazySettings } from '../variants/CrazySettings.js';
import { DiscardTrainer } from '../trainer/DiscardTrainer.js';
import { PeggingTrainer, PEGGING_CATEGORIES } from '../trainer/PeggingTrainer.js';
import { CountingTrainer } from '../trainer/CountingTrainer.js';
import { CoachPostMortem } from '../coach/CoachPostMortem.js';
import { DailyPuzzleManager, PEGGING_SCENARIOS } from '../trainer/DailyPuzzleManager.js';
import { SeriesTracker } from '../series/SeriesTracker.js';
import { TournamentManager } from '../series/TournamentManager.js';
import { TournamentModal } from '../ui/TournamentModal.js';
import { Board3DCatalog, DEFAULT_3D_BOARDS } from '../board/Board3DCatalog.js';
import { BoardSelectorModal } from '../ui/BoardSelectorModal.js';
import { BoardRenderer3D } from '../board/BoardRenderer3D.js';
import { ReactionManager, REACTION_ITEMS, BANTER_ITEMS } from '../ui/ReactionManager.js';
import { HandScoreTracker } from '../stats/HandScoreTracker.js';
import { HandOfGloryCard } from '../ui/HandOfGloryCard.js';
import { ClashPresets, CLASH_PRESETS_481 } from '../variants/ClashPresets.js';
import { PintsPresets, PINTS_PRESETS_121 } from '../variants/PintsPresets.js';
import { AdultPresets, ADULT_PRESETS_121 } from '../variants/AdultPresets.js';
import { ClashSandboxModal } from '../variants/ClashSandboxModal.js';
import { IndexedDBStorage, appStorage } from '../storage/IndexedDBStorage.js';
import { CardAssassinOverlay } from '../ui/CardAssassinOverlay.js';
import { JailAnimationOverlay } from '../ui/JailAnimationOverlay.js';
import { BarAnimationOverlay } from '../ui/BarAnimationOverlay.js';
import { CannonAnimationOverlay } from '../ui/CannonAnimationOverlay.js';
import { SpeedTrapAnimationOverlay } from '../ui/SpeedTrapAnimationOverlay.js';
import { ScenicRouteAnimationOverlay } from '../ui/ScenicRouteAnimationOverlay.js';
import { GameReplayManager } from '../game/GameReplayManager.js';
import { ReplayModal } from '../ui/ReplayModal.js';
import { AvatarManager, PRESET_AVATARS } from '../ui/AvatarManager.js';
import { Profile } from '../profile/Profile.js';
import { Room3DLoadingOverlay } from '../ui/Room3DLoadingOverlay.js';
import GameOptionsEngineModule from '../../game-options-engine.js';

let GameOptionsEngine = GameOptionsEngineModule;
if (typeof window !== 'undefined' && window.GameOptionsEngine) {
  GameOptionsEngine = window.GameOptionsEngine;
}

const active = {};
const CCProxy = new Proxy({}, {
  get(target, prop) {
    if (typeof window !== 'undefined' && window.CrazyCribbage && window.CrazyCribbage[prop] !== undefined) {
      const val = window.CrazyCribbage[prop];
      return typeof val === 'function' ? val.bind(window.CrazyCribbage) : val;
    }
    const val = active[prop];
    return typeof val === 'function' ? val.bind(active) : val;
  }
});

export const MODULES = {
  ...BoardGeometry,
  getTrackXY: BoardGeometry.getTrackXY,
  Card, Deck, Hand, Player, SEAT_COLORS, Roster, CribbageScorer, combinations, isConsecutiveRun,
  BoardRenderer, PegTracker, GameState, GameEngine, PintsManager, PINTS_OPTIONS_DEFS,
  AdultManager, ADULT_OPTIONS_DEFS, ClashManager, CribbageAI, ObstacleGeometry,
  AudioManager, NetworkManager, ROOM_CODE_CHARS, ModalManager, SettingsManager,
  UIController, PintsSettings, AdultSettings, ClashSettings, ClashPlacementModal,
  ShowModal, CelebrationFX, AchievementManager, LobbyController, ClashMiniGames, TableRenderer, DropdownHelpManager, CoachController,
  CrazyManager, CRAZY_OPTIONS_DEFS, CrazySettings, DiscardTrainer,
  HandScoreTracker, HandOfGloryCard, ClashPresets, CLASH_PRESETS_481, PintsPresets, PINTS_PRESETS_121, AdultPresets, ADULT_PRESETS_121, ClashSandboxModal,
  IndexedDBStorage, appStorage, CardAssassinOverlay, JailAnimationOverlay, BarAnimationOverlay, CannonAnimationOverlay,
  SpeedTrapAnimationOverlay, ScenicRouteAnimationOverlay, GameReplayManager, ReplayModal
};

const CC = (typeof window !== 'undefined' && window.CrazyCribbage) || MODULES;

let _suppressSettingsSave = true;

const RANKS = [1,2,3,4,5,6,7,8,9,10,11,12,13];
const RANK_NAME = {1:'A',2:'2',3:'3',4:'4',5:'5',6:'6',7:'7',8:'8',9:'9',10:'10',11:'J',12:'Q',13:'K'};
const SUITS = ['S','H','D','C'];
const SUIT_SYM = {S:'♠', H:'♥', D:'♦', C:'♣'};
const VAL = r => r >= 10 ? 10 : r;

const BOARD_CFG = (CC && CC.BOARD_CFG) || { viewW: 820, viewH: 216, st1Y: 54, st2Y: 108, st3Y: 162, trackOffset: { p1: -15, p2: -10, p3: -5, p4: 0, p5: 5, p6: 10, p7: 15, p8: 20, you: -6, cpu: 6, team1: -6, team2: 6, team3: 0, team4: 6 }, xStart: 85, xEnd: 715 };
const BOARD_CFG_481 = (CC && CC.BOARD_CFG_481) || { viewW: 820, viewH: 438, stY: [42, 86, 130, 174, 218, 262, 306, 350], finishY: 394, xStart: 75, xEnd: 745 };
function getClashBoardCfg(target) { return (CC && CC.getClashBoardCfg) ? CC.getClashBoardCfg(target) : { numRows: 8, viewW: 820, viewH: 438, stY: [42, 86, 130, 174, 218, 262, 306, 350], finishY: 394, xStart: 75, xEnd: 745 }; }

let pegState = (CC && CC.PegTracker) ? new CC.PegTracker().toPlain() : {
  p1: { front: 0, rear: 0 }, p2: { front: 0, rear: 0 }, p3: { front: 0, rear: 0 }, p4: { front: 0, rear: 0 },
  p5: { front: 0, rear: 0 }, p6: { front: 0, rear: 0 }, p7: { front: 0, rear: 0 }, p8: { front: 0, rear: 0 },
  team1: { front: 0, rear: 0 }, team2: { front: 0, rear: 0 }, team3: { front: 0, rear: 0 }, team4: { front: 0, rear: 0 },
  you: { front: 0, rear: 0 }, cpu: { front: 0, rear: 0 }
};

function getTrackXY(holeNum, who, forcedTarget = null) {
  if (CC?.getTrackXY) {
    let resolvedTarget = null;
    let extraOpts = null;
    if (typeof forcedTarget === 'object' && forcedTarget !== null) {
      resolvedTarget = forcedTarget.target;
      extraOpts = forcedTarget;
    } else if (typeof forcedTarget === 'number') {
      resolvedTarget = forcedTarget;
    }
    resolvedTarget = resolvedTarget || (typeof window !== 'undefined' && window._clashBoardTarget) || settings?.target || (typeof document !== 'undefined' && document.getElementById('optTarget') && parseInt(document.getElementById('optTarget').value, 10)) || 121;
    const brand = typeof getAppBrand === 'function' ? getAppBrand() : 'crazy';
    if (brand === 'clash' && ![241, 361, 481].includes(resolvedTarget)) {
      resolvedTarget = 481;
    }
    const isFixed = typeof isFixedTeams === 'function' ? isFixedTeams() : false;
    const opts = Object.assign({
      playerCount: settings?.playerCount || (isFixed ? 4 : 2),
      isFixedTeams: isFixed,
      getSeatTeam: typeof getSeatTeam === 'function' ? getSeatTeam : null,
      optNoPassingLanes: Boolean(settings?.optNoPassingLanes)
    }, extraOpts || {});
    return CC.getTrackXY(holeNum, who, resolvedTarget, opts);
  }
}

function parseGameMode(val) { return CC?.SettingsManager?.parseGameMode?.(val); }

function serializeGameMode(playerCount, gameMode) { return CC?.SettingsManager?.serializeGameMode?.(playerCount, gameMode); }

function isOnline() { return Boolean(settings && settings.opp === 'online'); }
function isHvH() { return CC?.SettingsManager?.isHvH?.(settings, { getAppBrand, GameOptionsEngine }); }

function isTwoHuman() { return CC && CC.Roster && CC.Roster.isTwoHuman({ isHvH, isOnline }); }

function isSoloVsPairs() { return CC && CC.Roster && CC.Roster.isSoloVsPairs(settings); }

function is3Player1vs2() { return CC && CC.Roster && CC.Roster.is3Player1vs2(settings); }

function is5Player1vs4() { return CC && CC.Roster && CC.Roster.is5Player1vs4(settings); }

function is7Player1vs6() { return CC && CC.Roster && CC.Roster.is7Player1vs6(settings); }

function isFixedTeams() { return CC && CC.Roster && CC.Roster.isFixedTeams(settings); }

function isPartnership() { return CC && CC.Roster && CC.Roster.isPartnership(settings); }

function isShiftingPartnership() { return false; }

function mySeat() { return CC && CC.Roster ? CC.Roster.mySeat({ isOnline, net, settings }) : 'p1'; }

function getPlayerCount() { return CC && CC.Roster ? CC.Roster.getPlayerCount(settings) : 2; }

function getRoster() { return CC && CC.Roster && CC.Roster.getRoster({ settings, isHvH, isOnline }); }

function getActiveSeats() { return CC && CC.Roster ? CC.Roster.getActiveSeats(settings) : []; }

function getActiveSeatIds() { return CC && CC.Roster ? CC.Roster.getActiveSeatIds({ getRoster, settings }) : []; }

function getSeatTeam(sId) { return CC?.Roster?.getSeatTeam?.(sId, { is3Player1vs2, settings }); }

function getShiftingRoles() { return CC?.Roster?.getShiftingRoles?.({ isShiftingPartnership, getActiveSeatIds, state }); }

function getShiftingPartner(seatId) { return CC?.Roster?.getShiftingPartner?.(seatId, { getShiftingRoles }); }

function nextSeat(seatId) { return CC?.GameEngine?.advanceDealer?.(seatId, getActiveSeatIds()) || 'p1'; }
function prevSeat(seatId) { return CC?.Roster?.prevSeat?.(seatId, getActiveSeatIds()); }

function getSeat(seatId) { return CC?.Roster?.getSeat?.(seatId, { getRoster, settings }); }

function getSeatHand(seatId) { return CC?.GameState?.getSeatHand?.(state, seatId); }

function setSeatHand(seatId, cards) { return CC?.GameState?.setSeatHand?.(state, seatId, cards); }

function getSeatKept(seatId) { return CC?.GameState?.getSeatKept?.(state, seatId); }

function setSeatKept(seatId, cards) { return CC?.GameState?.setSeatKept?.(state, seatId, cards); }

function getSeatHandModel(sId) { const c = getSeatHand(sId); return (CC && CC.Hand) ? new CC.Hand(c) : c; }
function getSeatKeptModel(sId) { const c = getSeatKept(sId); return (CC && CC.Hand) ? new CC.Hand(c) : c; }
function getCribModel() { const c = (state && state.crib) || []; return (CC && CC.Hand) ? new CC.Hand(c) : c; }
function getPileModel() { const c = (state && state.pile) || []; return (CC && CC.Hand) ? new CC.Hand(c) : c; }

function seatName(seatId) { return CC?.Roster?.seatName?.(seatId, { isOnline, mySeat, getSeat, settings, isHvH }); }

function pName(who) { return CC?.Roster?.pName?.(who, { seatName, isSoloVsPairs, is3Player1vs2, is5Player1vs4, is7Player1vs6, getPlayerCount }); }

function seatColor(seatId) { return CC?.Roster?.seatColor?.(seatId); }

function isSeatCpu(seatId) {
  if (!seatId || typeof seatId !== 'string') return false;
  return CC?.Roster?.isSeatCpu?.(seatId, { settings, isTwoHuman, getSeat }) ?? false;
}

function isSeatHuman(seatId) {
  if (!seatId || typeof seatId !== 'string') return false;
  return CC?.Roster?.isSeatHuman?.(seatId, { settings, isTwoHuman, getSeat }) ?? (!isSeatCpu(seatId));
}

function isSeatOnline(seatId) {
  return CC?.NetworkManager?.isSeatOnline?.(seatId, { net, settings }) ?? true;
}

function isMultiHuman() {
  return CC?.NetworkManager?.isMultiHuman?.(settings) ?? false;
}

function replacePlayerWithAi(seatId, aiProfile = 'shark', botName = 'The Shark') {
  if (CC?.NetworkManager?.replaceSeatWithCpu) {
    CC.NetworkManager.replaceSeatWithCpu(seatId, aiProfile, botName, {
      settings, state, setNetStatus, log, netPush, renderScores, render, scheduleOppPlay,
      chooseDiscard, handleSeatDiscardIntent, pickCut
    });
  }
}

function handlePlayerOfflinePrompt(seatId, seatName) {
  const showOfflineModal = (CC?.ModalManager?.showPlayerOfflineModal) || (window.modalManager && window.modalManager.showPlayerOfflineModal ? window.modalManager.showPlayerOfflineModal.bind(window.modalManager) : null);
  const showPickAiModal = (CC?.ModalManager?.showPickAiReplacementModal) || (window.modalManager && window.modalManager.showPickAiReplacementModal ? window.modalManager.showPickAiReplacementModal.bind(window.modalManager) : null);

  if (typeof showOfflineModal === 'function') {
    showOfflineModal(seatId, seatName, (sId, name) => {
      if (typeof showPickAiModal === 'function') {
        showPickAiModal(sId, name, (aiProfile, botName) => {
          replacePlayerWithAi(sId, aiProfile, botName);
        });
      } else {
        replacePlayerWithAi(sId, 'shark', 'The Shark');
      }
    }, (sId, name) => {
      const statusEl = document.getElementById('status');
      if (statusEl) {
        let waitBar = document.getElementById('reconnectWaitBar');
        if (!waitBar) {
          waitBar = document.createElement('div');
          waitBar.className = 'reconnect-wait-bar';
          waitBar.id = 'reconnectWaitBar';
          statusEl.appendChild(waitBar);
        }
        waitBar.innerHTML = `<span>⚠️ ${name} is offline.</span> <button type="button" class="btn-quick-replace" id="btnTriggerAiReplace">🤖 Replace with AI</button>`;
        const btn = document.getElementById('btnTriggerAiReplace');
        if (btn) {
          btn.onclick = () => {
            waitBar.remove();
            if (typeof showPickAiModal === 'function') {
              showPickAiModal(sId, name, (aiProfile, botName) => {
                replacePlayerWithAi(sId, aiProfile, botName);
              });
            } else {
              replacePlayerWithAi(sId, 'shark', 'The Shark');
            }
          };
        }
      }
    }, (sId, name) => {
      cancelGameAndReturnToMenu();
    });
  } else {
    replacePlayerWithAi(seatId, 'shark', 'The Shark');
  }
}

function scoreKey(who) { return CC?.Roster?.scoreKey?.(who, { isFixedTeams, settings, getSeatTeam }); }

function isInPegThroughZone(score) { return CC?.isInPegThroughZone ? CC.isInPegThroughZone(score) : false; }
function isInLast5PegOut(score) { return CC?.isInLast5PegOut ? CC.isInLast5PegOut(score, (settings && settings.target) || 121) : false; }
function isInBunker(score) { return CC?.isInBunker ? CC.isInBunker(score, (settings && settings.target) || 121) : false; }

function getNoPassingLanes() { return CC && CC.ObstacleGeometry ? CC.ObstacleGeometry.getNoPassingLanes(settings) : []; }
function isInNoPassingLane(score) { return CC && CC.ObstacleGeometry ? CC.ObstacleGeometry.isInNoPassingLane(score, settings) : false; }
function getNoPassingLaneForHole(score) { return CC && CC.ObstacleGeometry ? CC.ObstacleGeometry.getNoPassingLaneForHole(score, settings) : null; }
function getNoPassingLaneMaxScore(who, currentScore, targetScore) {
  if (CC && CC.ObstacleGeometry && CC.ObstacleGeometry.getNoPassingLaneMaxScore) {
    return CC.ObstacleGeometry.getNoPassingLaneMaxScore(scoreKey(who), currentScore, targetScore, state, settings, typeof getActiveTracks === 'function' ? getActiveTracks() : []);
  }
  return { allowedScore: targetScore, blocked: false, blockingPlayer: null };
}
function getJumpConfigs() { return CC && CC.ObstacleGeometry ? CC.ObstacleGeometry.getJumpConfigs(settings) : []; }
function getJumpForHole(holeNum) { return CC && CC.ObstacleGeometry ? CC.ObstacleGeometry.getJumpForHole(holeNum, settings) : null; }
function getJailConfigs() { return CC && CC.ObstacleGeometry ? CC.ObstacleGeometry.getJailConfigs(settings) : []; }
function getJailForHole(holeNum) { return CC && CC.ObstacleGeometry ? CC.ObstacleGeometry.getJailForHole(holeNum, settings) : null; }
function getJailStepXY(jailHole, step, who) { return CC && CC.ObstacleGeometry ? CC.ObstacleGeometry.getJailStepXY(jailHole, step, who, settings, getTrackXY) : (typeof getTrackXY === 'function' ? getTrackXY(jailHole, who || 'p1') : { x: 0, y: 0 }); }
function getBarConfigs() { return CC && CC.ObstacleGeometry ? CC.ObstacleGeometry.getBarConfigs(settings) : []; }
function getBarForHole(holeNum) { return CC && CC.ObstacleGeometry ? CC.ObstacleGeometry.getBarForHole(holeNum, settings) : null; }
function getBarStepXY(barHole, step, who) { return CC && CC.ObstacleGeometry ? CC.ObstacleGeometry.getBarStepXY(barHole, step, who, settings, getTrackXY) : (typeof getTrackXY === 'function' ? getTrackXY(barHole, who || 'p1') : { x: 0, y: 0 }); }
function getTimeoutConfigs() { return CC && CC.ObstacleGeometry ? CC.ObstacleGeometry.getTimeoutConfigs(settings) : []; }
function getTimeoutForHole(holeNum) { return CC && CC.ObstacleGeometry ? CC.ObstacleGeometry.getTimeoutForHole(holeNum, settings) : null; }
function getTimeoutStepXY(timeoutHole, step, who) { return CC && CC.ObstacleGeometry ? CC.ObstacleGeometry.getTimeoutStepXY(timeoutHole, step, who, settings, getTrackXY) : (typeof getTrackXY === 'function' ? getTrackXY(timeoutHole, who || 'p1') : { x: 0, y: 0 }); }
function getStopSignConfigs() { return CC && CC.ObstacleGeometry ? CC.ObstacleGeometry.getStopSignConfigs(settings) : []; }
function getStopSignForHole(holeNum) { return CC && CC.ObstacleGeometry ? CC.ObstacleGeometry.getStopSignForHole(holeNum, settings) : null; }
function getDetourConfigs() { return CC && CC.ObstacleGeometry ? CC.ObstacleGeometry.getDetourConfigs(settings) : []; }
function getDetourForHole(holeNum) { return CC && CC.ObstacleGeometry ? CC.ObstacleGeometry.getDetourForHole(holeNum, settings) : null; }
function catmullRomPath(pts) { return CC && CC.ObstacleGeometry ? CC.ObstacleGeometry.catmullRomPath(pts) : ''; }
function computeLoopGeometry(holeNum, who, isRoundAbout) { return CC && CC.ObstacleGeometry ? CC.ObstacleGeometry.computeLoopGeometry(holeNum, who, isRoundAbout, settings, getTrackXY) : { pEntry: { x: 0, y: 0 }, pExit: { x: 0, y: 0 } }; }
function getDetourStepXY(detourHole, step, who) { return CC && CC.ObstacleGeometry ? CC.ObstacleGeometry.getDetourStepXY(detourHole, step, who, settings, getTrackXY) : (typeof getTrackXY === 'function' ? getTrackXY(detourHole, who || 'p1') : { x: 0, y: 0 }); }
function getRoundAboutConfigs() { return CC && CC.ObstacleGeometry ? CC.ObstacleGeometry.getRoundAboutConfigs(settings) : []; }
function getRoundAboutForHole(holeNum) { return CC && CC.ObstacleGeometry ? CC.ObstacleGeometry.getRoundAboutForHole(holeNum, settings) : null; }
function getRoundAboutStepXY(roundAboutHole, step, who) { return CC && CC.ObstacleGeometry ? CC.ObstacleGeometry.getRoundAboutStepXY(roundAboutHole, step, who, settings, getTrackXY) : (typeof getTrackXY === 'function' ? getTrackXY(roundAboutHole, who || 'p1') : { x: 0, y: 0 }); }
function getSignConfigs() { return CC && CC.ObstacleGeometry ? CC.ObstacleGeometry.getSignConfigs(settings) : []; }
function getSignBlockForHole(h) { return CC && CC.ObstacleGeometry ? CC.ObstacleGeometry.getSignBlockForHole(h, settings) : null; }
function getDiceConfigs() { return CC && CC.ObstacleGeometry ? CC.ObstacleGeometry.getDiceConfigs(settings) : []; }
function getDiceForHole(holeNum) { return CC && CC.ObstacleGeometry ? CC.ObstacleGeometry.getDiceForHole(holeNum, settings) : null; }
function getWheelConfigs() { return CC && CC.ObstacleGeometry ? CC.ObstacleGeometry.getWheelConfigs(settings) : []; }
function getWheelForHole(holeNum) { return CC && CC.ObstacleGeometry ? CC.ObstacleGeometry.getWheelForHole(holeNum, settings) : null; }
function isDealerTeam(seatId) { return CC?.GameEngine?.isDealerTeam?.(seatId, { state, isSoloVsPairs, isFixedTeams, getSeatTeam }); }

function seatDiscardsToDeck(seatId) { return CC?.GameEngine?.seatDiscardsToDeck?.(seatId, { settings, state, getActiveSeatIds }); }

function applySeatLabels() { return CC?.UIController?.applySeatLabels?.({ isOnline, mySeat, getPlayerCount, seatName, settings, getAppBrand, isSoloVsPairs, pName, isHvH }); }

function passTo(who, thenFn) { return CC?.ModalManager?.passTo?.(who, thenFn, { isOnline, isSeatCpu, isSeatHuman, getActiveSeatIds, settings, getAppBrand, isHvH, state, render, showModal, hideModal, seatColor, seatName }); }

function scheduleOppPlay(seatId) { return CC?.GameEngine?.scheduleOppPlay?.(seatId, { state, isOnline, isSeatCpu, settings, cpuSeatPlay, mySeat, setStatus, render, netPush, seatName, isSeatHuman, passTo }); }

const CLASH_DEFAULTS_481 = (CC && CC.CLASH_DEFAULTS_481) || {};
const CLASH_DEFAULTS_241 = (CC && CC.CLASH_DEFAULTS_241) || {};
const CLASH_DEFAULTS_361 = (CC && CC.CLASH_DEFAULTS_361) || {};



function getClashDefaults(t) { return (CC && CC.ClashManager) ? CC.ClashManager.getClashDefaults(t) : (Number(t) === 241 ? CLASH_DEFAULTS_241 : (Number(t) === 361 ? CLASH_DEFAULTS_361 : CLASH_DEFAULTS_481)); }

function getSkunkDefaults(t) { return (CC && CC.ClashManager) ? CC.ClashManager.getSkunkDefaults(t) : { skunkHole: Math.round((Number(t || 481) - 1) * 0.75), doubleSkunkHole: Math.round((Number(t || 481) - 1) * 0.5) }; }

let settings = (CC && CC.SettingsManager && CC.SettingsManager.DEFAULT_SETTINGS)
  ? JSON.parse(JSON.stringify(CC.SettingsManager.DEFAULT_SETTINGS))
  : { opp: 'cpu', playerCount: 2, gameMode: 'individual', target: 121, decks: 1, cardBack: 'classic-blue' };
let gameHands = [];


const CARD_BACKS = (CC && CC.CARD_BACKS) || (CC && CC.SettingsManager && CC.SettingsManager.CARD_BACKS) || [
  { id: 'classic-blue', name: 'Classic Blue', desc: 'Traditional casino crosshatch' },
  { id: 'classic-red', name: 'Classic Red', desc: 'Bicycle crimson diamond weave' }
];

function setCardBack(id, skipSave = false) {
  return CC?.SettingsManager?.setCardBack?.(id, { settings, skipSave, onSave: () => { if (typeof saveSettingsStore === 'function' && typeof readFormSettings === 'function') saveSettingsStore(readFormSettings()); } });
}
function initCardBackPicker() {
  return CC?.SettingsManager?.initCardBackPicker?.({ currentCardBack: settings.cardBack || 'classic-blue', settings, onSave: () => { if (typeof saveSettingsStore === 'function' && typeof readFormSettings === 'function') saveSettingsStore(readFormSettings()); } });
}

let state;

function makeDeck(n) { return (CC && CC.Deck) ? CC.Deck.makeDeck(n != null ? n : (settings.decks || 1), settings && settings.jokers) : []; }
function isWild(c) { return (c && typeof c.isWild === 'boolean') ? c.isWild : !!(c && c.wild); }
function rankOf(c) { return (c && typeof c.rank === 'number') ? c.rank : ((c && c.playAs && c.playAs.r) || (c ? c.r : 0)); }
function suitOf(c) { return (c && typeof c.suit === 'string') ? c.suit : ((c && c.playAs && c.playAs.s) || (c ? c.s : 'S')); }
function cardFits(c, count) { if (!c) return false; return (CC && CC.Card) ? CC.Card.from(c).fits(count) : (isWild(c) && !c.playAs ? count <= 30 : count + VAL(rankOf(c)) <= 31); }
function assignCard(c, r, su) { return (CC && CC.Card) ? CC.Card.from(c).assignWild(r, su) : Object.assign({}, c, { r, s: su || suitOf(c) || 'S', playAs: { r, s: su || 'S' } }); }
function shuffle(a) { return (CC && CC.Deck && typeof CC.Deck.shuffleArray === 'function') ? CC.Deck.shuffleArray(a) : a; }

function takeAnim(key) { return CC?.TableRenderer?.takeAnim?.(key); }

function resetAnims() { CC?.TableRenderer?.resetAnims?.(); }

function faceMarkup(c) { return CC?.TableRenderer?.faceMarkup?.(c); }

function cardEl(c, opts = {}) { return CC?.TableRenderer?.cardEl?.(c, opts, { state }); }

function cardKey(c) { return CC?.TableRenderer ? CC.TableRenderer.cardKey(c) : ((c && c.id) || ''); }

function applyHighlights(cards, isBonus = false) { CC?.TableRenderer?.applyHighlights?.(cards, isBonus, { state }); }

function combinations(arr, k) { return CC?.combinations ? CC.combinations(arr, k) : []; }

function isConsecutiveRun(rs, allowWrap) {
  if (CC && CC.isConsecutiveRun) return CC.isConsecutiveRun(rs, allowWrap);
  return false;
}

function scoreShowCore(cards, starter, isCrib, extraOptions = {}) { return CC?.CribbageScorer?.scoreHandCore ? CC.CribbageScorer.scoreHandCore(cards, starter, isCrib, { ...settings, state, ...extraOptions }) : { fifteens: 0, nineteens: 0, thirtyOnes: 0, pairs: 0, runs: 0, flush: 0, nobs: 0, total: 0, notes: [], lines: [] }; }
function scoreShow(cards, starter, isCrib, precise, extraOptions = {}) { return CC?.CribbageScorer?.scoreHand ? CC.CribbageScorer.scoreHand(cards, starter, isCrib, { ...settings, state, ...extraOptions }, precise) : scoreShowCore(cards, starter, isCrib, extraOptions); }
function pegPoints(pile, justPlayed) { return CC?.CribbageScorer?.scorePegging ? CC.CribbageScorer.scorePegging(pile, settings) : []; }

let FULL_DECK = makeDeck(1);

function pegTotal(pile) { return CC?.CribbageScorer?.pegTotal ? CC.CribbageScorer.pegTotal(pile, settings) : pegPoints(pile).reduce((s, p) => s + p.n, 0); }
function pegPotential(keep) { return CC?.CribbageScorer?.pegPotential ? CC.CribbageScorer.pegPotential(keep) : 0; }
function expectedShowExact(keep, deadIds, isCrib) { return CC?.CribbageAI?.expectedShowExact ? CC.CribbageAI.expectedShowExact(keep, deadIds, isCrib, FULL_DECK) : 0; }
function getSeatPersonality(seatId) {
  const sId = seatId || state?.turn || 'p2';
  const seat = settings?.seats?.find(s => s.id === sId);
  return seat?.personality || settings?.aiProfile || 'shark';
}

function chooseDiscardFast(hand, isDealer, lowball, discardN = 2, toDeck = false, seatId = null) {
  const profile = getSeatPersonality(seatId);
  return CC?.CribbageAI?.chooseDiscardFast?.(hand, isDealer, lowball, discardN, toDeck, { profile, starter: state?.starter, optFivesCribbage: settings?.optFivesCribbage, optSevensAndEightsCribbage: settings?.optSevensAndEightsCribbage, settings }) || (hand || []).slice(0, discardN);
}

let lastSolver = null;

function chooseDiscard(hand, isDealer, lowball, discardN = 2, toDeck = false, seatId = null) {
  if (!hand || hand.length <= discardN) return (hand || []).slice(0, discardN);
  const profile = getSeatPersonality(seatId);
  if (CC?.CribbageAI?.chooseDiscard) {
    const myLead = (state?.scores?.[scoreKey(isDealer ? state.dealer : nextSeat(state.dealer))]) || 0;
    const inBlockedZone = (settings?.optPegThroughZones && isInPegThroughZone(myLead)) || (settings?.optLast5PegOut && isInLast5PegOut(myLead)) || (settings?.optBunkers && isInBunker(myLead));
    return CC.CribbageAI.chooseDiscard(hand, isDealer, lowball, discardN, toDeck, { profile, deck: FULL_DECK, starter: state?.starter, inBlockedZone, target: settings?.target, optRockCrib: settings?.optRockCrib, optShotgunCribbage: settings?.optShotgunCribbage, optFivesCribbage: settings?.optFivesCribbage, optSevensAndEightsCribbage: settings?.optSevensAndEightsCribbage, settings });
  }
  return chooseDiscardFast(hand, isDealer, lowball, discardN, toDeck, seatId);
}

function cpuKnownIds() { return CC && CC.CribbageAI ? CC.CribbageAI.cpuKnownIds(state) : new Set(); }

function unseenCards() { return CC && CC.CribbageAI ? CC.CribbageAI.unseenCards(state, FULL_DECK) : []; }

function sampleOppHand(unseen, n) { return CC && CC.CribbageAI ? CC.CribbageAI.sampleOppHand(unseen, n, shuffle) : []; }

function removeCard(hand, card) { if (CC && CC.CribbageAI) CC.CribbageAI.removeCard(hand, card); }

function fastPlayCard(hand, pile, count) { return CC?.CribbageAI?.fastPlayCard ? CC.CribbageAI.fastPlayCard(hand, pile, count, settings) : ((hand || []).filter(c => Card.from(c).fits(count))[0] || null); }
function rolloutPeg(myHand, oppHand, pile, count, myTurn, lastWasMe) { return CC?.CribbageAI?.rolloutPeg ? CC.CribbageAI.rolloutPeg(myHand, oppHand, pile, count, myTurn, lastWasMe, settings) : { myPts: 0, oppPts: 0 }; }
function raceBonus(whoPts, add, lowball) { if (CC?.CribbageAI?.raceBonus) { const opp = state?.scores ? (whoPts === state.scores.cpu ? state.scores.you : state.scores.cpu) : 0; return CC.CribbageAI.raceBonus(whoPts, add, lowball, settings?.target || 121, opp); } return 0; }

function cardLabel(c) { return CC && CC.TableRenderer ? CC.TableRenderer.cardLabel(c) : ''; }

function choosePlay(hand, pile, count, oppCanPlay, lowball, seatId = null) {
  const profile = getSeatPersonality(seatId);
  if (CC?.CribbageAI?.choosePlay) {
    const oppN = state?.youHand ? state.youHand.length : 4;
    return CC.CribbageAI.choosePlay(hand, pile, count, oppCanPlay, lowball, { profile, settings, knownIds: typeof cpuKnownIds === 'function' ? cpuKnownIds() : [], deck: FULL_DECK, oppHandCount: oppN, myScore: state?.scores?.cpu || 0, oppScore: state?.scores?.you || 0, target: settings?.target });
  }
  return (hand || []).filter(c => Card.from(c).fits(count))[0] || null;
}

/* Audio FX — layered resonant wood strikes */
let sfx = (typeof window !== 'undefined' && window.audioManager)
  ? window.audioManager
  : ((CC && CC.AudioManager) ? new CC.AudioManager() : {
      on: true, ctx: null, ensure() { return null; },
      tone() {}, noise() {}, snap() {}, peg() {}, shuffle() {}, riffle() {},
      fanfare20() {}, chime15() {}, bell31() {}, skunk() {}, chutesAndLadders() {}, spades() {}, gameHole() {},
      card() {}, deal() {}, pop() {}, alarm() {},
      setOn(enabled) { this.on = Boolean(enabled); }, toggle() { this.on = !this.on; return this.on; }
    });
if (typeof window !== 'undefined') {
  window.sfx = sfx;
  if (!window.audioManager) window.audioManager = sfx;
}
try { sfx.on = (appStorage.getItem('crazyCribbage.sfx') ?? (typeof localStorage !== 'undefined' ? localStorage.getItem('crazyCribbage.sfx') : null)) !== 'off'; } catch (e) {}

function log(msg) { return CC?.UIController?.log?.(msg); }

function showSolver() {}
function setStatus(t) { return CC?.UIController?.setStatus ? CC.UIController.setStatus(t, state, updateTurnPrompt) : (state ? (state.customStatus = t, updateTurnPrompt(t)) : null); }
function updateTurnPrompt(overrideText) { return CC?.UIController?.updateTurnPrompt?.(overrideText, { state, settings, mySeat, isOnline, isSeatHuman, seatName, pName, nextSeat, cardLabel }); }

function isBonusMessage(text) { return CC?.UIController?.isBonusMessage?.(text) ?? false; }

function fancyCall(text, kind, ms) { return CC?.UIController?.fancyCall?.(text, kind, ms); }

function fancyPegPhrase(why, n) { return CC?.UIController?.fancyPegPhrase?.(why, n); }

function announceCombos(pts) { return CC?.UIController?.announceCombos?.(pts, { fancyCall, fancyPegPhrase, isBonusMessage }); }

function updateCountGlow() { return CC?.UIController?.updateCountGlow?.(state ? state.count : 0, state ? state.phase : null); }
function streetName(score) { return CC?.streetName ? CC.streetName(score, settings?.target || 121) : 'Home'; }
function showStreetBanner(who, mark) { return CC?.UIController?.showStreetBanner?.(who, mark, { pName, streetName, fancyCall }); }
function showScoreToast(who, n, why, customDuration) { return CC?.UIController?.showScoreToast?.(who, n, why, customDuration, { pName }); }

function addScore(who, n, why, isPartnerBonus = false, isSignAction = false) {
  return CC?.GameEngine?.addScore?.(who, n, why, isPartnerBonus, isSignAction, {
    state, settings, scoreKey, getActiveTracks, pName, log, showScoreToast, fancyCall, renderScores, setShowMeter, fancyPegPhrase, isBonusMessage, sfx, showStreetBanner, checkGameOver,
    handleSignsLanding: window.handleSignsLanding,
    openDiceRollModal: window.openDiceRollModal || openDiceRollModal,
    openWheelSpinModal: window.openWheelSpinModal || openWheelSpinModal,
    getActivePintsOptionAtHole: window.getActivePintsOptionAtHole,
    showPintsBonusModal: window.showPintsBonusModal,
    getActiveAdultOptionAtHole: window.getActiveAdultOptionAtHole,
    showAdultStripModal: window.showAdultStripModal,
    showAdultTimerModal: window.showAdultTimerModal,
    pegTracker: (typeof window !== 'undefined' ? window.pegTracker : null),
    isShiftingPartnership: window.isShiftingPartnership,
    getShiftingPartner: window.getShiftingPartner,
    seatName: window.seatName,
    showJailAnimation: (opts) => {
      const fn = (typeof window !== 'undefined' && window.showJailAnimation) || (CC && CC.JailAnimationOverlay && CC.JailAnimationOverlay.show) || JailAnimationOverlay.show;
      return typeof fn === 'function' ? fn(opts) : null;
    },
    showBarAnimation: (opts) => {
      const fn = (typeof window !== 'undefined' && window.showBarAnimation) || (CC && CC.BarAnimationOverlay && CC.BarAnimationOverlay.show) || BarAnimationOverlay.show;
      return typeof fn === 'function' ? fn(opts) : null;
    },
    showCannonAnimation: (opts) => {
      const fn = (typeof window !== 'undefined' && window.showCannonAnimation) || (CC && CC.CannonAnimationOverlay && CC.CannonAnimationOverlay.show) || CannonAnimationOverlay.show;
      return typeof fn === 'function' ? fn(opts) : null;
    },
    showSpeedTrapAnimation: (opts) => {
      const fn = (typeof window !== 'undefined' && window.showSpeedTrapAnimation) || (CC && CC.SpeedTrapAnimationOverlay && CC.SpeedTrapAnimationOverlay.show) || SpeedTrapAnimationOverlay.show;
      return typeof fn === 'function' ? fn(opts) : null;
    },
    showScenicRouteAnimation: (opts) => {
      const fn = (typeof window !== 'undefined' && window.showScenicRouteAnimation) || (CC && CC.ScenicRouteAnimationOverlay && CC.ScenicRouteAnimationOverlay.show) || ScenicRouteAnimationOverlay.show;
      return typeof fn === 'function' ? fn(opts) : null;
    }
  });
}

function skunkResult(winner, youScore, cpuScore) { return CC?.skunkResult?.(winner, youScore, cpuScore, { target: settings?.target || 121, goal: settings?.goal || 'high', optSkunkMarkers: Boolean(settings?.optSkunkMarkers), skunkMarkerHole: settings?.skunkMarkerHole, doubleSkunkMarkerHole: settings?.doubleSkunkMarkerHole }); }

function checkChutesAndLadders() { return CC?.GameEngine?.checkChutesAndLadders?.(state, settings, { getActiveTracks, pName, log, fancyCall, renderScores, netPush, sfx }); }

function checkGameOver() { return CC?.GameEngine?.checkGameOver?.({ settings, state, getActiveTracks, pName, sfx, recordGameResult, renderRecordBox: window.renderRecordBox, skunkResult, showModal, log, fancyCall, renderScores, netPush }); }

/* Signs Option Mechanics & Modals */
const SIGN_DEFS = CC?.ClashMiniGames?.SIGN_DEFS || {};

const WHEEL_SLOTS = CC?.ClashMiniGames?.WHEEL_SLOTS || [];

function handleSignsLanding(block, who) { return CC?.ClashMiniGames?.handleSignsLanding?.(block, who, { state, settings, pName, log, fancyCall, showScoreToast, setStatus, isSeatHuman, isSeatCpu, showModal, closeModal, chooseSign }); }
function openSignPickerModal(block, who) { return CC?.ClashMiniGames?.openSignPickerModal?.(block, who, { state, showModal }); }
function onSignCardClick(signName, blockId, who) { return CC?.ClashMiniGames?.onSignCardClick?.(signName, blockId, who, { closeModal, getSignConfigs, chooseSign }); }

function chooseSign(signName, who, block) { return CC?.ClashMiniGames?.chooseSign?.(signName, who, block, { state, pName, isSeatHuman, log, fancyCall, showScoreToast, setStatus, updateSignsBillboard, executeSign, resumeGameAfterSign }); }
function resumeGameAfterSign() { return CC?.ClashMiniGames?.resumeGameAfterSign?.({ state, isSeatCpu, scheduleOppPlay }); }

function executeSign(signName, who, block) { return CC?.ClashMiniGames?.executeSign?.(signName, who, block, { state, settings, scoreKey, pName, log, fancyCall, renderScores, addScore, getJailConfigs, getBarConfigs, getTimeoutConfigs, openDiceRollModal, openWheelSpinModal }); }
function openDiceRollModal(who) { return CC?.ClashMiniGames?.openDiceRollModal?.(who, { showModal, performDiceRoll }); }
function performDiceRoll(who) { return CC?.ClashMiniGames?.performDiceRoll?.(who, { document, state, sfx, log, pName, isSeatCpu, getActiveTracks, setStatus, applyDiceResult, seatColor }); }

function onDicePickPlayer(targetWho, signedPts, dir, val) { return CC?.ClashMiniGames?.onDicePickPlayer?.(targetWho, signedPts, dir, val, { document, pName, log, setStatus, applyDiceResult }); }

function applyDiceResult(targetWho, signedPts) { return CC?.ClashMiniGames?.applyDiceResult?.(targetWho, signedPts, { closeModal, log, pName, fancyCall, addScore, resumeGameAfterSign }); }

function openWheelSpinModal(who) { return CC?.ClashMiniGames?.openWheelSpinModal?.(who, { document, showModal, isSeatCpu, performWheelSpin }); }
function drawWheelCanvas() { return CC?.ClashMiniGames?.drawWheelCanvas?.(document.getElementById('wheelCanvas')); }
function performWheelSpin(who) { return CC?.ClashMiniGames?.performWheelSpin?.(who, { document, sfx, isSeatHuman, pName, log, fancyCall, setStatus, closeModal, executeSign, resumeGameAfterSign }); }

function updateSignsBillboard(blockId) { return CC?.ClashMiniGames?.updateSignsBillboard?.(blockId, { document, state, getSignConfigs }); }

/* Serpentine Board Renderers */
function getActiveTracks() {
  return CC?.getActiveTracks ? CC.getActiveTracks(settings, typeof isFixedTeams === 'function' ? isFixedTeams() : false) : ['p1', 'p2'];
}

function getPegGradId(who) {
  return CC?.BoardRenderer?.getPegGradId ? CC.BoardRenderer.getPegGradId(who) : 'pegP1Grad';
}

function updateScoreChips() {
  return CC?.BoardRenderer?.updateScoreChips?.(document, getActiveTracks(), pName, settings);
}

function getBoardSignature() {
  return CC?.BoardRenderer?.getBoardSignature ? CC.BoardRenderer.getBoardSignature(settings, getActiveTracks(), typeof getAppBrand === 'function' ? getAppBrand() : '') : '';
}

function buildBoard(opts = {}) {
  const activeSettings = opts.settings || settings;
  const brand = opts.brand || (typeof getAppBrand === 'function' ? getAppBrand() : (activeSettings?.brand || 'crazy'));
  const isClash = brand === 'clash';
  const emptyObstacles = { noPassingLanes: [], jumps: [], jails: [], bars: [], timeouts: [], detours: [], roundabouts: [], signs: [], stopSigns: [], dice: [], wheels: [] };
  const defaultObstacles = isClash ? {
    noPassingLanes: typeof getNoPassingLanes === 'function' ? getNoPassingLanes(activeSettings) : [],
    jumps: typeof getJumpConfigs === 'function' ? getJumpConfigs(activeSettings) : [],
    jails: typeof getJailConfigs === 'function' ? getJailConfigs(activeSettings) : [],
    bars: typeof getBarConfigs === 'function' ? getBarConfigs(activeSettings) : [],
    timeouts: typeof getTimeoutConfigs === 'function' ? getTimeoutConfigs(activeSettings) : [],
    detours: typeof getDetourConfigs === 'function' ? getDetourConfigs(activeSettings) : [],
    roundabouts: typeof getRoundAboutConfigs === 'function' ? getRoundAboutConfigs(activeSettings) : [],
    signs: typeof getSignConfigs === 'function' ? getSignConfigs(activeSettings) : [],
    stopSigns: typeof getStopSignConfigs === 'function' ? getStopSignConfigs(activeSettings) : [],
    dice: typeof getDiceConfigs === 'function' ? getDiceConfigs(activeSettings) : [],
    wheels: typeof getWheelConfigs === 'function' ? getWheelConfigs(activeSettings) : []
  } : emptyObstacles;

  return CC?.BoardRenderer?.buildBoard?.({
    document: typeof document !== 'undefined' ? document : null,
    settings: activeSettings,
    state,
    pegState,
    brand,
    activeTracks: typeof getActiveTracks === 'function' ? getActiveTracks() : ['p1', 'p2'],
    BOARD_CFG,
    getTrackXY,
    getTrackXYFn: getTrackXY,
    getClashBoardCfg,
    getSkunkDefaults,
    CARD_BACKS,
    obstacleConfigs: isClash ? (opts.obstacleConfigs || defaultObstacles) : emptyObstacles,
    obstacleStepHelpers: {
      getJailStepXY: typeof getJailStepXY === 'function' ? getJailStepXY : null,
      getBarStepXY: typeof getBarStepXY === 'function' ? getBarStepXY : null,
      getTimeoutStepXY: typeof getTimeoutStepXY === 'function' ? getTimeoutStepXY : null,
      getDetourStepXY: typeof getDetourStepXY === 'function' ? getDetourStepXY : null,
      getRoundAboutStepXY: typeof getRoundAboutStepXY === 'function' ? getRoundAboutStepXY : null
    },
    createLegendFn: typeof createCrazyCribbageLegend === 'function' ? createCrazyCribbageLegend : null,
    onAfterRender: () => {
      initBoardFancyTooltips();
      if (typeof opts.onAfterRender === 'function') opts.onAfterRender();
    },
    ...opts
  });
}

function createCrazyCribbageLegend() { return CC?.BoardRenderer?.createLegend?.({ brand: typeof getAppBrand === 'function' ? getAppBrand() : 'crazy', settings, adultOptions: ADULT_OPTIONS_LIST || [], obstacleConfigs: { noPassingLanes: getNoPassingLanes(), jumps: getJumpConfigs(), jails: getJailConfigs(), bars: getBarConfigs(), timeouts: getTimeoutConfigs(), detours: getDetourConfigs(), roundabouts: getRoundAboutConfigs(), signs: getSignConfigs(), stopSigns: typeof getStopSignConfigs === 'function' ? getStopSignConfigs() : [], dice: typeof getDiceConfigs === 'function' ? getDiceConfigs() : [], wheels: typeof getWheelConfigs === 'function' ? getWheelConfigs() : [] } }) || document.createElement('div'); }
function getBoardTooltipContext() {
  return CC?.BoardRenderer?.getBoardTooltipContext?.({
    state,
    settings,
    pegState,
    getAppBrand,
    getStreetOrTurnName,
    getSignConfigs,
    getStopSignConfigs,
    getJailConfigs,
    getBarConfigs,
    getTimeoutConfigs,
    getJumpConfigs,
    getDetourConfigs,
    getRoundAboutConfigs,
    getNoPassingLanes,
    getDiceConfigs,
    getWheelConfigs
  }) || { state, settings, pegState, brand: typeof getAppBrand === 'function' ? getAppBrand() : 'crazy' };
}
function getBoardTooltipData(target) { return CC?.BoardRenderer?.getBoardTooltipData?.(target, getBoardTooltipContext()) ?? null; }
function hideFancyTooltip() { return CC?.BoardRenderer?.hideFancyTooltip?.(); }
function initBoardFancyTooltips() { return CC?.BoardRenderer?.initBoardFancyTooltips?.(null, getBoardTooltipContext()); }
function renderScores(animate = true) { return CC?.BoardRenderer?.renderScores?.(animate, { state, settings, pegState, getActiveTracks, getTrackXY, updateScoreChips, updateCoachDisplay }); }

function sortCards(arr) { return CC?.Hand?.sort ? CC.Hand.sort(arr) : (arr || []).slice().sort((a, b) => a.r - b.r || SUITS.indexOf(a.s) - SUITS.indexOf(b.s)); }
function render() { return CC?.TableRenderer?.render?.({ state, settings, mySeat, getActiveSeatIds, getPlayerCount, isSoloVsPairs, isOnline, isHvH, isSeatHuman, isSeatOnline, isMultiHuman, getSeatKept, getSeatHand, sortCards, cardLabel, cardFits, cardEl, isWild, onPickCribCard, onYouCard, onCpuCard, onOppSeatDiscardCard, updateCoachDisplay, seatName, nextSeat, poneDoCutStarter, seatDiscardsToDeck, isDealerTeam, setShowMeter, updateTurnPrompt, renderScores, updateCountGlow, renderCutFan }); }

function onOppSeatDiscardCard(sId, c) { return CC?.GameEngine?.onOppSeatDiscardCard?.(sId, c, { state, settings, isOnline, render, setStatus }); }

function onCpuCard(c) { return CC?.GameEngine?.onCpuCard?.(c, { state, settings, isHvH, isOnline, isWild, openWildPick, render, hideWildPick, playCard, rankOf, VAL, setStatus }); }

function onYouCard(c) { return CC?.GameEngine?.onYouCard?.(c, { state, settings, mySeat, isOnline, isSeatHuman, openWildPick, isWild, render, hideWildPick, netSend, playCard, rankOf, VAL, setStatus }); }

function hideWildPick() { return CC?.ModalManager?.hideWildPick?.({ state }); }

function openWildPick(c, kind) { return CC?.ModalManager?.openWildPick?.(c, kind, { state, setStatus, SUIT_SYM, SUITS, RANKS, RANK_NAME, confirmWildAs }); }

function stylePickBtn(b, ink) { return CC?.ModalManager?.stylePickBtn?.(b, ink); }

function drawWildPick() { return CC?.ModalManager?.drawWildPick?.({ state, SUIT_SYM, SUITS, RANKS, RANK_NAME, confirmWildAs }); }

function confirmWildAs(r, su) { return CC?.GameEngine?.confirmWildAs?.(r, su, null, { state, isOnline, settings, netSend, mySeat, hideWildPick, log, RANK_NAME, SUIT_SYM, afterStarterAssigned, isWild, setStatus, pName, render, isSeatCpu, cpuDoPickYourCrib, beginDiscard, openWildPick }); }

function cpuAssignWilds(hand) { return CC?.CribbageAI?.cpuAssignWilds?.(hand, { isWild, settings, scoreShow, assignCard, log, RANK_NAME, SUIT_SYM, RANKS }); }

function beginDiscard() { return CC?.GameEngine?.beginDiscard?.({ state, settings, seatDiscardsToDeck, isSoloVsPairs, isDealerTeam, setStatus, updateCoachDisplay, render }); }

function showWait(msg) { return CC?.UIController?.showWait?.(msg); }

function hideWait() { return CC?.UIController?.hideWait?.(); }

function afterStarterAssigned() {
  try {
    const repMgr = CC?.GameReplayManager || (typeof window !== 'undefined' ? window.GameReplayManager : null);
    if (repMgr?.currentReplay && state?.starter) {
      repMgr.recordEvent('starter_cut', { starter: state.starter });
    }
  } catch (_) {}
  return CC?.GameEngine?.afterStarterAssigned?.({ state, settings, log, cardLabel, rankOf, fancyCall, addScore, render, netPush, getSeatHand, isWild, setStatus, startDiscardOrPickFlow, nextSeat, hideWait, isSeatCpu, scheduleOppPlay, isOnline, isSeatHuman, passTo, sfx, RANK_NAME, pName, openWildPick });
}

function getAppBrand() {
  if (CC?.SettingsManager?.getAppBrand) {
    return CC.SettingsManager.getAppBrand(typeof window !== 'undefined' ? window : null);
  }
  if (typeof window !== 'undefined') {
    if (window.currentBrand) return window.currentBrand;
    if (window.currentLogoBrand) return window.currentLogoBrand;
    if (typeof window.detectBrand === 'function') {
      try { const b = window.detectBrand(); if (b) return b; } catch (e) {}
    }
    if (typeof document !== 'undefined' && document.documentElement) {
      const b = document.documentElement.getAttribute('data-brand');
      if (b) return b;
    }
    if (window.location) {
      try {
        const params = new URLSearchParams(window.location.search);
        const p = (params.get('brand') || params.get('game') || params.get('logo') || '').toLowerCase();
        if (p === 'clash' || p === 'cribbage-clash') return 'clash';
        if (p === 'pints' || p === 'pints-and-pegs' || p === 'cribbage-pints') return 'pints';
        if (p === 'adult' || p === 'stripped' || p === 'skunked-and-stripped') return 'adult';
        if (p === 'crazy' || p === 'crazy-cribbage') return 'crazy';

        const href = (window.location.href || '').toLowerCase();
        if (href.indexOf('game-cribbage-clash') !== -1 || href.indexOf('cribbage-clash') !== -1 || href.indexOf('clash') !== -1) return 'clash';
        if (href.indexOf('game-pints-and-pegs') !== -1 || href.indexOf('pints-and-pegs') !== -1 || href.indexOf('pints') !== -1) return 'pints';
        if (href.indexOf('game-skunked-and-stripped') !== -1 || href.indexOf('skunked-and-stripped') !== -1 || href.indexOf('adult') !== -1 || href.indexOf('stripped') !== -1) return 'adult';
      } catch (e) {}
    }
  }
  return 'crazy';
}

function getSettingsKey() { return CC?.SettingsManager?.getSettingsKey ? CC.SettingsManager.getSettingsKey(getAppBrand()) : 'crazyCribbage.settings.v1'; }

function getRecordKey() { return CC?.SettingsManager?.getRecordKey ? CC.SettingsManager.getRecordKey(getAppBrand()) : 'crazyCribbage.records.v1'; }

function isAgeVerified(brand) {
  const b = brand || getAppBrand();
  if (b !== 'pints' && b !== 'adult') return true;
  const mgr = (typeof SettingsManager !== 'undefined' ? SettingsManager : null) || CC?.SettingsManager || (typeof window !== 'undefined' && window.SettingsManager);
  if (mgr && typeof mgr.isAgeVerified === 'function') {
    return mgr.isAgeVerified(b, appStorage);
  }
  if (appStorage && typeof appStorage.getItem === 'function') {
    return appStorage.getItem('cc_age_verified_' + b) === 'true';
  }
  return false;
}

function setAgeVerified(brand, val = true) {
  const b = brand || getAppBrand();
  const mgr = (typeof SettingsManager !== 'undefined' ? SettingsManager : null) || CC?.SettingsManager || (typeof window !== 'undefined' && window.SettingsManager);
  if (mgr && typeof mgr.setAgeVerified === 'function') {
    mgr.setAgeVerified(b, val, appStorage);
  } else if (appStorage && typeof appStorage.setItem === 'function') {
    appStorage.setItem('cc_age_verified_' + b, val ? 'true' : 'false');
  }
}

function showAgeGateModal(brand) { return CC?.ModalManager?.showAgeGateModal?.(brand, { getAppBrand }); }

function hideAgeGateModal() { return CC?.ModalManager?.hideAgeGateModal?.(); }

function confirmAgeGate() { return CC?.ModalManager?.confirmAgeGate?.({ getAppBrand, setAgeVerified, sfx }); }

function exitAgeGateToSafeBrand() { return CC?.ModalManager?.exitAgeGateToSafeBrand?.(); }

const THEMES = CC?.THEMES || CC?.SettingsManager?.THEMES || [];
function getThemeKey() { return CC?.SettingsManager?.getThemeKey ? CC.SettingsManager.getThemeKey(getAppBrand()) : 'crazyCribbage.theme'; }

function getDefaultTheme() { return CC?.SettingsManager?.getDefaultTheme ? CC.SettingsManager.getDefaultTheme(getAppBrand()) : 'tournament'; }

var theme = getDefaultTheme();
try { theme = appStorage.getItem(getThemeKey()) || (typeof localStorage !== 'undefined' ? localStorage.getItem(getThemeKey()) : null) || getDefaultTheme(); } catch (e) { theme = getDefaultTheme(); }
if (!THEMES.some(t => t.id === theme)) theme = getDefaultTheme();
function applyTheme(showFeedback = false) { if (CC?.SettingsManager) { theme = CC.SettingsManager.applyTheme(theme, { showFeedback, brand: getAppBrand() }); (typeof window !== 'undefined' ? window.modalManager : null)?.setTheme?.(theme); } }
function toggleTheme() { if (getAppBrand() !== 'clash' && CC?.SettingsManager) { theme = CC.SettingsManager.toggleTheme(theme, getAppBrand(), appStorage); applyTheme(true); } }



const SETTINGS_KEY = getSettingsKey();
const RECORD_KEY = getRecordKey();
const CHECK_IDS = (CC && CC.CHECK_IDS) || (CC && CC.SettingsManager && CC.SettingsManager.CHECK_IDS) || {};

let recordCache = null;

function variantParts(obj) { return CC && CC.SettingsManager ? CC.SettingsManager.variantParts(obj) : { goal: 'high', target: '121', deal: 'std', decks: '1' }; }

function variantKeyFrom(obj) { return CC && CC.SettingsManager ? CC.SettingsManager.variantKeyFrom(obj) : ''; }

function variantLabelFrom(obj) { return CC && CC.SettingsManager ? CC.SettingsManager.variantLabelFrom(obj) : ''; }

function loadRecords() { return CC?.SettingsManager?.loadRecords(appStorage, getAppBrand()) ?? {}; }
function saveRecords(map) { CC?.SettingsManager?.saveRecords(map, appStorage, getAppBrand()); }
function getVariantRecord(key) { return CC?.SettingsManager?.getVariantRecord(key, appStorage, getAppBrand()) ?? { wins: 0, losses: 0 }; }
function recordGameResult(winner) {
  const src = settings && settings.deal ? settings : currentFormVariant();
  const vKey = variantKeyFrom(src);
  const vLabel = variantLabelFrom(src);
  const b = typeof getAppBrand === 'function' ? getAppBrand() : 'crazy';
  const res = CC?.SettingsManager?.recordGameResult(winner, vKey, vLabel, appStorage, b) ?? { wins: 0, losses: 0, label: vLabel };

  try {
    const profClass = (typeof window !== 'undefined' && window.Profile) || CC?.Profile || Profile;
    if (profClass) {
      const prof = typeof profClass.getInstance === 'function' ? profClass.getInstance(appStorage) : profClass;
      if (prof && typeof prof.recordGame === 'function') {
        const uScore = (state?.scores?.you ?? state?.scores?.p1 ?? 0);
        const oScore = (state?.scores?.opp ?? state?.scores?.p2 ?? 0);
        const oName = (settings?.seats && settings.seats[1]?.name) || 'Opponent';
        const isSkunk = Boolean(typeof skunkResult === 'function' && skunkResult(state, settings));
        prof.recordGame({
          winner,
          userScore: uScore,
          oppScore: oScore,
          opponentName: oName,
          variantKey: vKey,
          variantLabel: vLabel,
          brand: b,
          skunk: isSkunk
        }, appStorage);
      }
    }
  } catch (err) {
    console.warn('[Profile] Failed to record match in profile:', err);
  }

  return res;
}
function currentFormVariant() {
  return CC?.SettingsManager?.currentFormVariant?.({ brand: typeof getAppBrand === 'function' ? getAppBrand() : 'crazy' }) ?? { goal: 'high', target: 121, deal: 'std', decks: 1 };
}

function renderRecordBox() { return CC?.SettingsManager?.renderRecordBox?.({ brand: typeof getAppBrand === 'function' ? getAppBrand() : 'crazy', settings }); }

var CLASH_BLOCK_DEFS = CC?.ClashSettings?.getBlockDefs ? CC.ClashSettings.getBlockDefs() : [];
function getActiveClashBlocks() { return CC?.ClashSettings?.getActiveBlocks ? CC.ClashSettings.getActiveBlocks(CLASH_BLOCK_DEFS) : []; }
function checkClashCollision(candidateStart, length, excludeSelectId, blockList) { return CC?.ClashSettings?.checkCollision?.(candidateStart, length, excludeSelectId, blockList, CC.ClashManager); }
function findAvailableClashHole(sel, length, excludeSelectId, activeBlocks) { return CC?.ClashSettings?.findAvailableHole?.(sel, length, excludeSelectId, activeBlocks, CC.ClashManager); }
function updateAllClashDropdowns() { return CC?.ClashSettings?.updateAllDropdowns?.(CLASH_BLOCK_DEFS, CC.ClashManager); }
var _isEnforcingClashNoOverlap = false;
var _isRandomizingClash = false;
function enforceClashNoOverlap(initiatorSelectId = null) { return CC?.ClashSettings?.enforceNoOverlap?.(initiatorSelectId, { getActiveClashBlocks, updateAllClashDropdowns, checkCollision: checkClashCollision, findAvailableHole: findAvailableClashHole }); }
function sanitizeClashBlockPlacements(s) { return CC?.ClashSettings?.sanitizeBlockPlacements ? CC.ClashSettings.sanitizeBlockPlacements(s, typeof getClashDefaults === 'function' ? getClashDefaults : null) : s; }

// --- Clash Obstacle UI Options Delegated to CC.ClashSettings ---

function getStreetOrTurnName(h) {
  const targetVal = Number((settings && settings.target) || (document.getElementById('optTarget') && document.getElementById('optTarget').value) || 121);
  return CC?.ClashSettings?.getStreetOrTurnName ? CC.ClashSettings.getStreetOrTurnName(h, targetVal) : 'Street 1';
}

const CLASH_UI_TYPES = [
  { type: 'noPassingLanes', toggle: 'toggleNoPassingLanesConfig', rows: 'updateNoPassingLaneRows', field: 'onNoPassingLaneFieldChange' },
  { type: 'jump', toggle: 'toggleJumpConfig', rows: 'updateJumpRows', field: 'onJumpFieldChange' },
  { type: 'jail', toggle: 'toggleJailConfig', rows: 'updateJailRows', field: 'onJailFieldChange' },
  { type: 'bar', toggle: 'toggleBarConfig', rows: 'updateBarRows', field: 'onBarFieldChange' },
  { type: 'timeout', toggle: 'toggleTimeoutConfig', rows: 'updateTimeoutRows', field: 'onTimeoutFieldChange' },
  { type: 'detour', toggle: 'toggleDetourConfig', rows: 'updateDetourRows', field: 'onDetourFieldChange' },
  { type: 'roundAbout', toggle: 'toggleRoundAboutConfig', rows: 'updateRoundAboutRows', field: 'onRoundAboutFieldChange' },
  { type: 'signs', toggle: 'toggleSignsConfig', rows: 'updateSignsRows', field: 'onSignsFieldChange' },
  { type: 'stopSign', toggle: 'toggleStopSignConfig', rows: 'updateStopSignRows', field: 'onStopSignFieldChange' },
  { type: 'dice', toggle: 'toggleDiceConfig', rows: 'updateDiceRows', field: 'onDiceFieldChange' },
  { type: 'wheel', toggle: 'toggleWheelConfig', rows: 'updateWheelRows', field: 'onWheelFieldChange' }
];
function toggleSignsConfig(c) { return CC?.ClashSettings?.toggleObstacleConfig?.("signs", c); }
function toggleDetourConfig(c) { return CC?.ClashSettings?.toggleObstacleConfig?.("detour", c); }
function toggleRoundAboutConfig(c) { return CC?.ClashSettings?.toggleObstacleConfig?.("roundAbout", c); }
function toggleDiceConfig(c) { return CC?.ClashSettings?.toggleObstacleConfig?.("dice", c); }
function toggleWheelConfig(c) { return CC?.ClashSettings?.toggleObstacleConfig?.("wheel", c); }
function updateJumpOptionLabels(idx) { CC?.ClashSettings?.updateJumpOptionLabels?.(idx); }

function readSignsBlocksFromForm() { return CC?.ClashSettings?.readSignsBlocksFromForm ? CC.ClashSettings.readSignsBlocksFromForm({ target: settings?.target }) : []; }

function onSignBillboardClick(blockId) { return CC?.ClashMiniGames?.onSignBillboardClick?.(blockId, { state, log, fancyCall, getSignConfigs }); }

function toggleSkunkMarkersConfig(checked) { return CC?.ClashSettings?.toggleSkunkMarkersConfig?.(checked, { settings, onSkunkMarkerFieldChange, onDoubleSkunkMarkerFieldChange, syncAllClashPlacementButtons, readFormSettings, saveSettingsStore, buildBoard }); }

function onSkunkMarkerFieldChange(skipSave = false) { return CC?.ClashSettings?.onSkunkMarkerFieldChange?.(skipSave, { settings, getSkunkDefaults, getStreetOrTurnName, readFormSettings, saveSettingsStore, buildBoard }); }

function onDoubleSkunkMarkerFieldChange(skipSave = false) { return CC?.ClashSettings?.onDoubleSkunkMarkerFieldChange?.(skipSave, { settings, getSkunkDefaults, getStreetOrTurnName, readFormSettings, saveSettingsStore, buildBoard }); }

// ==========================================
// PINTS AND PEGS DRINKING OPTIONS SYSTEM (DELEGATED TO PINTSSETTINGS)
// ==========================================
var PINTS_DRINKING_DEFS = (CC && CC.PINTS_DRINKING_DEFS) ? CC.PINTS_DRINKING_DEFS : [];


function randomizePintsOptions(isFull = false) { return (typeof GameOptionsEngine !== 'undefined' && typeof GameOptionsEngine.randomizeBrandOptions === 'function') ? GameOptionsEngine.randomizeBrandOptions('pints', isFull) : undefined; }

// ==========================================
// SKUNKED AND STRIPPED (ADULT BRAND) SYSTEM (DELEGATED TO ADULTSETTINGS)
// ==========================================
var ADULT_OPTIONS_LIST = (CC && CC.ADULT_OPTIONS_LIST) ? CC.ADULT_OPTIONS_LIST : [];

function getDefaultAdultSettings() { return CC?.AdultSettings?.getDefaultSettings?.() || { target: 121, goal: 'high', theme: 'passion' }; }

function getAdultOptionDef(key) { return CC?.AdultSettings?.getOptionDef ? CC.AdultSettings.getOptionDef(key) : ((ADULT_OPTIONS_LIST && Array.isArray(ADULT_OPTIONS_LIST)) ? ADULT_OPTIONS_LIST.find(o => o.key === key) || null : null); }

function toggleAdultOptionConfig(optKey, checked, skipSave = false) { return CC?.AdultSettings?.toggleOptionConfig?.(optKey, checked, skipSave); }

function updateAdultOptionRows(optKey, skipSave = false) { return CC?.AdultSettings?.updateOptionRows?.(optKey, skipSave); }

function onAdultOptionPegsChange(optKey) { return CC?.AdultSettings?.onOptionPegsChange?.(optKey, settings); }

function onAdultOptionFieldChange(optKey, idx, skipSave = false) { return CC?.AdultSettings?.onOptionFieldChange?.(optKey, idx, skipSave); }

function getAdultOptionConfigs(optKey) { return CC?.AdultSettings?.getOptionConfigs?.(optKey, settings) || []; }

function randomizeAdultOptions(isFull = false) { return (typeof GameOptionsEngine !== 'undefined' && typeof GameOptionsEngine.randomizeBrandOptions === 'function') ? GameOptionsEngine.randomizeBrandOptions('adult', isFull) : undefined; }

// Adult Timed Challenge Controller
function showAdultTimerModal(optionDef, playerLabel, holeNum) { return CC?.ModalManager?.showAdultTimerModal?.(optionDef, playerLabel, holeNum, { state, log, setStatus, sfx }); }

function startAdultTimer() { return CC?.ModalManager?.startAdultTimer?.({ sfx }); }

function closeAdultTimerModal() { return CC?.ModalManager?.closeAdultTimerModal?.({ state, isSeatCpu, scheduleOppPlay, render }); }

function showAdultStripModal(optionDef, playerLabel, holeNum) { return CC?.ModalManager?.showAdultStripModal?.(optionDef, playerLabel, holeNum, { state, log, setStatus, sfx }); }

function closeAdultStripModal() { return CC?.ModalManager?.closeAdultStripModal?.({ state, isSeatCpu, scheduleOppPlay, render }); }

function resumeGameAfterAdultStrip() { return CC?.ModalManager?.resumeGameAfterAdultStrip?.({ state, isSeatCpu, scheduleOppPlay, render }); }

function resumeGameAfterAdultTimer() { return CC?.ModalManager?.resumeGameAfterAdultTimer?.({ state, isSeatCpu, scheduleOppPlay, render }); }

function showPintsBonusModal(optionDef, playerLabel, holeNum) { return CC?.PintsManager?.showPintsBonusModal?.(optionDef, playerLabel, holeNum, { state, log, setStatus, sfx, pintsManager: (typeof window !== 'undefined' ? window.pintsManager : null) }); }

function closePintsBonusModal() { return CC?.PintsManager?.closePintsBonusModal?.({ pintsManager: (typeof window !== 'undefined' ? window.pintsManager : null), hideModal, resumeGameAfterPintsBonus }); }

function resumeGameAfterPintsBonus() { return CC?.PintsManager?.resumeGameAfterPintsBonus?.({ state, isSeatCpu, scheduleOppPlay, render }); }


function readFormSettings() { return CC?.LobbyController?.readFormSettings?.({ getAppBrand, GameOptionsEngine, parseGameMode, settings, sanitizeClashBlockPlacements }) || {}; }

function updateTargetOptions(brand) { return CC?.LobbyController?.updateTargetOptions?.(brand); }

function applyFormSettings(saved) {
  if (CC?.LobbyController?.applyFormSettings) {
    const prev = _suppressSettingsSave; _suppressSettingsSave = true;
    try { return CC.LobbyController.applyFormSettings(saved, { settings, getAppBrand, GameOptionsEngine, setCardBack, updateCheckAllBonusesState, syncPlayerCountUi, enforceClashNoOverlap, applyBrandVisibility, syncAllClashPlacementButtons, updateCribbageClashOptionRanges }); } finally { _suppressSettingsSave = prev; }
  }
}

function applyBrandVisibility(brand) {
  if (CC?.LobbyController?.applyBrandVisibility) {
    const prev = _suppressSettingsSave; _suppressSettingsSave = true;
    try {
      return CC.LobbyController.applyBrandVisibility(brand, {
        settings,
        buildBoard,
        getAppBrand,
        GameOptionsEngine,
        uncheckPintsOptions,
        uncheckAdultOptions: window.uncheckAdultOptions,
        uncheckCribbageClashOptions,
        updateCribbageClashOptionRanges,
        updateCheckAllBonusesState
      });
    } finally {
      _suppressSettingsSave = prev;
    }
  }
}

function updateCheckAllBonusesState() { return CC?.LobbyController?.updateCheckAllBonusesState?.(); }

function saveSettingsStore(data) {
  if (_suppressSettingsSave) return;
  return CC?.SettingsManager?.saveSettingsStore ? CC.SettingsManager.saveSettingsStore(data, appStorage, getAppBrand(), _suppressSettingsSave) : null;
}
function loadSettingsStore() { return CC?.SettingsManager?.loadSettingsStore?.({ storage: appStorage, brand: typeof getAppBrand === 'function' ? getAppBrand() : 'crazy', defaultAdultSettings: typeof getDefaultAdultSettings === 'function' ? getDefaultAdultSettings : null, adultOptionsList: typeof ADULT_OPTIONS_LIST !== 'undefined' ? ADULT_OPTIONS_LIST : [] }) || null; }

let net = { peer: null, conn: null, clients: new Map(), room: null, ready: false, mySeatId: 'p1', isHost: false, roster: [] };
function setNetStatus(text, mode) { const el = document.getElementById('netStatus'); if (el) el.innerHTML = '<span id="netDot" class="' + (mode || '') + '"></span>' + text; }
function getLobbyRosterData() { return CC?.LobbyController?.getLobbyRosterData ? CC.LobbyController.getLobbyRosterData({ settings, net, mySeat, parseGameMode }) : []; }

function updateNetRoster(remoteRoster) { return CC?.NetworkManager?.updateNetRoster?.(remoteRoster, { net, settings, isOnline, getLobbyRosterData }); }

function broadcastLobbyUpdate() { return CC?.NetworkManager?.broadcastLobbyUpdate?.({ net, settings, getLobbyRosterData }); }
function serializeState() { return CC?.NetworkManager?.serializeState ? CC.NetworkManager.serializeState(state) : (state || {}); }
function getSanitizedStateForSeat(targetSeatId, rawState) { return CC?.NetworkManager?.sanitizeForClient ? CC.NetworkManager.sanitizeForClient(rawState, targetSeatId) : rawState; }
function netSend(msg, targetConn) { return CC?.NetworkManager?.netSend?.(msg, targetConn, { net, settings }); }
function netPush(extra = {}) { return CC?.NetworkManager?.netPush?.(extra, { net, settings, isOnline, state, serializeState, getSanitizedStateForSeat }); }

function applyRemoteState(payload) { return CC?.NetworkManager?.applyRemoteState?.(payload, { net, settings, state, setState: s => { state = s; }, hideWait, getBoardSignature, buildBoard, applySeatLabels, renderScores, render, fancyCall, sfx, log, setShowMeter, openShowModal, hideModal }); }
function handleSeatDiscardIntent(sId, ids) { return CC?.NetworkManager?.handleSeatDiscardIntent?.(sId, ids, { state, settings, getSeatHand, setSeatHand, setSeatKept, seatDiscardsToDeck, seatName, mySeat, getActiveSeatIds, isSeatHuman, isSeatCpu, isDealerTeam, chooseDiscard, setStatus, log, render, netPush, startPlayAfterDiscards, startStarter, beginCutStarter }); }
function hostApplyIntent(msg, fromConn) { return CC?.NetworkManager?.hostApplyIntent?.(msg, fromConn, { settings, state, netPush, resolveStolenCribGuess, pickCut, poneDoCutStarter, getSeatHand, playCard, handleSeatDiscardIntent, confirmDraw, playAgain, confirmWildAs }); }
function handleClientHello(conn, msg) { return CC?.NetworkManager?.handleClientHello?.(conn, msg, { settings, net, state, getLobbyRosterData, broadcastLobbyUpdate, updateNetRoster, setNetStatus, netPush }); }
function handleClientDisconnect(conn) { return CC?.NetworkManager?.handleClientDisconnect?.(conn, { net, broadcastLobbyUpdate, updateNetRoster, state, settings, setNetStatus, log, netPush, scheduleOppPlay, onPlayerOffline: handlePlayerOfflinePrompt, renderScores, render }); }
function handleHostDisconnect() { return CC?.NetworkManager?.handleHostDisconnect?.({ net, settings, state, setNetStatus, log, hideWait, onPlayerOffline: handlePlayerOfflinePrompt, renderScores, render }); }
function bindHostClientConn(conn) { return CC?.NetworkManager?.bindHostClientConn?.(conn, { handleClientHello, hostApplyIntent, handleClientDisconnect }); }
function makeRoomCode() { return CC?.NetworkManager?.generateRoomCode ? CC.NetworkManager.generateRoomCode() : 'ROOM99'; }
function getJoinUrl(code) { const roomCode = (code || net.room || (document.getElementById('optRoom') && document.getElementById('optRoom').value) || '').trim().toUpperCase(); return CC?.NetworkManager?.getJoinUrl ? CC.NetworkManager.getJoinUrl(roomCode) : (roomCode ? `?join=${encodeURIComponent(roomCode)}` : ''); }
function showHostInviteLink(code) { return CC?.NetworkManager?.showHostInviteLink?.(code, { getJoinUrl }); }
function copyRoomInviteLink(btnEl) { return CC?.NetworkManager?.copyRoomInviteLink?.(btnEl, { net, setNetStatus, getJoinUrl }); }
function shareRoomInvite() { return CC?.NetworkManager?.shareRoomInvite?.({ net, setNetStatus, getJoinUrl }); }
function shareRoomWhatsApp() { return CC?.NetworkManager?.shareRoomWhatsApp?.({ net, getJoinUrl }); }
function shareRoomEmail() { return CC?.NetworkManager?.shareRoomEmail?.({ net, getJoinUrl }); }
function fallbackCopyText(text, cb) { return CC?.NetworkManager?.fallbackCopyText?.(text, cb); }
function hostRoom() { return CC?.NetworkManager?.hostRoom?.({ setNetStatus, settings, net, makeRoomCode, showHostInviteLink, updateNetRoster, bindHostClientConn, hideWait }); }
function joinRoom(code) { return CC?.NetworkManager?.joinRoom?.(code, { setNetStatus, hideWait, settings, net, updateNetRoster, applyRemoteState, showModal, hideModal, onPlayerOffline: handlePlayerOfflinePrompt, renderScores, render, log }); }
function startGameOnline() { return CC?.LobbyController?.startGameOnline?.({ settings, net, getAppBrand, isAgeVerified, showAgeGateModal, hideModal, startGame, netPush }); }

function startGame() { return CC?.LobbyController?.startGame?.({ settings, net, getAppBrand, isAgeVerified, showAgeGateModal, hideMenuOptions: hideMenuOtions, parseGameMode, GameOptionsEngine, hostRoom, setCardBack, readFormSettings, sanitizeClashBlockPlacements, saveSettingsStore, makeDeck, applySeatLabels, resetMatch, isOnline, isHvH, is3Player1vs2, getNoPassingLanes, getJumpConfigs, getJailConfigs, getBarConfigs, getTimeoutConfigs, getDetourConfigs, getRoundAboutConfigs, getSignConfigs, getDiceConfigs, getWheelConfigs }); }

function resetMatch() {
  if (CC?.GameEngine?.resetMatch) {
    const res = CC.GameEngine.resetMatch({ settings, makeDeck, pegTracker: (typeof window !== 'undefined' ? window.pegTracker : null), getSignConfigs, setState: s => { state = s; if (typeof window !== 'undefined') window.state = s; }, hideModal, buildBoard, renderScores, beginCut });
    if (res?.state) {
      state = res.state;
      if (typeof window !== 'undefined') window.state = state;
      gameHands = res.gameHands || [];
    }
  }
  try {
    const repMgr = CC?.GameReplayManager || (typeof window !== 'undefined' ? window.GameReplayManager : null);
    if (repMgr) {
      repMgr.startRecording({ settings, state, brand: typeof getAppBrand === 'function' ? getAppBrand() : 'crazy', pName });
    }
  } catch (_) {}
}

function playAgain() { return CC?.GameEngine?.playAgain?.({ isOnline, settings, netSend, resetMatch, netPush }); }

function beginCut() { return CC?.GameEngine?.beginCut?.({ state, resetAnims, sfx, shuffle, makeDeck, setStatus, render, netPush, stepNextCut, resolveCuts, getActiveSeatIds, isSeatCpu, isSeatHuman, seatName, updateTurnPrompt, passTo, isWild, rankOf, log, fancyCall, cardLabel, mySeat, newHand }); }

function renderCutFan() {
  return CC?.GameEngine?.renderCutFan?.({ document, state, getActiveSeatIds, isSeatHuman, isOnline, mySeat, seatName, cardLabel, cardEl, pickCut });
}

function resolveCuts() { return CC?.GameEngine?.resolveCuts?.({ state, getActiveSeatIds, rankOf, isWild, log, setStatus, fancyCall, render, netPush, shuffle, makeDeck, stepNextCut, seatName, cardLabel, mySeat, newHand }); }
function pickCut(index, seat) { return CC?.GameEngine?.pickCut?.(index, seat, { state, isOnline, mySeat, settings, netSend, isWild, setStatus, sfx, render, netPush, stepNextCut, resolveCuts, getActiveSeatIds, isSeatCpu, isSeatHuman, seatName, updateTurnPrompt, passTo, rankOf, log, fancyCall, shuffle, makeDeck, cardLabel, newHand }); }
function stepNextCut() { return CC?.GameEngine?.stepNextCut?.({ state, getActiveSeatIds, isSeatCpu, isSeatHuman, seatName, setStatus, updateTurnPrompt, render, netPush, isOnline, passTo, isWild, sfx, resolveCuts, rankOf, log, fancyCall, shuffle, makeDeck, cardLabel, mySeat, newHand }); }
function doCut() { return CC?.GameEngine?.doCut?.({ state, pickCut }); }
function newHand() {
  try {
    const repMgr = CC?.GameReplayManager || (typeof window !== 'undefined' ? window.GameReplayManager : null);
    if (repMgr) {
      if (!repMgr.currentReplay) {
        repMgr.startRecording({ settings, state, brand: typeof getAppBrand === 'function' ? getAppBrand() : 'crazy', pName });
      }
      repMgr.recordEvent('round_start', { round: (state?.handsCompleted || 0) + 1, dealer: state?.dealer });
    }
  } catch (_) {}
  return CC?.GameEngine?.newHand?.({ state, resetAnims, sfx, getActiveSeatIds, settings, Deck: CC?.Deck || Deck, shuffle, makeDeck, setSeatHand, nextSeat, isSoloVsPairs, seatName, log, setStatus, fancyCall, render, finishDeal });
}
function openGuessStarterPrompt(poneSeat, onGuess) {
  const mm = (typeof window !== 'undefined' ? window.modalManager : null) || (CC?.ModalManager ? new CC.ModalManager() : null);
  return CC?.ModalManager?.openGuessStarterPrompt?.(poneSeat, onGuess, { seatName, showModal, hideModal }) ||
    mm?.openGuessStarterPrompt?.(poneSeat, onGuess, { seatName, showModal, hideModal });
}
function beginCutStarter(ctx = {}) { return CC?.GameEngine?.beginCutStarter?.({ state, settings, nextSeat, mySeat, isOnline, isSeatHuman, isSeatCpu, seatName, setStatus, render, netPush, poneDoCutStarter, passTo, openGuessStarterPrompt, ...ctx }); }
function poneDoCutStarter(ctx = {}) { return CC?.GameEngine?.poneDoCutStarter?.({ state, isOnline, settings, netSend, mySeat, sfx, startStarter, openGuessStarterPrompt, nextSeat, isSeatCpu, isSeatHuman, ...ctx }); }
function announcePlayTurn(seatId) { return CC?.GameEngine?.announcePlayTurn?.(seatId, { state, isOnline, isSeatHuman, mySeat, seatName, fancyCall, setStatus, netPush, boardRenderer3D: (typeof window !== 'undefined' ? (window.boardRenderer3D || window._currentRenderer3D) : null) }); }
function startPlayAfterDiscards() { return CC?.GameEngine?.startPlayAfterDiscards?.({ state, nextSeat, hideWait, render, netPush, isSeatCpu, isSeatHuman, isOnline, scheduleOppPlay, passTo, fancyCall, seatName, mySeat, setStatus, boardRenderer3D: (typeof window !== 'undefined' ? (window.boardRenderer3D || window._currentRenderer3D) : null) }); }
function finishDeal() {
  try {
    const repMgr = CC?.GameReplayManager || (typeof window !== 'undefined' ? window.GameReplayManager : null);
    if (repMgr?.currentReplay) {
      repMgr.recordEvent('deal', { hands: state?.hands || state?.seatHands });
    }
  } catch (_) {}
  return CC?.GameEngine?.finishDeal?.({ state, getActiveSeatIds, isSeatCpu, isSeatHuman, isDealerTeam, getSeatHand, setSeatHand, setSeatKept, cpuAssignWilds, settings, beginCutStarter, isWild, setStatus, render, startDiscardOrPickFlow, startCanadianDoublesFlow, showModal, hideModal, seatName, log, fancyCall, nextSeat, passTo, hideWait, startPlayAfterDiscards, startStarter, openGuessStarterPrompt, poneDoCutStarter, netPush, openWildPick });
}
function startStarter(ctx = {}) { return CC?.GameEngine?.startStarter?.({ state, settings, hideWait, setStatus, fancyCall, render, netPush, finishStarterRitual, document, beginCutStarter, openGuessStarterPrompt, nextSeat, isSeatCpu, isSeatHuman, seatName, poneDoCutStarter, passTo, isOnline, mySeat, netSend, sfx, ...ctx }); }
function finishStarterRitual() { return CC?.GameEngine?.finishStarterRitual?.({ state, isWild, isSeatHuman, render, openWildPick, hideWait, setStatus, seatName, isHvH, passTo, cpuAssignWilds, afterStarterAssigned }); }
function confirmP2Discard() { return CC?.GameEngine?.confirmP2Discard?.({ state, isOnline, settings, netSend, render, startPlayAfterDiscards, startStarter, beginCutStarter, openGuessStarterPrompt, poneDoCutStarter, nextSeat }); }
function onPickCribCard(seatId, c) { return CC?.GameEngine?.onPickCribCard?.(seatId, c, { state, render }); }
function startHumanPickCrib() { return CC?.GameEngine?.startHumanPickCrib?.({ state, finishPickYourCrib, settings, seatName, setStatus, render }); }
function confirmPickCrib() { return CC?.GameEngine?.confirmPickCrib?.({ state, getSeatHand, setSeatHand, setSeatKept, seatName, log, fancyCall, startHumanPickCrib, finishPickYourCrib }); }
function finishPickYourCrib() { return CC?.GameEngine?.finishPickYourCrib?.({ state, settings, startPlayAfterDiscards, startStarter, beginCutStarter, openGuessStarterPrompt, poneDoCutStarter, nextSeat }); }
function cpuPickFromPone(dealerSeat, poneSeat, pickN) { return CC?.GameEngine?.cpuPickFromPone?.(dealerSeat, poneSeat, pickN, { state, getSeatHand, setSeatHand, setSeatKept, seatName, log }); }
function cpuDoPickYourCrib() { return CC?.GameEngine?.cpuDoPickYourCrib?.({ state, settings, getSeatHand, chooseDiscard, setSeatHand, setSeatKept, getActiveSeatIds, cpuPickFromPone, fancyCall, setStatus, seatName, cardLabel, render, startPlayAfterDiscards, startStarter, beginCutStarter, openGuessStarterPrompt, poneDoCutStarter, nextSeat }); }
function startCanadianDoublesFlow() { return CC?.GameEngine?.startCanadianDoublesFlow?.({ state, settings, getSeatHand, setSeatHand, setSeatKept, isSeatCpu, isSeatHuman, isDealerTeam, seatName, log, fancyCall, nextSeat, passTo, hideWait, setStatus, render, startPlayAfterDiscards, startStarter, beginCutStarter, openGuessStarterPrompt, poneDoCutStarter, showModal, hideModal }); }
function startDiscardOrPickFlow() { return CC?.GameEngine?.startDiscardOrPickFlow?.({ settings, state, isSeatCpu, isSeatHuman, isDealerTeam, isTwoHuman, passTo, beginDiscard, cpuDoPickYourCrib, getSeatHand, chooseDiscard, setSeatHand, setSeatKept, seatName, log, fancyCall, render, startPlayAfterDiscards, startStarter, beginCutStarter, openGuessStarterPrompt, poneDoCutStarter, nextSeat, hideWait, setStatus, showModal, hideModal }); }

function confirmDiscard() {
  try {
    const repMgr = CC?.GameReplayManager || (typeof window !== 'undefined' ? window.GameReplayManager : null);
    if (repMgr?.currentReplay) {
      repMgr.recordEvent('discard', { who: mySeat, discards: state?.selected || [] });
    }
  } catch (_) {}
  return CC?.GameEngine?.confirmDiscard?.({ state, confirmPickCrib, settings, mySeat, isOnline, netSend, setStatus, render, handleSeatDiscardIntent, getSeatHand, setSeatHand, setSeatKept, seatDiscardsToDeck, seatName, log, getActiveSeatIds, startHumanPickCrib, isTwoHuman, hideWait, passTo, isWild, netPush, isSeatHuman, isSeatCpu, isDealerTeam, chooseDiscard, startPlayAfterDiscards, startStarter, beginDiscard, beginCutStarter, openGuessStarterPrompt, poneDoCutStarter, nextSeat });
}
function confirmDraw() { return CC?.GameEngine?.confirmDraw?.({ state, settings, isOnline, netSend, mySeat, getSeatHand, setSeatHand, setSeatKept, seatName, log, fancyCall, netPush, render, passTo, isTwoHuman, isSeatCpu, beginDiscard, startPlayAfterDiscards, startStarter, setStatus, updateCoachDisplay, seatDiscardsToDeck, isSoloVsPairs, isDealerTeam, rankOf, VAL, hideWait, isWild, isSeatHuman, finishStarterRitual, document, CribbageAI: CC?.CribbageAI || CribbageAI }); }

function findNextPlayerWithPlay(fromSeat, count) { const seats = getActiveSeatIds(), hands = {}; for (const s of seats) hands[s] = getSeatHand(s); return CC?.CribbageAI?.findNextPlayerWithPlay ? CC.CribbageAI.findNextPlayerWithPlay(fromSeat, count, seats, hands) : null; }

function settleGo(whoJustPlayed, arg2, arg3) {
  const goSeats = (Array.isArray(arg2) || typeof arg2 === 'string') ? arg2 : ((Array.isArray(arg3) || typeof arg3 === 'string') ? arg3 : null);
  const extraCtx = (arg2 && typeof arg2 === 'object' && !Array.isArray(arg2)) ? arg2 : {};
  return CC?.GameEngine?.settleGo?.(whoJustPlayed, { state, fancyCall, render, netPush, finishPlay, getActiveSeatIds, getSeatHand, nextSeat, isSeatCpu, isSeatHuman, isOnline, scheduleOppPlay, passTo, document, seatName, mySeat, log, setStatus, boardRenderer3D: (typeof window !== 'undefined' ? (window.boardRenderer3D || window._currentRenderer3D) : null), ...extraCtx }, goSeats);
}

function playCard(who, card) { return CC?.GameEngine?.playCard?.(who, card, { state, settings, getSeatHand, VAL, rankOf, pegPoints, isBonusMessage, announceCombos, seatName, cardLabel, isWild, log, sfx, addScore, fancyPegPhrase, render, netPush, settleGo, findNextPlayerWithPlay, isSeatCpu, scheduleOppPlay, isOnline, isSeatHuman, passTo, fancyCall, getActiveSeatIds, getSeatKept, setSeatKept, mySeat, setStatus, boardRenderer3D: (typeof window !== 'undefined' ? (window.boardRenderer3D || window._currentRenderer3D) : null) }); }

function cpuSeatPlay(seatId) { return CC?.GameEngine?.cpuSeatPlay?.(seatId, { state, getSeatHand, choosePlay, settings, log, seatName, findNextPlayerWithPlay, render, netPush, isSeatCpu, scheduleOppPlay, isOnline, isSeatHuman, passTo, addScore, settleGo, playCard, mySeat, setStatus, fancyCall, getActiveSeatIds, boardRenderer3D: (typeof window !== 'undefined' ? (window.boardRenderer3D || window._currentRenderer3D) : null) }); }

function cpuTurn() { return CC?.GameEngine?.cpuTurn?.({ state, getSeatHand, choosePlay, settings, log, seatName, findNextPlayerWithPlay, render, netPush, isSeatCpu, scheduleOppPlay, isOnline, isSeatHuman, passTo, addScore, settleGo, playCard, mySeat, setStatus, fancyCall, getActiveSeatIds, boardRenderer3D: (typeof window !== 'undefined' ? (window.boardRenderer3D || window._currentRenderer3D) : null) }); }

function miniCardHtml(c, extra) { return CC && CC.TableRenderer ? CC.TableRenderer.miniCardHtml(c, extra) : ''; }

function showPegPoints() { return CC?.ShowModal?.showPegPoints?.({ state, settings }); }

function showHandPoints(key) { return CC?.ShowModal?.showHandPoints?.(key, { state, settings }); }

function setShowMeter(act, total, line, isBonus = false) { return CC?.ShowModal?.setShowMeter?.(act, total, line, isBonus); }

function famousFanfare(n) { return CC?.ShowModal?.famousFanfare?.(n, { sfx, fancyCall }); }

function finishPlay() { return CC?.GameEngine?.finishPlay?.({ state, settings, getPlayerCount, getSeatKept, setSeatKept, scoreShow, pName, seatName, openShowModal, beginShowAct, nextSeat, isSoloVsPairs, getActiveSeatIds, log }); }

function cpuGuessCrib(poneSeat, dealerSeat) { const myDiscards = state?.seatDiscards?.[poneSeat] || []; if (!state?.starter || myDiscards.length < 2) return 4; return CC?.CribbageAI?.guessStolenCrib ? CC.CribbageAI.guessStolenCrib(myDiscards, state.starter, getSeatKept(poneSeat) || [], FULL_DECK, { scoreShow, shuffle }) : 4; }
function selectStolenChip(n) { return CC?.ShowModal?.selectStolenChip?.(n); }

function openStolenCribPrompt(poneSeat, dealerSeat, actIdx) { return CC?.ShowModal?.openStolenCribPrompt?.(poneSeat, dealerSeat, actIdx, { seatName, showModal, hideModal, sendStolenCribGuess }); }

function sendStolenCribGuess(val, actIdx) { return CC?.ShowModal?.sendStolenCribGuess?.(val, actIdx, { isOnline, settings, netSend, showWait, resolveStolenCribGuess }); }

function resolveStolenCribGuess(guess, actIdx) { return CC?.ShowModal?.resolveStolenCribGuess?.(guess, actIdx, { state, seatName, log, fancyCall, famousFanfare, netPush, beginShowAct }); }

function openTwistedCribbageModal(dealerSeat, actIdx) {
  return CC?.ShowModal?.openTwistedCribbageModal?.(dealerSeat, actIdx, {
    state, settings, getSeatKept, setSeatKept, seatName, pName, log, fancyCall,
    showModal, hideModal, render, netPush, beginShowAct, isOnline, resolveTwistedCribbageSplit
  });
}

function resolveTwistedCribbageSplit(hand1, hand2, actIdx) {
  return CC?.ShowModal?.resolveTwistedCribbageSplit?.(hand1, hand2, actIdx, {
    state, settings, getSeatKept, setSeatKept, seatName, pName, log, fancyCall,
    render, netPush, beginShowAct
  });
}

function beginShowAct(i) { return CC?.ShowModal?.beginShowAct?.(i, { state, setShowMeter, openShowModal, settings, getPlayerCount, render, getSeat, isOnline, seatName, openStolenCribPrompt, openTwistedCribbageModal, resolveTwistedCribbageSplit, getSeatKept, setSeatKept, log, showWait, fancyCall, cpuGuessCrib, resolveStolenCribGuess, resetAnims, netPush, finishShowAct, tickShowLine, isSeatHuman, showModal, hideModal, pName, checkGameOver, checkChutesAndLadders, sfx, renderScores, getActiveTracks }); }

function finishShowAct(actIdx) {
  return CC?.ShowModal?.finishShowAct?.(actIdx, {
    state, settings, scoreKey, isInPegThroughZone, isInLast5PegOut, isInBunker,
    applyHighlights, setShowMeter, pName, log, fancyCall, renderScores, netPush,
    famousFanfare, addScore, openShowModal, beginShowAct, isSeatHuman, mySeat,
    getActiveTracks, checkGameOver,
    showCardAssassin: (opts) => {
      const assassinOpt = settings?.cardAssassin ?? 'no';
      if (assassinOpt !== 'yes') {
        if (opts && typeof opts.onComplete === 'function') {
          opts.onComplete();
        }
        return;
      }
      const fn = CC?.CardAssassinOverlay?.show || (typeof window !== 'undefined' && window.CardAssassinOverlay?.show) || CardAssassinOverlay?.show;
      if (typeof fn === 'function') {
        return fn(opts);
      } else if (opts && typeof opts.onComplete === 'function') {
        opts.onComplete();
      }
    }
  });
}

function tickShowLine(actIdx, lineIdx) { return CC?.ShowModal?.tickShowLine?.(actIdx, lineIdx, { state, isBonusMessage, cardKey, applyHighlights, setShowMeter, fancyCall, sfx, netPush, finishShowAct }); }

function saveHand(state) { return CC?.ShowModal?.saveHand?.(state, gameHands); }

function openShowModal(pegOnly) { return CC?.ShowModal?.open?.(pegOnly, { state, settings, checkGameOver }); }

function showModal(html, closable = false) { if ((typeof window !== 'undefined' ? window.modalManager : null)) { (typeof window !== 'undefined' ? window.modalManager : null).setTheme(theme); return (typeof window !== 'undefined' ? window.modalManager : null).showModal(html, closable); } }
function hideModal() { if ((typeof window !== 'undefined' ? window.modalManager : null)) return (typeof window !== 'undefined' ? window.modalManager : null).hideModal(state); if (state?.passingTo) state.passingTo = null; }
function showOverlay(html, closable = false) { return showModal(html, closable); }
function hideOverlay() { return hideModal(); }
function closeModal() { return hideModal(); }
function closeOverlay() { return hideModal(); }

function setupCloseButtonCompat() { if (typeof document === "undefined") return;
  const origGetById = document.getElementById.bind(document);
  document.getElementById = function(id) {
    if (id === 'overlayClose') return origGetById('modalClose') || origGetById('modalBar');
    return origGetById(id);
  };
  const origQuerySelector = document.querySelector.bind(document);
  document.querySelector = function(sel) {
    if (sel === '#overlayClose') return origGetById('modalClose') || origGetById('modalBar') || origQuerySelector(sel);
    return origQuerySelector(sel);
  };
}
function showConfirmModal(opts) {
  if (typeof window !== 'undefined' && window.modalManager && typeof window.modalManager.showConfirmModal === 'function') {
    return window.modalManager.showConfirmModal(opts);
  }
  if (CC?.ModalManager?.showConfirmModal) {
    return CC.ModalManager.showConfirmModal(opts);
  }
  if (typeof ModalManager !== 'undefined') {
    return new ModalManager().showConfirmModal(opts);
  }
}

function backToMenu() { return CC?.ModalManager?.backToMenu?.({ state, hideModal, showConfirmModal, renderRecordBox }); }

function returnToMenu() { return CC?.ModalManager?.returnToMenu?.({ state, hideModal, showConfirmModal, renderRecordBox }); }

function cancelGameAndReturnToMenu() {
  if (state) state.over = true;
  if (typeof hideModal === 'function') hideModal();
  if (typeof hideWait === 'function') hideWait();
  const waitBar = typeof document !== 'undefined' ? document.getElementById('reconnectWaitBar') : null;
  if (waitBar) waitBar.remove();

  try {
    const camMgr = (typeof window !== 'undefined' && (window._ccCameraManager || (window.CrazyCribbage && window.CrazyCribbage.CameraManager && window.CrazyCribbage.CameraManager.getInstance())));
    if (camMgr && camMgr.isBroadcasting) {
      camMgr.stopBroadcast();
    }
    const chatMgr = (typeof window !== 'undefined' && (window._ccChatManager || (window.CrazyCribbage && window.CrazyCribbage.ChatManager && window.CrazyCribbage.ChatManager.getInstance())));
    if (chatMgr) {
      chatMgr.setChatVisibility(false);
      chatMgr.clearChat();
    }
  } catch (e) {}

  if (net && net.conn) {
    try {
      if (typeof netPush === 'function') {
        netPush({ type: 'game_cancelled', text: 'The game was cancelled.' });
      }
    } catch (e) {}
  }
  if (net) {
    net.ready = false;
  }

  if (typeof document !== 'undefined') {
    const game = document.getElementById('game');
    const menu = document.getElementById('menu');
    if (game) game.classList.add('hidden');
    if (menu) menu.classList.remove('hidden');
  }

  if (typeof renderRecordBox === 'function') renderRecordBox();
  if (typeof setNetStatus === 'function') {
    setNetStatus('Game cancelled.', 'idle');
  }
}

const DROPDOWN_DESCRIPTIONS = CC?.DROPDOWN_DESCRIPTIONS || {};

function showDropdownInfoModal(key) { return CC?.DropdownHelpManager?.showDropdownInfoModal?.(key, { settings, theme, getAppBrand, showModal, hideModal, setCardBack }); }

function selectOptionFromModal(key, val) { return CC?.DropdownHelpManager?.selectOptionFromModal?.(key, val, { setCardBack, hideModal }); }

function initDropdownLabels() { return CC?.DropdownHelpManager?.initDropdownLabels?.({ showDropdownInfoModal }); }

function syncPlayerCountUi() { return CC?.LobbyController?.syncPlayerCountUi?.({ settings, parseGameMode, getAppBrand, GameOptionsEngine }); }

function updateCribbageClashOptionRanges(targetOrIsClash) { return CC?.ClashPlacementModal?.updateOptionRanges?.(targetOrIsClash, { settings }); }

function randomizeCrazyCribbageBonuses() { return CC?.LobbyController?.randomizeCrazyCribbageBonuses?.({ GameOptionsEngine, getRandomSubset }); }

function randomizeCribbageClashOptions(isFull = false) { return (CC && CC.ClashPlacementModal) ? CC.ClashPlacementModal.randomizeOptions(isFull, { settings }) : undefined; }

function getJumpTriggerLength(jumpIdx, overrideStart = null) { return (CC && typeof CC.getJumpTriggerLength === 'function') ? CC.getJumpTriggerLength(jumpIdx, overrideStart) : 1; }

var CLASH_ITEM_METADATA = (CC && CC.ClashPlacementModal && CC.ClashPlacementModal.getMetadata) ? CC.ClashPlacementModal.getMetadata(settings) : {};

function initClashPlacementButtons() { return (CC && CC.ClashPlacementModal) ? CC.ClashPlacementModal.initPlacementButtons(CLASH_ITEM_METADATA) : undefined; }

function syncClashPlacementButton(selectId) { return (CC && CC.ClashPlacementModal) ? CC.ClashPlacementModal.syncPlacementButton(selectId, CLASH_ITEM_METADATA) : undefined; }

function syncAllClashPlacementButtons() { return (CC && CC.ClashPlacementModal) ? CC.ClashPlacementModal.syncAllPlacementButtons(CLASH_ITEM_METADATA) : undefined; }

function getAllPlacedClashItems(excludeSelectId) { return (CC && CC.ClashPlacementModal) ? CC.ClashPlacementModal.getAllPlacedItems(excludeSelectId, CLASH_ITEM_METADATA) : []; }

function openClashPlacementModal(selectId) { return (CC && CC.ClashPlacementModal) ? CC.ClashPlacementModal.open(selectId, { settings, meta: CLASH_ITEM_METADATA }) : undefined; }

function closeClashPlacementModal() { return CC?.ClashPlacementModal?.close?.(); }

function onConfirmClashPlacement() { return CC?.ClashPlacementModal?.onConfirm?.({ settings, meta: CLASH_ITEM_METADATA }); }

function renderClashPlacementBoard(selectId) { return CC?.ClashPlacementModal?.renderPlacementBoard?.(selectId, { settings, meta: CLASH_ITEM_METADATA }); }

function uncheckCribbageClashOptions() { return CC?.ClashPlacementModal?.uncheckClashOptions?.(); }

function uncheckPintsOptions() { return CC?.ClashPlacementModal?.uncheckPintsOptions?.(); }

function initTableSplitter() { return CC?.UIController?.initTableSplitter?.(); }


function initThemeButtons() { return CC?.UIController?.initThemeButtons?.({ applyTheme, toggleTheme }); }

function initSoundButton() { return CC?.UIController?.initSoundButton?.({ sfx }); }

function checkInitialAgeGate() { return CC?.LobbyController?.checkInitialAgeGate?.({ getAppBrand, isAgeVerified, showAgeGateModal }); }


function setupNetUi() { return CC?.NetworkManager?.setupNetUi?.({ settings, net, showHostInviteLink, hostRoom, joinRoom, copyRoomInviteLink, startGameOnline, syncPlayerCountUi, getAppBrand, showModal, hideModal, GameOptionsEngine: typeof GameOptionsEngine !== 'undefined' ? GameOptionsEngine : null }); }

function handleZoneOptionChange(changedEl) { return CC?.LobbyController?.handleZoneOptionChange?.(changedEl, { updateCheckAllBonusesState }); }

function setupCheckAllBonuses() { return CC?.LobbyController?.setupCheckAllBonuses?.({ updateCheckAllBonusesState, parseGameMode, toggleSignsConfig, toggleDetourConfig, toggleRoundAboutConfig, handleZoneOptionChange }); }


function toggleMenuSettings(fromGame = false) { return CC?.UIController?.toggleMenuSettings?.(fromGame, { state, hideModal, showConfirmModal, renderRecordBox }); }

function toggleMenuVariants(fromGame = false) { return CC?.UIController?.toggleMenuVariants?.(fromGame, { state, hideModal, showConfirmModal, renderRecordBox }); }

function hideMenuOtions() { return CC?.UIController?.hideMenuOptions?.(); }

function hideMenuVariants() { return CC?.UIController?.hideMenuVariants?.(); }

function setupCloseButtonEvents() { return CC?.ModalManager?.setupCloseButtonEvents?.({ hideModal, showIntro, showRecordsModal, toggleMenuSettings, showShareModal }); }


function analyzeKeepEV(keep, deadIds) {
  return CC?.CoachController?.analyzeKeepEV?.(keep, deadIds, { starter: state?.starter, fullDeck: FULL_DECK }) || CC?.CribbageAI?.analyzeKeepEV?.(keep, deadIds, state?.starter, FULL_DECK) || { min: 0, max: 0, avg: 0 };
}
function updateCoachDisplay(hoverDiscards) { return CC?.CoachController?.updateCoachDisplay?.(hoverDiscards, { settings, state, cardLabel, fullDeck: FULL_DECK }); }

function showIntro() { return CC?.ModalManager?.showIntro?.(typeof getAppBrand === 'function' ? getAppBrand() : 'crazy', { showModal }); }
function showRecordsModal() {
  const prof = (typeof window !== 'undefined' && window.Profile) || CC?.Profile || Profile;
  if (prof && typeof prof.showRecordsModal === 'function') {
    return prof.showRecordsModal({ brand: typeof getAppBrand === 'function' ? getAppBrand() : 'crazy', showModal, storage: appStorage });
  }
  return CC?.SettingsManager?.showRecordsModal?.({ brand: typeof getAppBrand === 'function' ? getAppBrand() : 'crazy', showModal });
}
function resetGameStatistics() {
  const prof = (typeof window !== 'undefined' && window.Profile) || CC?.Profile || Profile;
  if (prof && typeof prof.resetStatsConfirm === 'function') {
    return prof.resetStatsConfirm();
  }
  return CC?.SettingsManager?.resetGameStatistics?.({ brand: typeof getAppBrand === 'function' ? getAppBrand() : 'crazy', showConfirmModal });
}
function showShareModal() { return (typeof window !== 'undefined' ? window.modalManager : null)?.showShareModal?.({ settings, net, getJoinUrl }); }
function showJoinModal() { return (typeof window !== 'undefined' ? window.modalManager : null)?.showJoinModal?.({ settings, net, joinRoom, hostRoom, sfx, getJoinUrl }); }
function showDiscardTrainerModal(context = {}) { return (typeof window !== 'undefined' ? window.modalManager : null)?.showDiscardTrainerModal?.(context) || CC?.ModalManager?.showDiscardTrainerModal?.(context); }
function showPeggingTrainerModal(context = {}) { return (typeof window !== 'undefined' ? window.modalManager : null)?.showPeggingTrainerModal?.(context) || CC?.ModalManager?.showPeggingTrainerModal?.(context); }
function showCountingTrainerModal(context = {}) { return (typeof window !== 'undefined' ? window.modalManager : null)?.showCountingTrainerModal?.(context) || CC?.ModalManager?.showCountingTrainerModal?.(context); }
function showCoachPostMortemModal(context = {}) { return (typeof window !== 'undefined' ? window.modalManager : null)?.showCoachPostMortemModal?.(context) || CC?.ModalManager?.showCoachPostMortemModal?.(context); }
function shareCoachPostMortem(report) { return (typeof window !== 'undefined' ? window.modalManager : null)?.showCoachPostMortemShareModal?.(report) || (CC?.CoachPostMortem || CoachPostMortem).openShareDialog(report); }
function showDailyPuzzleModal(context = {}) { return (typeof window !== 'undefined' ? window.modalManager : null)?.showDailyPuzzleModal?.(context) || CC?.ModalManager?.showDailyPuzzleModal?.(context) || ModalManager.showDailyPuzzleModal(context); }
function showTrophyRoomModal(brand = 'all') { return CC?.AchievementManager?.showTrophyRoomModal ? CC.AchievementManager.showTrophyRoomModal(brand) : null; }
function showReplaysModal(brand = 'all') { const modal = (CC?.ReplayModal || window.ReplayModal || ReplayModal); return modal?.openLibrary ? modal.openLibrary(brand) : modal?.open?.(null, brand); }
function showGameSummaryModal(context = {}) { return (typeof window !== 'undefined' ? window.modalManager : null)?.showGameSummaryModal?.({ state, settings, ...context }) || CC?.ModalManager?.showGameSummaryModal?.({ state, settings, ...context }); }
function showGameWonModal() { return (typeof window !== 'undefined' && window.GameEngine?.showGameWonModal?.()) || CC?.GameEngine?.showGameWonModal?.() || (typeof GameEngine !== 'undefined' && GameEngine.showGameWonModal ? GameEngine.showGameWonModal() : false); }
function showHandOfGloryModal(keyOrData, context = {}) { return (typeof window !== 'undefined' ? window.modalManager : null)?.showHandOfGloryModal?.(keyOrData, { state, settings, ...context }) || CC?.ModalManager?.showHandOfGloryModal?.(keyOrData, { state, settings, ...context }) || CC?.HandOfGloryCard?.showModal?.(keyOrData, { state, settings, ...context }) || HandOfGloryCard?.showModal?.(keyOrData, { state, settings, ...context }); }
function closeHandOfGloryModal() { return (typeof window !== 'undefined' ? window.modalManager : null)?.closeHandOfGloryModal?.() || CC?.ModalManager?.closeHandOfGloryModal?.() || CC?.HandOfGloryCard?.closeModal?.() || HandOfGloryCard?.closeModal?.(); }
function openClashSandbox(context = {}) { return (typeof window !== 'undefined' ? window.ClashSandboxModal : null)?.open?.({ state, settings, ...context }) || CC?.ClashSandboxModal?.open?.({ state, settings, ...context }) || ClashSandboxModal?.open?.({ state, settings, ...context }); }
function applyClashPreset(key, context = {}) { return (typeof window !== 'undefined' ? window.ClashPresets : null)?.applyPreset?.(key, { state, settings, ...context }) || CC?.ClashPresets?.applyPreset?.(key, { state, settings, ...context }) || ClashPresets?.applyPreset?.(key, { state, settings, ...context }); }
function copyShareLink() { return CC?.ModalManager?.copyShareLink?.(); }
function triggerNativeShare() { return (typeof window !== 'undefined' ? window.modalManager : null)?.triggerNativeShare?.() || CC?.ModalManager?.triggerNativeShare?.(); }

function showStartGameModal() {
  if (typeof window !== 'undefined' && window.modalManager && typeof window.modalManager.showStartGameModal === 'function') {
    return window.modalManager.showStartGameModal({ getAppBrand, isAgeVerified, showAgeGateModal, syncPlayerCountUi });
  }
  const overlay = document.getElementById('startGameOverlay');
  if (overlay) overlay.style.display = 'flex';
}
function hideStartGameModal() {
  if (typeof window !== 'undefined' && window.modalManager && typeof window.modalManager.hideStartGameModal === 'function') {
    return window.modalManager.hideStartGameModal();
  }
  const overlay = document.getElementById('startGameOverlay');
  if (overlay) overlay.style.display = 'none';
}
function confirmAndStartGame() {
  const modalVal = document.getElementById('optBoardViewModal')?.value;
  hideStartGameModal();
  startGame();
  if (modalVal === 'room3d-fullscreen') {
    try {
      const b3d = document.getElementById('board3DContainer');
      if (b3d && b3d.requestFullscreen) {
        b3d.requestFullscreen().catch(() => {});
      }
    } catch (_) {}
  }
}


function initAutoJoin() { return CC?.NetworkManager?.initAutoJoin?.({ settings, setNetStatus, showWait, hideWait, joinRoom, showModal, hideModal }); }


function updateMobileDeviceClass() { return CC?.UIController?.updateMobileDeviceClass?.(); }



export class CrazyCribbageApp {
  constructor(options = {}) {
    this.options = options;
    this.CC = CC;
    this.BOARD_CFG = BOARD_CFG;
    this.BOARD_CFG_481 = BOARD_CFG_481;
    this.CLASH_DEFAULTS_481 = CLASH_DEFAULTS_481;
    this.CLASH_DEFAULTS_241 = CLASH_DEFAULTS_241;
    this.CLASH_DEFAULTS_361 = CLASH_DEFAULTS_361;
    this.CARD_BACKS = CARD_BACKS;
    this.THEMES = THEMES;
    this.CLASH_BLOCK_DEFS = CLASH_BLOCK_DEFS;
    this.PINTS_DRINKING_DEFS = PINTS_DRINKING_DEFS;
    this.ADULT_OPTIONS_LIST = ADULT_OPTIONS_LIST;
    this.DROPDOWN_DESCRIPTIONS = DROPDOWN_DESCRIPTIONS;
    this.CLASH_ITEM_METADATA = CLASH_ITEM_METADATA;
    this.CRAZY_OPTIONS_DEFS = CRAZY_OPTIONS_DEFS;

    // Instance state encapsulation
    this._state = { scores: { you: 0, cpu: 0 }, prev: { you: 0, cpu: 0 }, over: false, dealer: 'you' };
    state = this._state;
    const resolvedBrand = (typeof window !== 'undefined' && typeof window.getAppBrand === 'function')
      ? window.getAppBrand()
      : (typeof getAppBrand === 'function' ? getAppBrand() : 'crazy');
    this._settings = (CC && CC.SettingsManager && CC.SettingsManager.DEFAULT_SETTINGS)
      ? JSON.parse(JSON.stringify(CC.SettingsManager.DEFAULT_SETTINGS))
      : { opp: 'cpu', playerCount: 2, gameMode: 'individual', target: 121, decks: 1, cardBack: 'classic-blue' };
    if (resolvedBrand === 'clash') {
      this._settings.target = 481;
    }
    settings = this._settings;
    this._pegState = pegState || { p1: { front: 0, rear: 0 }, p2: { front: 0, rear: 0 }, you: { front: 0, rear: 0 }, cpu: { front: 0, rear: 0 } };
    pegState = this._pegState;
    this._net = net || { peer: null, conn: null, clients: new Map(), room: null, ready: false, mySeatId: 'p1', isHost: false, roster: [] };
    net = this._net;
    this._gameHands = [];
    gameHands = this._gameHands;
    this._theme = theme || 'tournament';
    this._recordCache = null;

    Object.defineProperty(this, 'state', { get: () => this._state, set: v => { this._state = state = v; }, configurable: true });
    Object.defineProperty(this, 'settings', { get: () => this._settings, set: v => { this._settings = settings = v; }, configurable: true });
    Object.defineProperty(this, 'pegState', { get: () => this._pegState, set: v => { this._pegState = pegState = v; }, configurable: true });
    Object.defineProperty(this, 'net', { get: () => this._net, set: v => { this._net = net = v; }, configurable: true });
    Object.defineProperty(this, 'gameHands', { get: () => this._gameHands, set: v => { this._gameHands = gameHands = v; }, configurable: true });
    Object.defineProperty(this, 'theme', { get: () => this._theme, set: v => { this._theme = theme = v; }, configurable: true });
    Object.defineProperty(this, 'recordCache', { get: () => this._recordCache, set: v => { this._recordCache = recordCache = v; }, configurable: true });
    Object.defineProperty(this, '_suppressSettingsSave', { get: () => _suppressSettingsSave, set: v => { _suppressSettingsSave = v; }, configurable: true });
    Object.defineProperty(this, '_isEnforcingClashNoOverlap', { get: () => _isEnforcingClashNoOverlap, set: v => { _isEnforcingClashNoOverlap = v; }, configurable: true });
    Object.defineProperty(this, '_isRandomizingClash', { get: () => _isRandomizingClash, set: v => { _isRandomizingClash = v; }, configurable: true });
    Object.defineProperty(this, 'sfx', { get: () => sfx, set: v => {}, configurable: true });
    Object.defineProperty(this, 'FULL_DECK', { get: () => FULL_DECK, set: v => { FULL_DECK = v; }, configurable: true });
  }


  init() {
    if (typeof document === 'undefined') return this;
    const brand = typeof getAppBrand === 'function' ? getAppBrand() : (window.currentBrand || 'crazy');
    if (brand === 'clash') {
      if (!this._settings.target || ![241, 361, 481].includes(this._settings.target)) {
        this._settings.target = 481;
        settings.target = 481;
      }
      const isClashBoard = this._settings.selected3DBoardId === 'board-cribbage-clash' ||
        this._settings.selected3DBoardId === 'board-cribbage-clash-square' ||
        (Board3DCatalog.getBoards('clash').some(b => b.id === this._settings.selected3DBoardId));
      if (!this._settings.selected3DBoardId || !isClashBoard) {
        this._settings.selected3DBoardId = 'board-cribbage-clash';
        settings.selected3DBoardId = 'board-cribbage-clash';
      }
    } else if (brand === 'pints') {
      if (!this._settings.target || [241, 361, 481].includes(this._settings.target)) {
        this._settings.target = 121;
        settings.target = 121;
      }
      if (this._settings.selected3DBoardId === 'board-cribbage-clash' || this._settings.selected3DBoardId === 'board-cribbage-clash-square') {
        this._settings.selected3DBoardId = 'board-14';
        settings.selected3DBoardId = 'board-14';
      }
    }
    setupCloseButtonEvents();
    setupCheckAllBonuses();

    // Automatically load default 3D boards from JSON configuration file if available (avoids needing bundle rebuilds)
    if (typeof Board3DCatalog !== 'undefined' && typeof Board3DCatalog.autoLoad === 'function') {
      Board3DCatalog.autoLoad().then(res => {
        if (res && res.success) {
          const el3d = document.getElementById('opt3DBoard');
          if (el3d && CC?.LobbyController) {
            Board3DCatalog.syncDropdownOptions(el3d, settings?.selected3DBoardId, brand);
          }
          this.syncInGameBoardSelector();
        }
      }).catch(() => {});
    }

    if (typeof applyBrandVisibility === 'function') applyBrandVisibility(brand);
    if (CC?.LobbyController && typeof CC.LobbyController.disableOtherGameBonusOptions === 'function') {
      CC.LobbyController.disableOtherGameBonusOptions(brand, this._settings);
      CC.LobbyController.disableOtherGameBonusOptions(brand, settings);
    }
    applyFormSettings(loadSettingsStore());
    if (CC?.LobbyController && typeof CC.LobbyController.disableOtherGameBonusOptions === 'function') {
      CC.LobbyController.disableOtherGameBonusOptions(brand, this._settings);
      CC.LobbyController.disableOtherGameBonusOptions(brand, settings);
    }
    initTableSplitter();
    initCardBackPicker();
    initDropdownLabels();
    renderRecordBox();
    syncPlayerCountUi();
    initThemeButtons();
    initSoundButton();
    checkInitialAgeGate();
    setupNetUi();
    this._state = state = { scores: { you: 0, cpu: 0 }, prev: { you: 0, cpu: 0 }, over: false, dealer: 'you' };
    buildBoard({ skipLoader: true });
    initBoardFancyTooltips();
    renderScores(false);
    this.bindDomEvents();
    initAutoJoin();
    updateMobileDeviceClass();
    this.updateBoardViewToggleBtn();
    this.syncInGameBoardSelector();
    try {
      const savedBoardState = (typeof localStorage !== 'undefined' ? localStorage.getItem('cc_board_display_state') : null);
      if (savedBoardState && savedBoardState !== 'normal') {
        this.setBoardDisplayState(savedBoardState);
      } else {
        this.updateBoardSizeButtons();
      }
    } catch (_) {}

    const applyStoredSettingsAndSync = () => {
      checkInitialAgeGate();
      try {
        const stored = loadSettingsStore();
        if (stored) {
          applyFormSettings(stored);
          buildBoard({ skipLoader: true });
          renderScores(false);
          this.syncInGameBoardSelector();
        }
      } catch (err) {
        console.warn('[CrazyCribbageApp] Failed to apply settings from storage:', err);
      }
    };

    if (appStorage && appStorage.ready && typeof appStorage.ready.then === 'function') {
      appStorage.ready.then(() => {
        applyStoredSettingsAndSync();
        _suppressSettingsSave = false;
      }).catch(() => {
        _suppressSettingsSave = false;
      });
    } else {
      _suppressSettingsSave = false;
    }

    try {
      if (typeof window !== 'undefined' && window.location?.search?.includes('replay=')) {
        const urlParams = new URLSearchParams(window.location.search);
        const repCode = urlParams.get('replay');
        if (repCode) {
          const repMgr = CC?.GameReplayManager || window.CrazyCribbage?.GameReplayManager || GameReplayManager;
          const repModal = CC?.ReplayModal || window.CrazyCribbage?.ReplayModal || ReplayModal;
          if (repMgr && repModal) {
            repMgr.decompressReplay(repCode).then(replay => {
              if (replay && Array.isArray(replay.events)) {
                setTimeout(() => {
                  repModal.open(replay);
                }, 400);
              }
            }).catch(() => {});
          }
        }
      }
    } catch (_) {}

    return this;
  }

  bindDomEvents() {
    if (typeof document === 'undefined') return;
    const byId = id => document.getElementById(id);
    const on = (id, event, handler, options) => {
      const el = byId(id);
      if (el) el.addEventListener(event, handler, options);
    };
    const onAll = (selector, event, handler) => {
      document.querySelectorAll(selector).forEach(el => el.addEventListener(event, handler));
    };

    // Orbit menu buttons
    on('btnStart', 'click', showStartGameModal);
    on('btnNew', 'click', backToMenu);
    on('btnJoinMenu', 'click', showJoinModal);
    on('btnSettingsMenu', 'click', () => toggleMenuSettings());
    on('btnVariantsMenu', 'click', () => toggleMenuVariants());
    on('btnThemeMenu', 'click', toggleTheme);
    on('btnRecords', 'click', showRecordsModal);
    on('btnShareMenu', 'click', showShareModal);
    on('btnHelp', 'click', showIntro);
    on('btnBrandMenu', 'click', (e) => toggleBrandDropdown(e));

    // Brand dropdown items
    on('brandItemCrazy', 'click', (e) => handleBrandLinkClick(e, 'crazy'));
    on('brandItemClash', 'click', (e) => handleBrandLinkClick(e, 'clash'));
    on('brandItemPints', 'click', (e) => handleBrandLinkClick(e, 'pints'));
    on('brandItemAdult', 'click', (e) => handleBrandLinkClick(e, 'adult'));

    // Trainers / Features launched from menu
    on('btnLaunchTrainer', 'click', () => showDiscardTrainerModal());
    on('btnLaunchPeggingTrainer', 'click', () => showPeggingTrainerModal());
    on('btnLaunchCountingTrainer', 'click', () => showCountingTrainerModal());
    on('btnLaunchDailyPuzzle', 'click', () => showDailyPuzzleModal());
    on('btnLaunchTrophyRoom', 'click', () => showTrophyRoomModal());
    on('btnLaunchTourney', 'click', () => {
      if (window.TournamentModal) window.TournamentModal.open();
      else if (window.showTournamentModal) window.showTournamentModal();
    });
    on('btnLaunch3DBoard', 'click', () => this.showBoard3DModal());
    on('btnLaunchReplays', 'click', () => {
      const b = typeof getAppBrand === 'function' ? getAppBrand() : 'all';
      (CC?.ReplayModal || window.ReplayModal || ReplayModal)?.openLibrary?.(b);
    });

    // Menu options / variants panel close and play
    onAll('#menuOptions .menu-options-close', 'click', () => toggleMenuSettings());
    on('btnStartSettings', 'click', () => { showStartGameModal(); toggleMenuSettings(); });
    onAll('#menuOptions .settings-play-label', 'click', () => { showStartGameModal(); toggleMenuSettings(); });
    onAll('.menu-variants-close', 'click', () => {
      if (window.hideMenuVariants) window.hideMenuVariants();
      else toggleMenuVariants();
    });
    onAll('.menu-variants-play-btn', 'click', () => {
      showStartGameModal();
      if (window.hideMenuVariants) window.hideMenuVariants();
      else toggleMenuVariants();
    });

    // Header action bar
    on('btnThemeHeader', 'click', toggleTheme);
    on('btnRecordsHeader', 'click', showRecordsModal);
    on('btnScorecardHeader', 'click', showGameSummaryModal);
    on('btnSettingsHeader', 'click', () => toggleMenuSettings(true));
    on('btnBoardHeader', 'click', () => this.showBoard3DModal());
    on('btnShareHeader', 'click', showShareModal);
    on('btnReplaysHeader', 'click', () => {
      const b = typeof getAppBrand === 'function' ? getAppBrand() : 'all';
      (CC?.ReplayModal || window.ReplayModal || ReplayModal)?.openLibrary?.(b);
    });
    on('btnHelpHeader', 'click', showIntro);

    // Avatars & in-game controls
    const handleAvatarClick = () => {
      const av = (this.CC && this.CC.AvatarManager) || AvatarManager || window.AvatarManager;
      if (av && typeof av.openAvatarModal === 'function') {
        av.openAvatarModal();
      } else if (typeof window.openAvatarModal === 'function') {
        window.openAvatarModal();
      }
    };
    on('scoreChipAvatarYou', 'click', handleAvatarClick);
    on('youZoneAvatar', 'click', handleAvatarClick);
    on('inGameBoardTriggerThumb', 'error', function() {
      if (this.parentElement) this.parentElement.style.display = 'none';
    });
    on('btnChange3DBoard', 'click', () => this.showBoard3DModal());
    on('btnOpenBoardGallery', 'click', () => this.showBoard3DModal());

    on('btnToggleBoardView', 'click', (e) => {
      e.stopPropagation();
      this.toggleInGameBoardViewDropdown();
    });
    on('inGameBoardViewDropdownTrigger', 'click', (e) => {
      e.stopPropagation();
      this.toggleInGameBoardViewDropdown();
    });
    const inGameOptBoardView = byId('inGameOptBoardView');
    if (inGameOptBoardView) {
      inGameOptBoardView.addEventListener('change', () => this.selectBoardViewMode(inGameOptBoardView.value));
    }
    const boardViewMenu = byId('inGameBoardViewDropdownMenu');
    if (boardViewMenu) {
      boardViewMenu.querySelectorAll('.board-view-dropdown-item[data-view-mode]').forEach(item => {
        item.addEventListener('click', (e) => {
          e.stopPropagation();
          const mode = item.getAttribute('data-view-mode');
          if (mode) this.selectBoardViewMode(mode);
        });
        item.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            const mode = item.getAttribute('data-view-mode');
            if (mode) this.selectBoardViewMode(mode);
          }
        });
      });

      const fsItem = boardViewMenu.querySelector('.board-view-fullscreen-item');
      if (fsItem) {
        const handleFsClick = (e) => {
          e.stopPropagation();
          this.closeInGameBoardViewDropdown();
          this.toggle3DRoomFullScreen();
        };
        fsItem.addEventListener('click', handleFsClick);
        fsItem.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleFsClick(e);
          }
        });
      }
    }

    const dropdownTrigger = byId('inGameBoardDropdownTrigger');
    if (dropdownTrigger) {
      dropdownTrigger.addEventListener('click', (e) => {
        e.stopPropagation();
        this.toggleInGameBoardDropdown();
      });
    }
    const inGameOpt3DBoard = byId('inGameOpt3DBoard');
    if (inGameOpt3DBoard) {
      inGameOpt3DBoard.addEventListener('change', () => this.select3DBoard(inGameOpt3DBoard.value));
    }

    if (typeof document !== 'undefined') {
      document.addEventListener('click', (e) => {
        const wrap = document.getElementById('inGameBoardDropdownWrap');
        if (wrap && !wrap.contains(e.target)) {
          this.closeInGameBoardDropdown();
        }
        const wrapBv = document.getElementById('inGameBoardViewDropdownWrap');
        if (wrapBv && !wrapBv.contains(e.target)) {
          this.closeInGameBoardViewDropdown();
        }
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.closeInGameBoardDropdown();
          this.closeInGameBoardViewDropdown();
        }
      });
    }

    on('btnFullscreen3D', 'click', () => this.toggle3DRoomFullScreen());
    on('btnMinimizeBoard', 'click', () => this.toggleBoardMinimize());
    on('btnMaximizeBoard', 'click', () => this.toggleBoardMaximize());
    on('btnFloatingRestoreBoard', 'click', () => this.restoreBoard());

    // Generic Modal Close Buttons
    onAll('.modal-btn-close', 'click', hideModal);

    // Overlays & Start Game Dialog
    on('startGameOverlay', 'click', e => {
      if (e.target.id === 'startGameOverlay') hideStartGameModal();
    });
    on('btnCloseStartGame', 'click', hideStartGameModal);
    on('btnModeTourney', 'click', () => {
      if (typeof hideStartGameModal === 'function') hideStartGameModal();
      if (window.TournamentModal) window.TournamentModal.open();
      else if (window.showTournamentModal) window.showTournamentModal();
    });
    on('optPlayerName', 'input', function() {
      this.value = this.value.replace(/[^a-zA-Z0-9]/g, '');
    });
    on('optBoardViewModal', 'change', function() {
      const el = document.getElementById('optBoardView');
      if (el) el.value = this.value;
      if (window.settings) window.settings.boardViewMode = this.value;
    });
    on('optBoardView', 'change', function() {
      const el = document.getElementById('optBoardViewModal');
      if (el) el.value = this.value;
    });
    on('btnShareInvite', 'click', () => { if (window.shareRoomInvite) window.shareRoomInvite(); });
    on('btnShareWhatsApp', 'click', () => { if (window.shareRoomWhatsApp) window.shareRoomWhatsApp(); });
    on('btnShareEmail', 'click', () => { if (window.shareRoomEmail) window.shareRoomEmail(); });
    on('btnShareMore', 'click', () => { if (window.showShareModal) window.showShareModal(); });
    on('btnNetTrackSandbox', 'click', () => {
      if (window.openClashSandbox) window.openClashSandbox();
      else if (window.ClashSandboxModal) window.ClashSandboxModal.open();
    });
    on('btnConfirmStartGame', 'click', confirmAndStartGame);
    on('btnStartGameMoreSettings', 'click', () => {
      hideStartGameModal();
      toggleMenuSettings();
    });
    onAll('.start-game-btn-cancel', 'click', hideStartGameModal);

    // Hand of Glory Modal
    on('gloryOverlay', 'click', e => {
      if (e.target.id === 'gloryOverlay') closeHandOfGloryModal();
    });

    // Adult / Pints Modals
    on('adultTimerOverlay', 'click', e => {
      if (e.target.id === 'adultTimerOverlay') closeAdultTimerModal();
    });
    on('btnAdultTimerStart', 'click', startAdultTimer);
    on('btnAdultTimerClose', 'click', closeAdultTimerModal);
    on('adultBonusOverlay', 'click', e => {
      if (e.target.id === 'adultBonusOverlay') closeAdultStripModal();
    });
    on('btnAdultBonusDone', 'click', closeAdultStripModal);
    on('pintsBonusOverlay', 'click', e => {
      if (e.target.id === 'pintsBonusOverlay') closePintsBonusModal();
    });
    on('btnPintsBonusDone', 'click', closePintsBonusModal);

    // Age Gate
    on('btnAgeGateConfirm', 'click', confirmAgeGate);
    on('btnAgeGateExit', 'click', exitAgeGateToSafeBrand);

    const topChips = byId('boardTopChips');
    if (topChips) {
      topChips.addEventListener('dblclick', (e) => {
        if (e.target.closest('button, select, input, a')) return;
        this.toggleBoardMinimize();
      });
      topChips.addEventListener('click', (e) => {
        if (this.getBoardDisplayState() === 'minimized' && !e.target.closest('button, select, input, a')) {
          this.restoreBoard();
        }
      });
    }

    window.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.getBoardDisplayState() !== 'normal') {
        this.restoreBoard();
      }
    });

    on('btnCamToggle', 'click', async () => {
      const camMgr = (CC?.CameraManager && CC.CameraManager.getInstance ? CC.CameraManager.getInstance() : (typeof CameraManager !== 'undefined' ? CameraManager.getInstance() : (window._ccCameraManager || null)));
      if (!camMgr) return;
      const camCtx = { net, settings, byId, document, setStatus };
      if (!camMgr.isBroadcasting) {
        await camMgr.startBroadcast(camCtx);
      } else if (camMgr.isPaused) {
        camMgr.resumeBroadcast(camCtx);
      } else {
        camMgr.pauseBroadcast(camCtx);
      }
    });

    on('gamePlayLogo', 'click', returnToMenu);

    on('btnDiscard', 'click', () => {
      if (state.phase === 'pick-crib') { confirmPickCrib(); return; }
      if (state.phase === 'draw-1' || state.phase === 'draw-2' || state.phase === 'draw-1-p2' || state.phase === 'draw-2-p2') {
        confirmDraw();
        return;
      }
      showWait(state.phase === 'discard-p2' ? 'Turning the starter…' : 'Discarding and setting the starter…');
      setTimeout(() => {
        try { confirmDiscard(); } catch (e) { hideWait(); throw e; }
        if (state.phase !== 'play') hideWait();
      }, 40);
    });

    const clashOverlayEl = byId('clashPlacementOverlay');
    if (clashOverlayEl) clashOverlayEl.addEventListener('click', e => { if (e.target === clashOverlayEl) closeClashPlacementModal(); });

    if (typeof window !== 'undefined') {
      window.addEventListener('keydown', e => {
        if (e.key === 'Escape') {
          const ov = byId('clashPlacementOverlay');
          if (ov && ov.style.display === 'flex') closeClashPlacementModal();
          const adultOv = byId('adultTimerOverlay');
          if (adultOv && adultOv.style.display === 'flex' && typeof closeAdultTimerModal === 'function') closeAdultTimerModal();
        }
      });
    }

    on('btnCloseClashPlacement', 'click', closeClashPlacementModal);
    on('btnCancelClashPlacement', 'click', closeClashPlacementModal);
    on('btnConfirmClashPlacement', 'click', onConfirmClashPlacement);

    const bTarget = byId('optTarget');
    if (bTarget) {
      const bClash = byId('cribbage-clash-options');
      const handleTargetChange = (isInitial = false) => {
        const brand = typeof getAppBrand === 'function' ? getAppBrand() : (window.currentBrand || 'crazy');
        let targetVal = bTarget ? parseInt(bTarget.value, 10) : 121;
        if (brand === 'clash' && ![241, 361, 481].includes(targetVal)) {
          targetVal = 481;
          if (bTarget) bTarget.value = '481';
        }
        const prevTarget = settings.target;
        settings.target = targetVal;
        if (typeof window !== 'undefined' && window.settings) {
          window.settings.target = targetVal;
        }
        const isClashMode = (brand === 'clash') || [241, 361, 481].includes(targetVal);
        if (typeof applyBrandVisibility === 'function') applyBrandVisibility(brand);
        else if (bClash) { bClash.style.display = isClashMode ? 'block' : 'none'; if (!isClashMode) uncheckCribbageClashOptions(); }
        updateCribbageClashOptionRanges(isClashMode ? targetVal : false);
        initClashPlacementButtons();
        if (!isInitial || prevTarget !== targetVal) {
          buildBoard();
        }
        if (!_suppressSettingsSave && !isInitial && typeof saveSettingsStore === 'function' && typeof readFormSettings === 'function') {
          saveSettingsStore(readFormSettings());
        }
      };
      bTarget.addEventListener('change', () => handleTargetChange(false));
      handleTargetChange(true);
    }

    const optPlayerCountEl = byId('optPlayerCount');
    if (optPlayerCountEl) {
      optPlayerCountEl.addEventListener('change', () => {
        const cdChk = byId('optCanadianDoubles');
        if (optPlayerCountEl.value !== '4_part' && cdChk && cdChk.checked) {
          cdChk.checked = false;
          if (settings) settings.optCanadianDoubles = false;
        }
        syncPlayerCountUi();
        if (typeof saveSettingsStore === 'function' && typeof readFormSettings === 'function') {
          saveSettingsStore(readFormSettings());
        }
        renderRecordBox();
      });
    }

    ['optPlayerCount','optDeal','optDealStyle','optShowDetail','optCardAssassin','optJailAnimation','optBarAnimation','optCannonAnimation','optSpeedTrapAnimation','optDetourAnimation','optRoundAboutAnimation','optScenicRouteAnimation','optGoal','optTarget','optDecks','optCardBack','optBoardFinish','optPegSkin','optPegStyle','optTableEnvironment','optActionCam','optSeriesMode','optOpp','optCoach','optAiProfile','optBoardView','opt3DBoard','optSeat2Type','optSeat3Type','optSeat4Type','optSeat5Type','optSeat6Type','optSeat7Type','optSeat8Type'].forEach(id => {
      const sel = byId(id);
      if (sel && id !== 'optPlayerCount') {
        sel.addEventListener('change', () => {
          if (id === 'optShowDetail') {
            settings.showDetail = sel.value;
            if (sel.value === 'muggins') settings.optMuggins = true;
          }
          if (id === 'optCardAssassin') {
            settings.cardAssassin = sel.value;
          }
          if (id === 'optJailAnimation') {
            settings.jailAnimation = sel.value;
          }
          if (id === 'optBarAnimation') {
            settings.barAnimation = sel.value;
          }
          if (id === 'optCannonAnimation') {
            settings.cannonAnimation = sel.value;
            settings.jumpAnimation = sel.value;
          }
          if (id === 'optSpeedTrapAnimation') {
            settings.speedTrapAnimation = sel.value;
            settings.stopSignAnimation = sel.value;
            settings.stopAnimation = sel.value;
          }
          if (id === 'optDetourAnimation') {
            settings.detourAnimation = sel.value;
          }
          if (id === 'optRoundAboutAnimation') {
            settings.roundAboutAnimation = sel.value;
          }
          if (id === 'optScenicRouteAnimation') {
            settings.scenicRouteAnimation = sel.value;
          }
          if (id === 'optActionCam') {
            settings.optActionCam = sel.value;
          }
          if (id === 'optBoardView') {
            settings.boardViewMode = sel.value;
            if (typeof buildBoard === 'function') {
              buildBoard();
              renderScores(false);
            }
            if (typeof this.updateBoardViewToggleBtn === 'function') {
              this.updateBoardViewToggleBtn();
            }
          }
          if (id === 'opt3DBoard') {
            settings.selected3DBoardId = sel.value;
            const inGameEl = byId('inGameOpt3DBoard');
            if (inGameEl) inGameEl.value = sel.value;
            if (typeof buildBoard === 'function') {
              buildBoard();
              renderScores(false);
            }
          }
          if (id === 'optBoardFinish') {
            settings.boardFinish = sel.value;
            const boardTracks = byId('boardTracks');
            if (boardTracks && boardTracks.querySelector('svg') && typeof buildBoard === 'function') {
              buildBoard();
              renderScores(false);
            }
          }
          if (id === 'optPegSkin') {
            settings.pegSkin = sel.value;
            const boardTracks = byId('boardTracks');
            if (boardTracks && boardTracks.querySelector('svg') && typeof buildBoard === 'function') {
              buildBoard();
              renderScores(false);
            }
          }
          if (id === 'optPegStyle') {
            settings.pegStyle = sel.value;
            if (typeof window !== 'undefined' && window._currentRenderer3D) {
              window._currentRenderer3D.setPegStyle(sel.value);
            }
          }
          if (id === 'optTableEnvironment') {
            settings.tableEnvironment = sel.value;
            if (typeof window !== 'undefined' && window._currentRenderer3D) {
              window._currentRenderer3D.setTableEnvironment(sel.value);
            }
          }
          if (id === 'optSeriesMode') {
            settings.seriesMode = sel.value;
            SeriesTracker.setMode(sel.value);
          }
          if (typeof saveSettingsStore === 'function' && typeof readFormSettings === 'function') {
            saveSettingsStore(readFormSettings());
          }
          renderRecordBox();
        });
      }
    });

    const optOppEl = byId('optOpp');
    if (optOppEl) {
      optOppEl.addEventListener('change', (e) => {
        const brand = typeof getAppBrand === 'function' ? getAppBrand() : 'crazy';
        const allowCpu = typeof GameOptionsEngine !== 'undefined' && typeof GameOptionsEngine.isCpuAllowed === 'function' ? GameOptionsEngine.isCpuAllowed(brand) : !['pints', 'adult'].includes(brand);
        if (!allowCpu && e.target.value === 'cpu') e.target.value = 'online';
        syncPlayerCountUi();
      });
    }

    const gameModeBar = byId('gameModeBar');
    if (gameModeBar) {
      gameModeBar.querySelectorAll('.game-mode-pill').forEach(pill => {
        const mode = pill.dataset.mode;
        if (!mode || mode === 'tournament') return;
        pill.addEventListener('click', () => {
          const brand = typeof getAppBrand === 'function' ? getAppBrand() : 'crazy';
          const allowCpu = typeof GameOptionsEngine !== 'undefined' && typeof GameOptionsEngine.isCpuAllowed === 'function' ? GameOptionsEngine.isCpuAllowed(brand) : !['pints', 'adult'].includes(brand);
          if (mode === 'cpu' && !allowCpu) return;
          const optOpp = byId('optOpp');
          if (optOpp) {
            optOpp.value = mode;
          }
          if (mode === 'cpu') {
            const defaultCpu = { 2: 'cpu:shark', 3: 'cpu:regular', 4: 'cpu:turtler', 5: 'cpu:maverick', 6: 'cpu:master', 7: 'cpu:rookie', 8: 'cpu:casual' };
            for (let i = 2; i <= 8; i++) {
              const seatEl = byId(`optSeat${i}Type`);
              if (seatEl && (seatEl.value === 'human' || seatEl.value === 'online')) {
                seatEl.value = defaultCpu[i] || 'cpu:shark';
              }
            }
          }
          syncPlayerCountUi();
          if (typeof saveSettingsStore === 'function' && typeof readFormSettings === 'function') {
            saveSettingsStore(readFormSettings());
          }
        });
      });
    }

    const overlayEl = byId('overlay');
    if (overlayEl) {
      overlayEl.addEventListener('click', e => {
        if (e.target.id === 'overlay') {
          if (overlayEl.classList.contains('closable')) {
            hideModal();
            return;
          }
        }
      });
    }

    const avMgr = (this.CC && this.CC.AvatarManager) || AvatarManager;
    if (avMgr && typeof avMgr.syncAvatarUI === 'function') {
      avMgr.syncAvatarUI();
    }
    const btnSelectAvatar = byId('btnSelectAvatar');
    if (btnSelectAvatar) {
      btnSelectAvatar.addEventListener('click', () => {
        if (avMgr && typeof avMgr.openAvatarModal === 'function') {
          avMgr.openAvatarModal();
        }
      });
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', updateMobileDeviceClass);
      window.addEventListener('orientationchange', updateMobileDeviceClass);

      if (window.location) {
        try {
          const params = new URLSearchParams(window.location.search);
          if (params.get('tool') === 'trainer' || params.get('mode') === 'trainer') {
            setTimeout(() => showDiscardTrainerModal(), 300);
          }
          if (params.get('tool') === 'pegging' || params.get('mode') === 'pegging') {
            setTimeout(() => showPeggingTrainerModal(), 300);
          }
          if (params.get('tool') === 'counting' || params.get('mode') === 'counting' || params.get('trainer') === 'counting') {
            const decoded = (CC?.CountingTrainer || window.CountingTrainer || CountingTrainer)?.decodeShareUrl?.(window.location.search);
            setTimeout(() => showCountingTrainerModal(decoded || {}), 300);
          }
          if (params.get('tool') === 'coach' || params.get('tool') === 'postmortem') {
            setTimeout(() => showCoachPostMortemModal(), 300);
          }
          if (params.get('testAnim') === 'bar' || params.get('anim') === 'bar') {
            setTimeout(() => {
              const showBar = window.showBarAnimation || (this.CC && this.CC.BarAnimationOverlay && this.CC.BarAnimationOverlay.show) || BarAnimationOverlay.show;
              if (typeof showBar === 'function') {
                showBar({
                  playerName: 'You',
                  who: 'p1',
                  barHole: 40,
                  score: 40,
                  card: { r: 'K', s: '♠' },
                  settings: { barAnimation: 'yes' }
                });
              }
            }, 600);
          } else if (params.get('testAnim') === 'jail' || params.get('anim') === 'jail') {
            setTimeout(() => {
              const showJail = window.showJailAnimation || (this.CC && this.CC.JailAnimationOverlay && this.CC.JailAnimationOverlay.show) || JailAnimationOverlay.show;
              if (typeof showJail === 'function') {
                showJail({
                  playerName: 'You',
                  who: 'p1',
                  jailHole: 25,
                  score: 25,
                  card: { r: 'J', s: '♠' },
                  settings: { jailAnimation: 'yes' }
                });
              }
            }, 600);
          } else if (params.get('testAnim') === 'cannon' || params.get('anim') === 'cannon') {
            setTimeout(() => {
              const showCannon = window.showCannonAnimation || (this.CC && this.CC.CannonAnimationOverlay && this.CC.CannonAnimationOverlay.show) || CannonAnimationOverlay.show;
              if (typeof showCannon === 'function') {
                showCannon({
                  playerName: 'You',
                  who: 'p1',
                  fromHole: 30,
                  toHole: 55,
                  diff: 25,
                  isForward: true,
                  card: { r: 'A', s: '♠' },
                  settings: { cannonAnimation: 'yes' }
                });
              }
            }, 600);
          } else if (params.get('testAnim') === 'speedtrap' || params.get('anim') === 'speedtrap' || params.get('anim') === 'radar') {
            setTimeout(() => {
              const showTrap = window.showSpeedTrapAnimation || (this.CC && this.CC.SpeedTrapAnimationOverlay && this.CC.SpeedTrapAnimationOverlay.show) || SpeedTrapAnimationOverlay.show;
              if (typeof showTrap === 'function') {
                showTrap({
                  playerName: 'You',
                  who: 'p1',
                  reason: 'stop_sign',
                  hole: 35,
                  ptsScored: 2,
                  ptsLost: 3,
                  card: { r: 'K', s: '♠' },
                  settings: { speedTrapAnimation: 'yes' }
                });
              }
            }, 600);
          } else if (params.get('testAnim') === 'roundabout' || params.get('anim') === 'roundabout') {
            setTimeout(() => {
              const showScenic = window.showScenicRouteAnimation || (this.CC && this.CC.ScenicRouteAnimationOverlay && this.CC.ScenicRouteAnimationOverlay.show) || ScenicRouteAnimationOverlay.show;
              if (typeof showScenic === 'function') {
                showScenic({
                  type: 'roundabout',
                  playerName: 'You',
                  who: 'p1',
                  hole: 35,
                  card: { r: 'Q', s: '♥' },
                  settings: { scenicRouteAnimation: 'yes' }
                });
              }
            }, 600);
          } else if (params.get('testAnim') === 'detour' || params.get('anim') === 'detour') {
            setTimeout(() => {
              const showScenic = window.showScenicRouteAnimation || (this.CC && this.CC.ScenicRouteAnimationOverlay && this.CC.ScenicRouteAnimationOverlay.show) || ScenicRouteAnimationOverlay.show;
              if (typeof showScenic === 'function') {
                showScenic({
                  type: 'detour',
                  playerName: 'You',
                  who: 'p1',
                  hole: 10,
                  card: { r: 'J', s: '♦' },
                  settings: { scenicRouteAnimation: 'yes' }
                });
              }
            }, 600);
          }
        } catch (e) {}
      }
    }
  }

  updateBoardViewToggleBtn() {
    if (typeof document === 'undefined') return;
    const btn = document.getElementById('btnToggleBoardView');
    const labelEl = document.getElementById('inGameBoardViewTriggerLabel');
    const iconEl = document.getElementById('inGameBoardViewTriggerIcon');
    const menuEl = document.getElementById('inGameBoardViewDropdownMenu');
    const nativeSel = document.getElementById('inGameOptBoardView');
    const mode = (this.settings && this.settings.boardViewMode) || 'room3d-fullscreen';

    let icon = '🎲';
    let text = '3D Board';
    let title = 'Current: 3D Board (Click to select board view)';

    if (mode === 'room3d-fullscreen') {
      icon = '⛶';
      text = '3D Room (FS)';
      title = 'Current: 3D Room (Full Screen) (Click to select board view)';
    } else if (mode === 'room3d') {
      icon = '🏛️';
      text = '3D Room';
      title = 'Current: 3D Room (Click to select board view)';
    } else if (mode === 'none') {
      icon = '🚫';
      text = 'No Board';
      title = 'Current: No Board (Click to select board view)';
    } else if (mode === '3d') {
      icon = '🎲';
      text = '3D Board';
      title = 'Current: 3D Board (Click to select board view)';
    } else {
      icon = '📋';
      text = 'Flat Board';
      title = 'Current: 2D Flat Board (Click to select board view)';
    }

    if (labelEl) labelEl.textContent = text;
    if (iconEl) iconEl.textContent = icon;
    if (btn) {
      if (!labelEl) {
        btn.innerHTML = `${icon} ${text}`;
      }
      btn.title = title;
      btn.setAttribute('aria-pressed', (mode === '3d' || mode === 'room3d' || mode === 'room3d-fullscreen') ? 'true' : 'false');
    }
    if (nativeSel) {
      nativeSel.value = mode;
    }

    if (menuEl) {
      menuEl.querySelectorAll('.board-view-dropdown-item').forEach(item => {
        const itemMode = item.getAttribute('data-view-mode');
        const isSelected = (itemMode === mode);
        item.classList.toggle('is-selected', isSelected);
        item.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      });
    }

    const b3dControls = document.getElementById('board3DIngameControls');
    const btnFullscreen = document.getElementById('btnFullscreen3D');
    if (b3dControls) {
      if (mode === '2d' || mode === 'none') {
        b3dControls.classList.add('hidden');
      } else {
        b3dControls.classList.remove('hidden');
      }
    }
    if (btnFullscreen) {
      if (mode === '2d' || mode === 'none') {
        btnFullscreen.classList.add('hidden');
      } else {
        btnFullscreen.classList.remove('hidden');
      }
    }
  }

  selectBoardViewMode(mode) {
    if (!mode) return;
    this.settings.boardViewMode = mode;
    if (typeof settings !== 'undefined' && settings) {
      settings.boardViewMode = mode;
    }
    if (typeof window !== 'undefined' && window.settings) {
      window.settings.boardViewMode = mode;
    }
    const elBv = document.getElementById('optBoardView');
    if (elBv) elBv.value = mode;
    const elBvm = document.getElementById('optBoardViewModal');
    if (elBvm) elBvm.value = mode;
    const elInGame = document.getElementById('inGameOptBoardView');
    if (elInGame) elInGame.value = mode;

    if (typeof saveSettingsStore === 'function') saveSettingsStore(this.settings);
    if (typeof buildBoard === 'function') buildBoard();
    if (typeof renderScores === 'function') renderScores(false);
    this.updateBoardViewToggleBtn();
    this.closeInGameBoardViewDropdown();

    const r3d = (typeof window !== 'undefined') ? (window._currentRenderer3D || window.boardRenderer3D) : null;
    if (mode === 'room3d-fullscreen') {
      if (r3d && typeof r3d.enterFullScreen === 'function') {
        r3d.enterFullScreen();
      }
    } else if (r3d && typeof r3d.isFullScreen === 'function' && r3d.isFullScreen()) {
      r3d.exitFullScreen();
    }
  }

  toggleInGameBoardViewDropdown() {
    const menu = document.getElementById('inGameBoardViewDropdownMenu');
    if (!menu) return;
    if (menu.classList.contains('hidden')) {
      this.openInGameBoardViewDropdown();
    } else {
      this.closeInGameBoardViewDropdown();
    }
  }

  openInGameBoardViewDropdown() {
    const menu = document.getElementById('inGameBoardViewDropdownMenu');
    const trigger = document.getElementById('btnToggleBoardView');
    if (!menu) return;
    if (typeof this.closeInGameBoardDropdown === 'function') {
      this.closeInGameBoardDropdown();
    }
    menu.classList.remove('hidden');
    if (trigger) {
      trigger.classList.add('is-active');
      trigger.setAttribute('aria-expanded', 'true');
    }
    this.updateBoardViewToggleBtn();
  }

  closeInGameBoardViewDropdown() {
    const menu = document.getElementById('inGameBoardViewDropdownMenu');
    const trigger = document.getElementById('btnToggleBoardView');
    if (!menu) return;
    menu.classList.add('hidden');
    if (trigger) {
      trigger.classList.remove('is-active');
      trigger.setAttribute('aria-expanded', 'false');
    }
  }

  select3DBoard(boardId) {
    if (!boardId) return;
    this.settings.selected3DBoardId = boardId;
    if (typeof settings !== 'undefined' && settings) {
      settings.selected3DBoardId = boardId;
    }
    const el3d = document.getElementById('opt3DBoard');
    if (el3d) el3d.value = boardId;
    const elInGame = document.getElementById('inGameOpt3DBoard');
    if (elInGame) elInGame.value = boardId;
    if (typeof saveSettingsStore === 'function') saveSettingsStore(this.settings);
    if (typeof buildBoard === 'function') buildBoard();
    if (typeof renderScores === 'function') renderScores(false);
    this.updateBoardViewToggleBtn();
    this.syncInGameBoardSelector();
    this.closeInGameBoardDropdown();
  }

  toggleInGameBoardDropdown() {
    if (typeof document === 'undefined') return;
    const menuEl = document.getElementById('inGameBoardDropdownMenu');
    if (!menuEl) return;
    const isOpen = !menuEl.classList.contains('hidden');
    if (isOpen) {
      this.closeInGameBoardDropdown();
    } else {
      this.openInGameBoardDropdown();
    }
  }

  openInGameBoardDropdown() {
    if (typeof document === 'undefined') return;
    const menuEl = document.getElementById('inGameBoardDropdownMenu');
    const triggerEl = document.getElementById('inGameBoardDropdownTrigger');
    if (!menuEl) return;
    this.syncInGameBoardSelector();
    menuEl.classList.remove('hidden');
    if (triggerEl) {
      triggerEl.classList.add('is-active');
      triggerEl.setAttribute('aria-expanded', 'true');
    }
  }

  closeInGameBoardDropdown() {
    if (typeof document === 'undefined') return;
    const menuEl = document.getElementById('inGameBoardDropdownMenu');
    const triggerEl = document.getElementById('inGameBoardDropdownTrigger');
    if (menuEl) menuEl.classList.add('hidden');
    if (triggerEl) {
      triggerEl.classList.remove('is-active');
      triggerEl.setAttribute('aria-expanded', 'false');
    }
  }

  syncInGameBoardSelector() {
    if (typeof document === 'undefined') return;
    const curBoardId = (this.settings && this.settings.selected3DBoardId) || (typeof settings !== 'undefined' && settings?.selected3DBoardId) || 'board-14';
    const brand = typeof getAppBrand === 'function' ? getAppBrand() : (this.settings?.brand || 'crazy');
    const catalog = this.CC?.Board3DCatalog || (typeof Board3DCatalog !== 'undefined' ? Board3DCatalog : null);

    // Sync native hidden select element for compatibility
    const inGameEl = document.getElementById('inGameOpt3DBoard');
    if (inGameEl) {
      if (catalog && typeof catalog.syncDropdownOptions === 'function') {
        catalog.syncDropdownOptions(inGameEl, curBoardId, brand);
      }
      inGameEl.value = curBoardId;
    }

    const curBoard = catalog && typeof catalog.getBoardById === 'function' ? catalog.getBoardById(curBoardId) : null;

    // Update trigger thumb and name
    const triggerThumb = document.getElementById('inGameBoardTriggerThumb');
    const triggerThumbWrap = document.getElementById('inGameBoardTriggerThumbWrap');
    const triggerName = document.getElementById('inGameBoardTriggerName');

    if (triggerName) {
      triggerName.textContent = curBoard ? curBoard.name : curBoardId;
    }

    if (triggerThumb && triggerThumbWrap) {
      const thumbUrl = (curBoard?.thumbnailUrl && curBoard.thumbnailUrl.trim()) ? curBoard.thumbnailUrl.trim() : '';
      if (thumbUrl) {
        triggerThumb.src = thumbUrl;
        triggerThumb.alt = curBoard?.name || '';
        triggerThumb.style.display = 'block';
        triggerThumbWrap.style.display = 'flex';
      } else {
        triggerThumb.style.display = 'none';
        triggerThumbWrap.style.display = 'none';
      }
    }

    // Render dropdown menu with thumbnail on top and name under thumbnail
    const menuEl = document.getElementById('inGameBoardDropdownMenu');
    if (menuEl && catalog && typeof catalog.getBoards === 'function') {
      const boards = catalog.getBoards(brand) || [];
      this.renderInGameBoardDropdown(menuEl, boards, curBoardId);
    }
  }

  renderInGameBoardDropdown(menuEl, boards, curBoardId) {
    if (!menuEl) return;
    let html = `
      <div class="board-3d-dropdown-header">
        <span class="board-3d-dropdown-header-title">🎲 3D Cribbage Boards</span>
        <span class="board-3d-dropdown-count">${boards.length} Boards</span>
      </div>
      <div class="board-3d-dropdown-grid">
    `;

    boards.forEach(b => {
      const isSelected = (b.id === curBoardId);
      const thumbUrl = (b.thumbnailUrl && b.thumbnailUrl.trim()) ? b.thumbnailUrl.trim() : '';
      html += `
        <div class="board-3d-dropdown-item ${isSelected ? 'is-selected' : ''}"
             role="option"
             aria-selected="${isSelected ? 'true' : 'false'}"
             data-board-id="${b.id}"
             tabindex="0"
             title="${b.name}${b.desc ? ' - ' + b.desc : ''}">
          <div class="board-3d-dropdown-item-thumb-box">
            ${thumbUrl ? `
              <img src="${thumbUrl}" alt="${b.name}" class="board-3d-dropdown-item-thumb" loading="lazy">
            ` : `
              <span class="board-3d-thumb-fallback">🎲</span>
            `}
            ${isSelected ? '<span class="board-3d-item-badge-active">✓ Active</span>' : ''}
            ${b.isCustom ? '<span class="board-3d-item-badge-custom">Custom</span>' : ''}
          </div>
          <div class="board-3d-dropdown-item-name">${b.name}</div>
        </div>
      `;
    });

    html += `
      </div>
      <div class="board-3d-dropdown-footer">
        <button type="button" class="btn-ghost board-3d-dropdown-gallery-btn" id="btnDropdownOpenGallery">
          <span>🎨 Open Gallery &amp; Custom Loader</span>
        </button>
      </div>
    `;

    menuEl.innerHTML = html;

    menuEl.querySelectorAll('img.board-3d-dropdown-item-thumb').forEach(img => {
      img.addEventListener('error', () => {
        if (img.parentElement) {
          img.parentElement.innerHTML = '<span class="board-3d-thumb-fallback">🎲</span>';
        }
      }, { once: true });
    });

    menuEl.querySelectorAll('.board-3d-dropdown-item').forEach(item => {
      item.onclick = (e) => {
        e.stopPropagation();
        const boardId = item.getAttribute('data-board-id');
        if (boardId) {
          this.select3DBoard(boardId);
        }
      };
      item.onkeydown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          const boardId = item.getAttribute('data-board-id');
          if (boardId) {
            this.select3DBoard(boardId);
          }
        }
      };
    });

    const btnGallery = menuEl.querySelector('#btnDropdownOpenGallery');
    if (btnGallery) {
      btnGallery.onclick = (e) => {
        e.stopPropagation();
        this.closeInGameBoardDropdown();
        this.showBoard3DModal();
      };
    }
  }

  showBoard3DModal(options = {}) {
    const modal = (typeof BoardSelectorModal !== 'undefined' ? BoardSelectorModal : null) || this.CC?.BoardSelectorModal || (typeof window !== 'undefined' ? window.BoardSelectorModal : null);
    if (modal) {
      const brand = (typeof options === 'string' ? options : options?.brand) || (typeof getAppBrand === 'function' ? getAppBrand() : (this.settings?.brand || 'crazy'));
      modal.show({
        brand,
        settings: this.settings,
        onSelect: ({ viewMode, boardId }) => {
          const currentMode = this.settings?.boardViewMode || (typeof settings !== 'undefined' ? settings?.boardViewMode : null) || '3d';
          const resolvedMode = viewMode || currentMode;
          this.settings.boardViewMode = resolvedMode;
          this.settings.selected3DBoardId = boardId;
          if (typeof settings !== 'undefined' && settings) {
            settings.boardViewMode = resolvedMode;
            settings.selected3DBoardId = boardId;
          }
          const elBv = document.getElementById('optBoardView');
          if (elBv) elBv.value = resolvedMode;
          const el3d = document.getElementById('opt3DBoard');
          if (el3d) el3d.value = boardId;
          const elInGame = document.getElementById('inGameOpt3DBoard');
          if (elInGame) elInGame.value = boardId;
          if (typeof saveSettingsStore === 'function') saveSettingsStore(this.settings);
          if (typeof buildBoard === 'function') buildBoard();
          if (typeof renderScores === 'function') renderScores(false);
          this.updateBoardViewToggleBtn();
          this.syncInGameBoardSelector();
        }
      });
    }
  }

  exposeGlobals(target = (typeof window !== 'undefined' ? window : globalThis)) {
    target.crazyCribbageApp = this;
    target.CrazyCribbage = target.CrazyCribbage || this.CC;
    target.CrazyCribbageApp = CrazyCribbageApp;
    target.CC = target.CrazyCribbage;
    target.RANKS = RANKS;
    target.RANK_NAME = RANK_NAME;
    target.SUITS = SUITS;
    target.SUIT_SYM = SUIT_SYM;
    target.VAL = VAL;
    target.BOARD_CFG = BOARD_CFG;
    target.BOARD_CFG_481 = BOARD_CFG_481;
    target.CLASH_DEFAULTS_481 = CLASH_DEFAULTS_481;
    target.CLASH_DEFAULTS_241 = CLASH_DEFAULTS_241;
    target.CLASH_DEFAULTS_361 = CLASH_DEFAULTS_361;
    target.CARD_BACKS = CARD_BACKS;
    target.THEMES = THEMES;
    target.CLASH_BLOCK_DEFS = CLASH_BLOCK_DEFS;
    target.PINTS_DRINKING_DEFS = PINTS_DRINKING_DEFS;
    target.ADULT_OPTIONS_LIST = ADULT_OPTIONS_LIST;
    target.DROPDOWN_DESCRIPTIONS = DROPDOWN_DESCRIPTIONS;
    target.CLASH_ITEM_METADATA = CLASH_ITEM_METADATA;
    target.CRAZY_OPTIONS_DEFS = CRAZY_OPTIONS_DEFS;

    const stateProps = ['state', 'settings', 'pegState', 'net', 'gameHands', 'theme', 'recordCache', '_suppressSettingsSave', '_isEnforcingClashNoOverlap', '_isRandomizingClash', 'sfx', 'FULL_DECK'];
    for (const prop of stateProps) {
      Object.defineProperty(target, prop, {
        get: () => this[prop],
        set: (v) => { this[prop] = v; },
        configurable: true
      });
    }

    if (this.CC) {
      const classes = ['Card', 'Deck', 'Hand', 'Player', 'Roster', 'CribbageScorer', 'PegTracker', 'GameState', 'PintsManager', 'AdultManager', 'ClashManager', 'CrazyManager', 'CrazySettings', 'CribbageAI', 'ObstacleGeometry', 'AudioManager', 'NetworkManager', 'GameEngine', 'ModalManager', 'SettingsManager', 'UIController', 'BoardRenderer', 'PintsSettings', 'AdultSettings', 'ClashSettings', 'CoachController', 'ClashPlacementModal', 'ShowModal', 'LobbyController', 'DropdownHelpManager', 'TableRenderer', 'ClashMiniGames', 'DiscardTrainer', 'HandScoreTracker', 'HandOfGloryCard', 'ClashPresets', 'PintsPresets', 'AdultPresets', 'ClashSandboxModal', 'IndexedDBStorage', 'appStorage', 'TournamentManager', 'TournamentModal', 'Board3DCatalog', 'DEFAULT_3D_BOARDS', 'BoardRenderer3D', 'BoardSelectorModal', 'CardAssassinOverlay', 'JailAnimationOverlay', 'BarAnimationOverlay', 'CannonAnimationOverlay', 'SpeedTrapAnimationOverlay', 'ScenicRouteAnimationOverlay', 'GameReplayManager', 'ReplayModal', 'AvatarManager', 'PRESET_AVATARS', 'Profile', 'RoomCatalog3D', 'CardMesh3D', 'CardEngine3D', 'SeatManager3D', 'PlayerAvatar3D', 'Room3DLoadingOverlay'];
      classes.forEach(c => { if (this.CC[c]) target[c] = this.CC[c]; });
      target.Profile = target.Profile || this.CC?.Profile || Profile;
      target.PlayerProfile = target.PlayerProfile || target.Profile;
      target.AvatarManager = target.AvatarManager || this.CC?.AvatarManager || AvatarManager;
      target.openAvatarModal = (opts) => (target.AvatarManager || AvatarManager).openAvatarModal(opts);
      target.CardAssassinOverlay = target.CardAssassinOverlay || CardAssassinOverlay;
      target.showCardAssassin = target.showCardAssassin || ((opts) => (target.CardAssassinOverlay || CardAssassinOverlay).show(opts));
      target.JailAnimationOverlay = target.JailAnimationOverlay || JailAnimationOverlay;
      target.showJailAnimation = target.showJailAnimation || ((opts) => (target.JailAnimationOverlay || JailAnimationOverlay).show(opts));
      target.BarAnimationOverlay = target.BarAnimationOverlay || BarAnimationOverlay;
      target.showBarAnimation = target.showBarAnimation || ((opts) => (target.BarAnimationOverlay || BarAnimationOverlay).show(opts));
      target.CannonAnimationOverlay = target.CannonAnimationOverlay || CannonAnimationOverlay;
      target.showCannonAnimation = target.showCannonAnimation || ((opts) => (target.CannonAnimationOverlay || CannonAnimationOverlay).show(opts));
      target.SpeedTrapAnimationOverlay = target.SpeedTrapAnimationOverlay || SpeedTrapAnimationOverlay;
      target.showSpeedTrapAnimation = target.showSpeedTrapAnimation || ((opts) => (target.SpeedTrapAnimationOverlay || SpeedTrapAnimationOverlay).show(opts));
      target.ScenicRouteAnimationOverlay = target.ScenicRouteAnimationOverlay || ScenicRouteAnimationOverlay;
      target.showScenicRouteAnimation = target.showScenicRouteAnimation || ((opts) => (target.ScenicRouteAnimationOverlay || ScenicRouteAnimationOverlay).show(opts));
      target.Room3DLoadingOverlay = target.Room3DLoadingOverlay || this.CC?.Room3DLoadingOverlay || Room3DLoadingOverlay;
      target.showRoom3DLoader = target.showRoom3DLoader || ((container, opts) => (target.Room3DLoadingOverlay || Room3DLoadingOverlay).show(container, opts));
      target.GameReplayManager = target.GameReplayManager || this.CC?.GameReplayManager || GameReplayManager;
      target.ReplayModal = target.ReplayModal || this.CC?.ReplayModal || ReplayModal;
      target.showReplaysModal = (brand = null) => {
        const b = brand || (typeof getAppBrand === 'function' ? getAppBrand() : 'all');
        return (this.CC?.ReplayModal || target.ReplayModal || ReplayModal)?.openLibrary?.(b);
      };
      target.showReplayPlayer = (replay) => (this.CC?.ReplayModal || target.ReplayModal || ReplayModal)?.open?.(replay);
      target.playCurrentGameReplay = () => (this.CC?.GameReplayManager || target.GameReplayManager || GameReplayManager)?.playCurrentGameReplay?.();
      target.shareCurrentGameReplay = () => (this.CC?.GameReplayManager || target.GameReplayManager || GameReplayManager)?.shareCurrentGameReplay?.();
      target.shareCurrentReplay = target.shareCurrentGameReplay;
      target.appStorage = appStorage;
      target.IndexedDBStorage = IndexedDBStorage;
      target.crazyManager = target.crazyManager || (this.CC.CrazyManager ? new this.CC.CrazyManager({ settings: this.settings }) : null);
      target.pintsManager = target.pintsManager || (this.CC.PintsManager ? new this.CC.PintsManager() : null);
      target.adultManager = target.adultManager || (this.CC.AdultManager ? new this.CC.AdultManager() : null);
      target.clashManager = target.clashManager || (this.CC.ClashManager ? new this.CC.ClashManager() : null);
      target.pegTracker = target.pegTracker || (this.CC.PegTracker ? new this.CC.PegTracker() : null);
      target.audioManager = target.audioManager || (this.CC.AudioManager ? new this.CC.AudioManager() : null);
      target.modalManager = target.modalManager || (this.CC.ModalManager ? new this.CC.ModalManager() : null);
      if (target.modalManager && !target.modalManager.openGuessStarterPrompt) {
        target.modalManager.openGuessStarterPrompt = openGuessStarterPrompt;
      }
      target.settingsManager = target.settingsManager || (this.CC.SettingsManager ? new this.CC.SettingsManager() : null);
      target.uiController = target.uiController || (this.CC.UIController ? new this.CC.UIController() : null);
      target.load3DBoardsJson = (source, opts) => Board3DCatalog.loadDefaultBoards(source, opts);
      target.reload3DBoardsJson = (opts) => Board3DCatalog.loadDefaultBoards(null, opts);
      if (typeof window !== 'undefined') {
        window.modalManager = target.modalManager;
        window.showModal = showModal;
        window.hideModal = hideModal;
        window.load3DBoardsJson = target.load3DBoardsJson;
        window.reload3DBoardsJson = target.reload3DBoardsJson;
      }
    }

    const proto = Object.getPrototypeOf(this);
    for (const key of Object.getOwnPropertyNames(proto)) {
      if (key !== 'constructor' && typeof this[key] === 'function' && !key.startsWith('_')) {
        target[key] = this[key];
      }
    }

    target.openGuessStarterPrompt = openGuessStarterPrompt;
    target.beginCutStarter = beginCutStarter;
    target.poneDoCutStarter = poneDoCutStarter;
    target.beginCut = beginCut;
    target.pickCut = pickCut;
    target.stepNextCut = stepNextCut;
    target.resolveCuts = resolveCuts;
    if (typeof window !== 'undefined') {
      window.openGuessStarterPrompt = openGuessStarterPrompt;
      window.beginCutStarter = beginCutStarter;
      window.poneDoCutStarter = poneDoCutStarter;
      window.beginCut = beginCut;
      window.pickCut = pickCut;
      window.stepNextCut = stepNextCut;
      window.resolveCuts = resolveCuts;
    }

    target.pegScore = this.addScore;
    target.showOptionDescriptionModal = this.showDropdownInfoModal;
    target.showDiscardTrainerModal = showDiscardTrainerModal;
    target.showPeggingTrainerModal = showPeggingTrainerModal;
    target.showCountingTrainerModal = showCountingTrainerModal;
    target.showCoachPostMortemModal = showCoachPostMortemModal;
    target.shareCoachPostMortem = shareCoachPostMortem;
    target.showGameWonModal = showGameWonModal;
    target.PeggingTrainer = CC?.PeggingTrainer || PeggingTrainer;
    target.CountingTrainer = CC?.CountingTrainer || CountingTrainer;
    target.CoachPostMortem = CC?.CoachPostMortem || CoachPostMortem;
    target.showDailyPuzzleModal = showDailyPuzzleModal;
    target.DailyPuzzleManager = CC?.DailyPuzzleManager || DailyPuzzleManager;
    target.TournamentManager = CC?.TournamentManager || TournamentManager;
    target.TournamentModal = CC?.TournamentModal || TournamentModal;
    target.showTournamentModal = (context) => (CC?.TournamentModal || TournamentModal).open(context);
    target.showBoard3DModal = (options = {}) => this.showBoard3DModal(options);
    target.select3DBoard = (boardId) => this.select3DBoard(boardId);
    target.syncInGameBoardSelector = () => this.syncInGameBoardSelector();
    target.toggleBoardViewMode = () => this.toggleBoardViewMode();
    target.selectBoardViewMode = (mode) => this.selectBoardViewMode(mode);
    target.toggleInGameBoardViewDropdown = () => this.toggleInGameBoardViewDropdown();
    target.openInGameBoardViewDropdown = () => this.openInGameBoardViewDropdown();
    target.closeInGameBoardViewDropdown = () => this.closeInGameBoardViewDropdown();
    target.updateBoardViewToggleBtn = () => this.updateBoardViewToggleBtn();
    target.minimizeBoard = () => this.minimizeBoard();
    target.maximizeBoard = () => this.maximizeBoard();
    target.restoreBoard = () => this.restoreBoard();
    target.toggleBoardMinimize = () => this.toggleBoardMinimize();
    target.toggleBoardMaximize = () => this.toggleBoardMaximize();
    target.toggle3DRoomFullScreen = () => this.toggle3DRoomFullScreen();
    target.is3DRoomFullScreen = () => this.is3DRoomFullScreen();
    target.updateBoardSizeButtons = () => this.updateBoardSizeButtons();
    target.getAppBrand = getAppBrand;
    target.isAgeVerified = isAgeVerified;
    target.setAgeVerified = setAgeVerified;
    target.checkInitialAgeGate = checkInitialAgeGate;
    target.showAgeGateModal = showAgeGateModal;
    target.hideAgeGateModal = hideAgeGateModal;
  }
}


// Attach all functions as prototype methods of CrazyCribbageApp
CrazyCribbageApp.prototype.getAppBrand = getAppBrand;
CrazyCribbageApp.prototype.getClashBoardCfg = getClashBoardCfg;
CrazyCribbageApp.prototype.getTrackXY = getTrackXY;
CrazyCribbageApp.prototype.parseGameMode = parseGameMode;
CrazyCribbageApp.prototype.serializeGameMode = serializeGameMode;
CrazyCribbageApp.prototype.isOnline = isOnline;
CrazyCribbageApp.prototype.isHvH = isHvH;
CrazyCribbageApp.prototype.isTwoHuman = isTwoHuman;
CrazyCribbageApp.prototype.isSoloVsPairs = isSoloVsPairs;
CrazyCribbageApp.prototype.is3Player1vs2 = is3Player1vs2;
CrazyCribbageApp.prototype.is5Player1vs4 = is5Player1vs4;
CrazyCribbageApp.prototype.is7Player1vs6 = is7Player1vs6;
CrazyCribbageApp.prototype.isFixedTeams = isFixedTeams;
CrazyCribbageApp.prototype.isPartnership = isPartnership;
CrazyCribbageApp.prototype.isShiftingPartnership = isShiftingPartnership;
CrazyCribbageApp.prototype.mySeat = mySeat;
CrazyCribbageApp.prototype.getPlayerCount = getPlayerCount;
CrazyCribbageApp.prototype.getRoster = getRoster;
CrazyCribbageApp.prototype.getActiveSeats = getActiveSeats;
CrazyCribbageApp.prototype.getActiveSeatIds = getActiveSeatIds;
CrazyCribbageApp.prototype.getSeatTeam = getSeatTeam;
CrazyCribbageApp.prototype.getShiftingRoles = getShiftingRoles;
CrazyCribbageApp.prototype.getShiftingPartner = getShiftingPartner;
CrazyCribbageApp.prototype.nextSeat = nextSeat;
CrazyCribbageApp.prototype.prevSeat = prevSeat;
CrazyCribbageApp.prototype.getSeat = getSeat;
CrazyCribbageApp.prototype.getSeatHand = getSeatHand;
CrazyCribbageApp.prototype.setSeatHand = setSeatHand;
CrazyCribbageApp.prototype.getSeatKept = getSeatKept;
CrazyCribbageApp.prototype.setSeatKept = setSeatKept;
CrazyCribbageApp.prototype.getSeatHandModel = getSeatHandModel;
CrazyCribbageApp.prototype.getSeatKeptModel = getSeatKeptModel;
CrazyCribbageApp.prototype.getCribModel = getCribModel;
CrazyCribbageApp.prototype.getPileModel = getPileModel;
CrazyCribbageApp.prototype.seatName = seatName;
CrazyCribbageApp.prototype.pName = pName;
CrazyCribbageApp.prototype.seatColor = seatColor;
CrazyCribbageApp.prototype.isSeatCpu = isSeatCpu;
CrazyCribbageApp.prototype.isSeatHuman = isSeatHuman;
CrazyCribbageApp.prototype.scoreKey = scoreKey;
CrazyCribbageApp.prototype.isInPegThroughZone = isInPegThroughZone;
CrazyCribbageApp.prototype.isInLast5PegOut = isInLast5PegOut;
CrazyCribbageApp.prototype.isInBunker = isInBunker;
CrazyCribbageApp.prototype.getNoPassingLanes = getNoPassingLanes;
CrazyCribbageApp.prototype.isInNoPassingLane = isInNoPassingLane;
CrazyCribbageApp.prototype.getNoPassingLaneForHole = getNoPassingLaneForHole;
CrazyCribbageApp.prototype.getNoPassingLaneMaxScore = getNoPassingLaneMaxScore;
CrazyCribbageApp.prototype.getJumpConfigs = getJumpConfigs;
CrazyCribbageApp.prototype.getJumpForHole = getJumpForHole;
CrazyCribbageApp.prototype.getJailConfigs = getJailConfigs;
CrazyCribbageApp.prototype.getJailForHole = getJailForHole;
CrazyCribbageApp.prototype.getJailStepXY = getJailStepXY;
CrazyCribbageApp.prototype.getBarConfigs = getBarConfigs;
CrazyCribbageApp.prototype.getBarForHole = getBarForHole;
CrazyCribbageApp.prototype.getBarStepXY = getBarStepXY;
CrazyCribbageApp.prototype.getTimeoutConfigs = getTimeoutConfigs;
CrazyCribbageApp.prototype.getTimeoutForHole = getTimeoutForHole;
CrazyCribbageApp.prototype.getTimeoutStepXY = getTimeoutStepXY;
CrazyCribbageApp.prototype.getStopSignConfigs = getStopSignConfigs;
CrazyCribbageApp.prototype.getStopSignForHole = getStopSignForHole;
CrazyCribbageApp.prototype.getDetourConfigs = getDetourConfigs;
CrazyCribbageApp.prototype.getDetourForHole = getDetourForHole;
CrazyCribbageApp.prototype.catmullRomPath = catmullRomPath;
CrazyCribbageApp.prototype.computeLoopGeometry = computeLoopGeometry;
CrazyCribbageApp.prototype.getDetourStepXY = getDetourStepXY;
CrazyCribbageApp.prototype.getRoundAboutConfigs = getRoundAboutConfigs;
CrazyCribbageApp.prototype.getRoundAboutForHole = getRoundAboutForHole;
CrazyCribbageApp.prototype.getRoundAboutStepXY = getRoundAboutStepXY;
CrazyCribbageApp.prototype.getSignConfigs = getSignConfigs;
CrazyCribbageApp.prototype.getSignBlockForHole = getSignBlockForHole;
CrazyCribbageApp.prototype.getDiceConfigs = getDiceConfigs;
CrazyCribbageApp.prototype.getDiceForHole = getDiceForHole;
CrazyCribbageApp.prototype.getWheelConfigs = getWheelConfigs;
CrazyCribbageApp.prototype.getWheelForHole = getWheelForHole;
CrazyCribbageApp.prototype.isDealerTeam = isDealerTeam;
CrazyCribbageApp.prototype.seatDiscardsToDeck = seatDiscardsToDeck;
CrazyCribbageApp.prototype.applySeatLabels = applySeatLabels;
CrazyCribbageApp.prototype.passTo = passTo;
CrazyCribbageApp.prototype.scheduleOppPlay = scheduleOppPlay;
CrazyCribbageApp.prototype.announcePlayTurn = announcePlayTurn;
CrazyCribbageApp.prototype.getClashDefaults = getClashDefaults;
CrazyCribbageApp.prototype.getSkunkDefaults = getSkunkDefaults;
CrazyCribbageApp.prototype.setCardBack = setCardBack;
CrazyCribbageApp.prototype.initCardBackPicker = initCardBackPicker;
CrazyCribbageApp.prototype.makeDeck = makeDeck;
CrazyCribbageApp.prototype.isWild = isWild;
CrazyCribbageApp.prototype.rankOf = rankOf;
CrazyCribbageApp.prototype.suitOf = suitOf;
CrazyCribbageApp.prototype.cardFits = cardFits;
CrazyCribbageApp.prototype.assignCard = assignCard;
CrazyCribbageApp.prototype.shuffle = shuffle;
CrazyCribbageApp.prototype.takeAnim = takeAnim;
CrazyCribbageApp.prototype.resetAnims = resetAnims;
CrazyCribbageApp.prototype.faceMarkup = faceMarkup;
CrazyCribbageApp.prototype.cardEl = cardEl;
CrazyCribbageApp.prototype.cardKey = cardKey;
CrazyCribbageApp.prototype.applyHighlights = applyHighlights;
CrazyCribbageApp.prototype.combinations = combinations;
CrazyCribbageApp.prototype.isConsecutiveRun = isConsecutiveRun;
CrazyCribbageApp.prototype.scoreShowCore = scoreShowCore;
CrazyCribbageApp.prototype.scoreShow = scoreShow;
CrazyCribbageApp.prototype.pegPoints = pegPoints;
CrazyCribbageApp.prototype.pegTotal = pegTotal;
CrazyCribbageApp.prototype.pegPotential = pegPotential;
CrazyCribbageApp.prototype.expectedShowExact = expectedShowExact;
CrazyCribbageApp.prototype.chooseDiscardFast = chooseDiscardFast;
CrazyCribbageApp.prototype.chooseDiscard = chooseDiscard;
CrazyCribbageApp.prototype.cpuKnownIds = cpuKnownIds;
CrazyCribbageApp.prototype.unseenCards = unseenCards;
CrazyCribbageApp.prototype.sampleOppHand = sampleOppHand;
CrazyCribbageApp.prototype.removeCard = removeCard;
CrazyCribbageApp.prototype.fastPlayCard = fastPlayCard;
CrazyCribbageApp.prototype.rolloutPeg = rolloutPeg;
CrazyCribbageApp.prototype.raceBonus = raceBonus;
CrazyCribbageApp.prototype.cardLabel = cardLabel;
CrazyCribbageApp.prototype.choosePlay = choosePlay;
CrazyCribbageApp.prototype.log = log;
CrazyCribbageApp.prototype.showSolver = showSolver;
CrazyCribbageApp.prototype.setStatus = setStatus;
CrazyCribbageApp.prototype.updateTurnPrompt = updateTurnPrompt;
CrazyCribbageApp.prototype.isBonusMessage = isBonusMessage;
CrazyCribbageApp.prototype.fancyCall = fancyCall;
CrazyCribbageApp.prototype.fancyPegPhrase = fancyPegPhrase;
CrazyCribbageApp.prototype.announceCombos = announceCombos;
CrazyCribbageApp.prototype.updateCountGlow = updateCountGlow;
CrazyCribbageApp.prototype.streetName = streetName;
CrazyCribbageApp.prototype.showStreetBanner = showStreetBanner;
CrazyCribbageApp.prototype.showScoreToast = showScoreToast;
CrazyCribbageApp.prototype.addScore = addScore;
CrazyCribbageApp.prototype.skunkResult = skunkResult;
CrazyCribbageApp.prototype.checkGameOver = checkGameOver;
CrazyCribbageApp.prototype.checkChutesAndLadders = checkChutesAndLadders;
CrazyCribbageApp.prototype.handleSignsLanding = handleSignsLanding;
CrazyCribbageApp.prototype.openSignPickerModal = openSignPickerModal;
CrazyCribbageApp.prototype.onSignCardClick = onSignCardClick;
CrazyCribbageApp.prototype.chooseSign = chooseSign;
CrazyCribbageApp.prototype.resumeGameAfterSign = resumeGameAfterSign;
CrazyCribbageApp.prototype.executeSign = executeSign;
CrazyCribbageApp.prototype.openDiceRollModal = openDiceRollModal;
CrazyCribbageApp.prototype.performDiceRoll = performDiceRoll;
CrazyCribbageApp.prototype.onDicePickPlayer = onDicePickPlayer;
CrazyCribbageApp.prototype.applyDiceResult = applyDiceResult;
CrazyCribbageApp.prototype.openWheelSpinModal = openWheelSpinModal;
CrazyCribbageApp.prototype.drawWheelCanvas = drawWheelCanvas;
CrazyCribbageApp.prototype.performWheelSpin = performWheelSpin;
CrazyCribbageApp.prototype.updateSignsBillboard = updateSignsBillboard;
CrazyCribbageApp.prototype.getActiveTracks = getActiveTracks;
CrazyCribbageApp.prototype.getPegGradId = getPegGradId;
CrazyCribbageApp.prototype.updateScoreChips = updateScoreChips;
CrazyCribbageApp.prototype.getBoardSignature = getBoardSignature;
CrazyCribbageApp.prototype.buildBoard = buildBoard;
CrazyCribbageApp.prototype.createCrazyCribbageLegend = createCrazyCribbageLegend;
CrazyCribbageApp.prototype.getBoardTooltipContext = getBoardTooltipContext;
CrazyCribbageApp.prototype.getBoardTooltipData = getBoardTooltipData;
CrazyCribbageApp.prototype.hideFancyTooltip = hideFancyTooltip;
CrazyCribbageApp.prototype.initBoardFancyTooltips = initBoardFancyTooltips;
CrazyCribbageApp.prototype.renderScores = renderScores;
CrazyCribbageApp.prototype.sortCards = sortCards;
CrazyCribbageApp.prototype.render = render;
CrazyCribbageApp.prototype.onOppSeatDiscardCard = onOppSeatDiscardCard;
CrazyCribbageApp.prototype.onCpuCard = onCpuCard;
CrazyCribbageApp.prototype.onYouCard = onYouCard;
CrazyCribbageApp.prototype.hideWildPick = hideWildPick;
CrazyCribbageApp.prototype.openWildPick = openWildPick;
CrazyCribbageApp.prototype.stylePickBtn = stylePickBtn;
CrazyCribbageApp.prototype.drawWildPick = drawWildPick;
CrazyCribbageApp.prototype.confirmWildAs = confirmWildAs;
CrazyCribbageApp.prototype.cpuAssignWilds = cpuAssignWilds;
CrazyCribbageApp.prototype.beginDiscard = beginDiscard;
CrazyCribbageApp.prototype.showWait = showWait;
CrazyCribbageApp.prototype.hideWait = hideWait;
CrazyCribbageApp.prototype.afterStarterAssigned = afterStarterAssigned;
CrazyCribbageApp.prototype.getAppBrand = getAppBrand;
CrazyCribbageApp.prototype.getSettingsKey = getSettingsKey;
CrazyCribbageApp.prototype.getRecordKey = getRecordKey;
CrazyCribbageApp.prototype.isAgeVerified = isAgeVerified;
CrazyCribbageApp.prototype.setAgeVerified = setAgeVerified;
CrazyCribbageApp.prototype.showAgeGateModal = showAgeGateModal;
CrazyCribbageApp.prototype.hideAgeGateModal = hideAgeGateModal;
CrazyCribbageApp.prototype.confirmAgeGate = confirmAgeGate;
CrazyCribbageApp.prototype.exitAgeGateToSafeBrand = exitAgeGateToSafeBrand;
CrazyCribbageApp.prototype.getThemeKey = getThemeKey;
CrazyCribbageApp.prototype.getDefaultTheme = getDefaultTheme;
CrazyCribbageApp.prototype.applyTheme = applyTheme;
CrazyCribbageApp.prototype.toggleTheme = toggleTheme;
CrazyCribbageApp.prototype.variantParts = variantParts;
CrazyCribbageApp.prototype.variantKeyFrom = variantKeyFrom;
CrazyCribbageApp.prototype.variantLabelFrom = variantLabelFrom;
CrazyCribbageApp.prototype.loadRecords = loadRecords;
CrazyCribbageApp.prototype.saveRecords = saveRecords;
CrazyCribbageApp.prototype.getVariantRecord = getVariantRecord;
CrazyCribbageApp.prototype.recordGameResult = recordGameResult;
CrazyCribbageApp.prototype.currentFormVariant = currentFormVariant;
CrazyCribbageApp.prototype.renderRecordBox = renderRecordBox;
CrazyCribbageApp.prototype.getActiveClashBlocks = getActiveClashBlocks;
CrazyCribbageApp.prototype.checkClashCollision = checkClashCollision;
CrazyCribbageApp.prototype.findAvailableClashHole = findAvailableClashHole;
CrazyCribbageApp.prototype.updateAllClashDropdowns = updateAllClashDropdowns;
CrazyCribbageApp.prototype.enforceClashNoOverlap = enforceClashNoOverlap;
CrazyCribbageApp.prototype.sanitizeClashBlockPlacements = sanitizeClashBlockPlacements;
CrazyCribbageApp.prototype.getStreetOrTurnName = getStreetOrTurnName;
CrazyCribbageApp.prototype.toggleSignsConfig = toggleSignsConfig;
CrazyCribbageApp.prototype.toggleDetourConfig = toggleDetourConfig;
CrazyCribbageApp.prototype.toggleRoundAboutConfig = toggleRoundAboutConfig;
CrazyCribbageApp.prototype.toggleDiceConfig = toggleDiceConfig;
CrazyCribbageApp.prototype.toggleWheelConfig = toggleWheelConfig;
CrazyCribbageApp.prototype.updateJumpOptionLabels = updateJumpOptionLabels;
CrazyCribbageApp.prototype.readSignsBlocksFromForm = readSignsBlocksFromForm;
CrazyCribbageApp.prototype.onSignBillboardClick = onSignBillboardClick;
CrazyCribbageApp.prototype.toggleSkunkMarkersConfig = toggleSkunkMarkersConfig;
CrazyCribbageApp.prototype.onSkunkMarkerFieldChange = onSkunkMarkerFieldChange;
CrazyCribbageApp.prototype.onDoubleSkunkMarkerFieldChange = onDoubleSkunkMarkerFieldChange;
CrazyCribbageApp.prototype.randomizePintsOptions = randomizePintsOptions;
CrazyCribbageApp.prototype.getDefaultAdultSettings = getDefaultAdultSettings;
CrazyCribbageApp.prototype.getAdultOptionDef = getAdultOptionDef;
CrazyCribbageApp.prototype.toggleAdultOptionConfig = toggleAdultOptionConfig;
CrazyCribbageApp.prototype.updateAdultOptionRows = updateAdultOptionRows;
CrazyCribbageApp.prototype.onAdultOptionPegsChange = onAdultOptionPegsChange;
CrazyCribbageApp.prototype.onAdultOptionFieldChange = onAdultOptionFieldChange;
CrazyCribbageApp.prototype.getAdultOptionConfigs = getAdultOptionConfigs;
CrazyCribbageApp.prototype.randomizeAdultOptions = randomizeAdultOptions;
CrazyCribbageApp.prototype.showAdultTimerModal = showAdultTimerModal;
CrazyCribbageApp.prototype.startAdultTimer = startAdultTimer;
CrazyCribbageApp.prototype.closeAdultTimerModal = closeAdultTimerModal;
CrazyCribbageApp.prototype.showAdultStripModal = showAdultStripModal;
CrazyCribbageApp.prototype.closeAdultStripModal = closeAdultStripModal;
CrazyCribbageApp.prototype.resumeGameAfterAdultStrip = resumeGameAfterAdultStrip;
CrazyCribbageApp.prototype.resumeGameAfterAdultTimer = resumeGameAfterAdultTimer;
CrazyCribbageApp.prototype.showPintsBonusModal = showPintsBonusModal;
CrazyCribbageApp.prototype.closePintsBonusModal = closePintsBonusModal;
CrazyCribbageApp.prototype.resumeGameAfterPintsBonus = resumeGameAfterPintsBonus;
CrazyCribbageApp.prototype.readFormSettings = readFormSettings;
CrazyCribbageApp.prototype.updateTargetOptions = updateTargetOptions;
CrazyCribbageApp.prototype.applyFormSettings = applyFormSettings;
CrazyCribbageApp.prototype.applyBrandVisibility = applyBrandVisibility;
CrazyCribbageApp.prototype.updateCheckAllBonusesState = updateCheckAllBonusesState;
CrazyCribbageApp.prototype.saveSettingsStore = saveSettingsStore;
CrazyCribbageApp.prototype.loadSettingsStore = loadSettingsStore;
CrazyCribbageApp.prototype.setNetStatus = setNetStatus;
CrazyCribbageApp.prototype.getLobbyRosterData = getLobbyRosterData;
CrazyCribbageApp.prototype.updateNetRoster = updateNetRoster;
CrazyCribbageApp.prototype.broadcastLobbyUpdate = broadcastLobbyUpdate;
CrazyCribbageApp.prototype.serializeState = serializeState;
CrazyCribbageApp.prototype.getSanitizedStateForSeat = getSanitizedStateForSeat;
CrazyCribbageApp.prototype.netSend = netSend;
CrazyCribbageApp.prototype.netPush = netPush;
CrazyCribbageApp.prototype.applyRemoteState = applyRemoteState;
CrazyCribbageApp.prototype.handleSeatDiscardIntent = handleSeatDiscardIntent;
CrazyCribbageApp.prototype.confirmDraw = confirmDraw;
CrazyCribbageApp.prototype.hostApplyIntent = hostApplyIntent;
CrazyCribbageApp.prototype.handleClientHello = handleClientHello;
CrazyCribbageApp.prototype.handleClientDisconnect = handleClientDisconnect;
CrazyCribbageApp.prototype.handleHostDisconnect = handleHostDisconnect;
CrazyCribbageApp.prototype.isSeatOnline = isSeatOnline;
CrazyCribbageApp.prototype.isMultiHuman = isMultiHuman;
CrazyCribbageApp.prototype.replacePlayerWithAi = replacePlayerWithAi;
CrazyCribbageApp.prototype.handlePlayerOfflinePrompt = handlePlayerOfflinePrompt;
CrazyCribbageApp.prototype.bindHostClientConn = bindHostClientConn;
CrazyCribbageApp.prototype.makeRoomCode = makeRoomCode;
CrazyCribbageApp.prototype.getJoinUrl = getJoinUrl;
CrazyCribbageApp.prototype.showHostInviteLink = showHostInviteLink;
CrazyCribbageApp.prototype.copyRoomInviteLink = copyRoomInviteLink;
CrazyCribbageApp.prototype.shareRoomInvite = shareRoomInvite;
CrazyCribbageApp.prototype.shareRoomWhatsApp = shareRoomWhatsApp;
CrazyCribbageApp.prototype.shareRoomEmail = shareRoomEmail;
CrazyCribbageApp.prototype.fallbackCopyText = fallbackCopyText;
CrazyCribbageApp.prototype.hostRoom = hostRoom;
CrazyCribbageApp.prototype.joinRoom = joinRoom;
CrazyCribbageApp.prototype.startGameOnline = startGameOnline;
CrazyCribbageApp.prototype.startGame = startGame;
CrazyCribbageApp.prototype.resetMatch = resetMatch;
CrazyCribbageApp.prototype.playAgain = playAgain;
CrazyCribbageApp.prototype.beginCut = beginCut;
CrazyCribbageApp.prototype.renderCutFan = renderCutFan;
CrazyCribbageApp.prototype.resolveCuts = resolveCuts;
CrazyCribbageApp.prototype.pickCut = pickCut;
CrazyCribbageApp.prototype.stepNextCut = stepNextCut;
CrazyCribbageApp.prototype.doCut = doCut;
CrazyCribbageApp.prototype.newHand = newHand;
CrazyCribbageApp.prototype.beginCutStarter = beginCutStarter;
CrazyCribbageApp.prototype.poneDoCutStarter = poneDoCutStarter;
CrazyCribbageApp.prototype.openGuessStarterPrompt = openGuessStarterPrompt;
CrazyCribbageApp.prototype.startPlayAfterDiscards = startPlayAfterDiscards;
CrazyCribbageApp.prototype.finishDeal = finishDeal;
CrazyCribbageApp.prototype.startStarter = startStarter;
CrazyCribbageApp.prototype.finishStarterRitual = finishStarterRitual;
CrazyCribbageApp.prototype.confirmP2Discard = confirmP2Discard;
CrazyCribbageApp.prototype.onPickCribCard = onPickCribCard;
CrazyCribbageApp.prototype.startHumanPickCrib = startHumanPickCrib;
CrazyCribbageApp.prototype.confirmPickCrib = confirmPickCrib;
CrazyCribbageApp.prototype.finishPickYourCrib = finishPickYourCrib;
CrazyCribbageApp.prototype.cpuPickFromPone = cpuPickFromPone;
CrazyCribbageApp.prototype.cpuDoPickYourCrib = cpuDoPickYourCrib;
CrazyCribbageApp.prototype.startCanadianDoublesFlow = startCanadianDoublesFlow;
CrazyCribbageApp.prototype.startDiscardOrPickFlow = startDiscardOrPickFlow;
CrazyCribbageApp.prototype.confirmDiscard = confirmDiscard;
CrazyCribbageApp.prototype.findNextPlayerWithPlay = findNextPlayerWithPlay;
CrazyCribbageApp.prototype.settleGo = settleGo;
CrazyCribbageApp.prototype.playCard = playCard;
CrazyCribbageApp.prototype.cpuSeatPlay = cpuSeatPlay;
CrazyCribbageApp.prototype.cpuTurn = cpuTurn;
CrazyCribbageApp.prototype.miniCardHtml = miniCardHtml;
CrazyCribbageApp.prototype.showPegPoints = showPegPoints;
CrazyCribbageApp.prototype.showHandPoints = showHandPoints;
CrazyCribbageApp.prototype.setShowMeter = setShowMeter;
CrazyCribbageApp.prototype.famousFanfare = famousFanfare;
CrazyCribbageApp.prototype.finishPlay = finishPlay;
CrazyCribbageApp.prototype.cpuGuessCrib = cpuGuessCrib;
CrazyCribbageApp.prototype.selectStolenChip = selectStolenChip;
CrazyCribbageApp.prototype.openStolenCribPrompt = openStolenCribPrompt;
CrazyCribbageApp.prototype.sendStolenCribGuess = sendStolenCribGuess;
CrazyCribbageApp.prototype.resolveStolenCribGuess = resolveStolenCribGuess;
CrazyCribbageApp.prototype.openTwistedCribbageModal = openTwistedCribbageModal;
CrazyCribbageApp.prototype.resolveTwistedCribbageSplit = resolveTwistedCribbageSplit;
CrazyCribbageApp.prototype.beginShowAct = beginShowAct;
CrazyCribbageApp.prototype.finishShowAct = finishShowAct;
CrazyCribbageApp.prototype.tickShowLine = tickShowLine;
CrazyCribbageApp.prototype.saveHand = saveHand;
CrazyCribbageApp.prototype.openShowModal = openShowModal;
CrazyCribbageApp.prototype.showModal = showModal;
CrazyCribbageApp.prototype.hideModal = hideModal;
CrazyCribbageApp.prototype.showOverlay = showOverlay;
CrazyCribbageApp.prototype.hideOverlay = hideOverlay;
CrazyCribbageApp.prototype.closeModal = closeModal;
CrazyCribbageApp.prototype.closeOverlay = closeOverlay;
CrazyCribbageApp.prototype.setupCloseButtonCompat = setupCloseButtonCompat;
CrazyCribbageApp.prototype.showConfirmModal = showConfirmModal;
CrazyCribbageApp.prototype.backToMenu = backToMenu;
CrazyCribbageApp.prototype.returnToMenu = returnToMenu;
CrazyCribbageApp.prototype.showDropdownInfoModal = showDropdownInfoModal;
CrazyCribbageApp.prototype.selectOptionFromModal = selectOptionFromModal;
CrazyCribbageApp.prototype.initDropdownLabels = initDropdownLabels;
CrazyCribbageApp.prototype.syncPlayerCountUi = syncPlayerCountUi;
CrazyCribbageApp.prototype.updateCribbageClashOptionRanges = updateCribbageClashOptionRanges;
CrazyCribbageApp.prototype.randomizeCrazyCribbageBonuses = randomizeCrazyCribbageBonuses;
CrazyCribbageApp.prototype.randomizeCribbageClashOptions = randomizeCribbageClashOptions;
CrazyCribbageApp.prototype.getJumpTriggerLength = getJumpTriggerLength;
CrazyCribbageApp.prototype.initClashPlacementButtons = initClashPlacementButtons;
CrazyCribbageApp.prototype.syncClashPlacementButton = syncClashPlacementButton;
CrazyCribbageApp.prototype.syncAllClashPlacementButtons = syncAllClashPlacementButtons;
CrazyCribbageApp.prototype.getAllPlacedClashItems = getAllPlacedClashItems;
CrazyCribbageApp.prototype.openClashPlacementModal = openClashPlacementModal;
CrazyCribbageApp.prototype.closeClashPlacementModal = closeClashPlacementModal;
CrazyCribbageApp.prototype.onConfirmClashPlacement = onConfirmClashPlacement;
CrazyCribbageApp.prototype.renderClashPlacementBoard = renderClashPlacementBoard;
CrazyCribbageApp.prototype.uncheckCribbageClashOptions = uncheckCribbageClashOptions;
CrazyCribbageApp.prototype.uncheckPintsOptions = uncheckPintsOptions;
CrazyCribbageApp.prototype.initTableSplitter = initTableSplitter;
CrazyCribbageApp.prototype.initThemeButtons = initThemeButtons;
CrazyCribbageApp.prototype.initSoundButton = initSoundButton;
CrazyCribbageApp.prototype.checkInitialAgeGate = checkInitialAgeGate;
CrazyCribbageApp.prototype.setupNetUi = setupNetUi;
CrazyCribbageApp.prototype.handleZoneOptionChange = handleZoneOptionChange;
CrazyCribbageApp.prototype.setupCheckAllBonuses = setupCheckAllBonuses;
CrazyCribbageApp.prototype.toggleMenuSettings = toggleMenuSettings;
CrazyCribbageApp.prototype.toggleMenuVariants = toggleMenuVariants;
CrazyCribbageApp.prototype.hideMenuOtions = hideMenuOtions;
CrazyCribbageApp.prototype.hideMenuOptions = hideMenuOtions;
CrazyCribbageApp.prototype.hideMenuVariants = hideMenuVariants;
CrazyCribbageApp.prototype.setupCloseButtonEvents = setupCloseButtonEvents;
CrazyCribbageApp.prototype.analyzeKeepEV = analyzeKeepEV;
CrazyCribbageApp.prototype.updateCoachDisplay = updateCoachDisplay;
CrazyCribbageApp.prototype.showIntro = showIntro;
CrazyCribbageApp.prototype.showRecordsModal = showRecordsModal;
CrazyCribbageApp.prototype.resetGameStatistics = resetGameStatistics;
CrazyCribbageApp.prototype.showShareModal = showShareModal;
CrazyCribbageApp.prototype.showStartGameModal = showStartGameModal;
CrazyCribbageApp.prototype.hideStartGameModal = hideStartGameModal;
CrazyCribbageApp.prototype.confirmAndStartGame = confirmAndStartGame;
CrazyCribbageApp.prototype.showJoinModal = showJoinModal;
CrazyCribbageApp.prototype.copyShareLink = copyShareLink;
CrazyCribbageApp.prototype.triggerNativeShare = triggerNativeShare;
CrazyCribbageApp.prototype.initAutoJoin = initAutoJoin;
CrazyCribbageApp.prototype.updateMobileDeviceClass = updateMobileDeviceClass;
CrazyCribbageApp.prototype.showDiscardTrainerModal = showDiscardTrainerModal;
CrazyCribbageApp.prototype.showPeggingTrainerModal = showPeggingTrainerModal;
CrazyCribbageApp.prototype.showCountingTrainerModal = showCountingTrainerModal;
CrazyCribbageApp.prototype.showCoachPostMortemModal = showCoachPostMortemModal;
CrazyCribbageApp.prototype.shareCoachPostMortem = shareCoachPostMortem;
CrazyCribbageApp.prototype.PeggingTrainer = CC?.PeggingTrainer || PeggingTrainer;
CrazyCribbageApp.prototype.CountingTrainer = CC?.CountingTrainer || CountingTrainer;
CrazyCribbageApp.prototype.CoachPostMortem = CC?.CoachPostMortem || CoachPostMortem;
CrazyCribbageApp.prototype.showDailyPuzzleModal = showDailyPuzzleModal;
CrazyCribbageApp.prototype.DailyPuzzleManager = CC?.DailyPuzzleManager || DailyPuzzleManager;
CrazyCribbageApp.prototype.showTrophyRoomModal = showTrophyRoomModal;
CrazyCribbageApp.prototype.showReplaysModal = showReplaysModal;
CrazyCribbageApp.prototype.showGameSummaryModal = showGameSummaryModal;
CrazyCribbageApp.prototype.showGameWonModal = showGameWonModal;
CrazyCribbageApp.prototype.showHandOfGloryModal = showHandOfGloryModal;
CrazyCribbageApp.prototype.closeHandOfGloryModal = closeHandOfGloryModal;
CrazyCribbageApp.prototype.openClashSandbox = openClashSandbox;
CrazyCribbageApp.prototype.applyClashPreset = applyClashPreset;
CrazyCribbageApp.prototype.triggerReaction = (id) => ReactionManager.triggerReaction(id, { sfx, net, netSend });
CrazyCribbageApp.prototype.triggerBanter = (id) => ReactionManager.triggerBanter(id, { sfx, net, netSend });
CrazyCribbageApp.prototype.toggleReactionWheel = () => ReactionManager.toggleReactionWheel();
CrazyCribbageApp.prototype.SeriesTracker = SeriesTracker;
CrazyCribbageApp.prototype.ReactionManager = ReactionManager;
CrazyCribbageApp.prototype.BANTER_ITEMS = BANTER_ITEMS;
CrazyCribbageApp.prototype.HandScoreTracker = CC?.HandScoreTracker || HandScoreTracker;
CrazyCribbageApp.prototype.HandOfGloryCard = CC?.HandOfGloryCard || HandOfGloryCard;
CrazyCribbageApp.prototype.ClashPresets = CC?.ClashPresets || ClashPresets;
CrazyCribbageApp.prototype.PintsPresets = CC?.PintsPresets || PintsPresets;
CrazyCribbageApp.prototype.AdultPresets = CC?.AdultPresets || AdultPresets;
CrazyCribbageApp.prototype.ClashSandboxModal = CC?.ClashSandboxModal || ClashSandboxModal;
CrazyCribbageApp.prototype.showPlayerOfflineModal = (seatId, seatName, onConfirm, onWait, onCancel) => ModalManager.showPlayerOfflineModal(seatId, seatName, onConfirm, onWait, onCancel);
CrazyCribbageApp.prototype.showPickAiReplacementModal = (seatId, seatName, onSelectAi) => ModalManager.showPickAiReplacementModal(seatId, seatName, onSelectAi);
CrazyCribbageApp.prototype.cancelGame = cancelGameAndReturnToMenu;
CrazyCribbageApp.prototype.cancelGameAndReturnToMenu = cancelGameAndReturnToMenu;
CrazyCribbageApp.prototype.IndexedDBStorage = IndexedDBStorage;
CrazyCribbageApp.prototype.appStorage = appStorage;
CrazyCribbageApp.prototype.toggleBoardViewMode = function() {
  const currentMode = (this.settings && this.settings.boardViewMode) || 'room3d-fullscreen';
  let newMode = '3d';
  if (currentMode === 'room3d-fullscreen') newMode = 'room3d';
  else if (currentMode === 'room3d') newMode = '3d';
  else if (currentMode === '3d') newMode = '2d';
  else if (currentMode === '2d') newMode = 'none';
  else if (currentMode === 'none') newMode = 'room3d';
  this.selectBoardViewMode(newMode);
};
CrazyCribbageApp.prototype.selectBoardViewMode = function(mode) {
  if (!mode) return;
  this.settings.boardViewMode = mode;
  if (typeof settings !== 'undefined' && settings) {
    settings.boardViewMode = mode;
  }
  if (typeof window !== 'undefined' && window.settings) {
    window.settings.boardViewMode = mode;
  }
  const elBv = document.getElementById('optBoardView');
  if (elBv) elBv.value = mode;
  const elBvm = document.getElementById('optBoardViewModal');
  if (elBvm) elBvm.value = mode;
  const elInGame = document.getElementById('inGameOptBoardView');
  if (elInGame) elInGame.value = mode;

  if (typeof saveSettingsStore === 'function') saveSettingsStore(this.settings);
  if (typeof buildBoard === 'function') buildBoard();
  if (typeof renderScores === 'function') renderScores(false);
  this.updateBoardViewToggleBtn();
  this.closeInGameBoardViewDropdown();

  const r3d = (typeof window !== 'undefined') ? (window._currentRenderer3D || window.boardRenderer3D) : null;
  if (mode === 'room3d-fullscreen') {
    if (r3d && typeof r3d.enterFullScreen === 'function') {
      r3d.enterFullScreen();
    }
  } else if (r3d && typeof r3d.isFullScreen === 'function' && r3d.isFullScreen()) {
    r3d.exitFullScreen();
  }
};
CrazyCribbageApp.prototype.toggleInGameBoardViewDropdown = function() {
  const menu = document.getElementById('inGameBoardViewDropdownMenu');
  if (!menu) return;
  if (menu.classList.contains('hidden')) {
    this.openInGameBoardViewDropdown();
  } else {
    this.closeInGameBoardViewDropdown();
  }
};
CrazyCribbageApp.prototype.openInGameBoardViewDropdown = function() {
  const menu = document.getElementById('inGameBoardViewDropdownMenu');
  const trigger = document.getElementById('btnToggleBoardView');
  if (!menu) return;
  if (typeof this.closeInGameBoardDropdown === 'function') {
    this.closeInGameBoardDropdown();
  }
  menu.classList.remove('hidden');
  if (trigger) {
    trigger.classList.add('is-active');
    trigger.setAttribute('aria-expanded', 'true');
  }
  this.updateBoardViewToggleBtn();
};
CrazyCribbageApp.prototype.closeInGameBoardViewDropdown = function() {
  const menu = document.getElementById('inGameBoardViewDropdownMenu');
  const trigger = document.getElementById('btnToggleBoardView');
  if (!menu) return;
  menu.classList.add('hidden');
  if (trigger) {
    trigger.classList.remove('is-active');
    trigger.setAttribute('aria-expanded', 'false');
  }
};
CrazyCribbageApp.prototype.zoomIn3DBoard = function(step) {
  if (typeof window !== 'undefined' && window._currentRenderer3D?.zoomIn) {
    return window._currentRenderer3D.zoomIn(step);
  }
};
CrazyCribbageApp.prototype.zoomOut3DBoard = function(step) {
  if (typeof window !== 'undefined' && window._currentRenderer3D?.zoomOut) {
    return window._currentRenderer3D.zoomOut(step);
  }
};
CrazyCribbageApp.prototype.togglePanMode3DBoard = function(enable) {
  if (typeof window !== 'undefined' && window._currentRenderer3D?.setPanMode) {
    return window._currentRenderer3D.setPanMode(enable);
  }
};
CrazyCribbageApp.prototype.pan3DBoard = function(deltaX, deltaY) {
  if (typeof window !== 'undefined' && window._currentRenderer3D?.pan) {
    return window._currentRenderer3D.pan(deltaX, deltaY);
  }
};
CrazyCribbageApp.prototype.getBoardAngles = function() {
  if (typeof window !== 'undefined' && window._currentRenderer3D?.getCameraAngles) {
    return window._currentRenderer3D.getCameraAngles();
  }
  if (CC?.BoardRenderer3D?.getCameraAngles) {
    return CC.BoardRenderer3D.getCameraAngles();
  }
  console.warn('[CrazyCribbage] No active 3D board found. Ensure 3D board view is selected.');
  return null;
};
CrazyCribbageApp.prototype.getCameraAngles = function() {
  return this.getBoardAngles();
};
CrazyCribbageApp.prototype.minimizeBoard = function() {
  return CC?.BoardRenderer?.minimizeBoard ? CC.BoardRenderer.minimizeBoard() : BoardRenderer.minimizeBoard();
};
CrazyCribbageApp.prototype.maximizeBoard = function() {
  return CC?.BoardRenderer?.maximizeBoard ? CC.BoardRenderer.maximizeBoard() : BoardRenderer.maximizeBoard();
};
CrazyCribbageApp.prototype.restoreBoard = function() {
  return CC?.BoardRenderer?.restoreBoard ? CC.BoardRenderer.restoreBoard() : BoardRenderer.restoreBoard();
};
CrazyCribbageApp.prototype.toggleBoardMinimize = function() {
  return CC?.BoardRenderer?.toggleBoardMinimize ? CC.BoardRenderer.toggleBoardMinimize() : BoardRenderer.toggleBoardMinimize();
};
CrazyCribbageApp.prototype.toggleBoardMaximize = function() {
  return CC?.BoardRenderer?.toggleBoardMaximize ? CC.BoardRenderer.toggleBoardMaximize() : BoardRenderer.toggleBoardMaximize();
};
CrazyCribbageApp.prototype.getBoardDisplayState = function() {
  return (CC?.BoardRenderer?.getBoardDisplayState ? CC.BoardRenderer.getBoardDisplayState() : BoardRenderer.getBoardDisplayState()) || 'normal';
};
CrazyCribbageApp.prototype.setBoardDisplayState = function(state) {
  return CC?.BoardRenderer?.setBoardDisplayState ? CC.BoardRenderer.setBoardDisplayState(state) : BoardRenderer.setBoardDisplayState(state);
};
CrazyCribbageApp.prototype.updateBoardSizeButtons = function() {
  return CC?.BoardRenderer?.updateBoardSizeButtons ? CC.BoardRenderer.updateBoardSizeButtons() : BoardRenderer.updateBoardSizeButtons();
};
CrazyCribbageApp.prototype.toggle3DRoomFullScreen = function() {
  const r3d = (typeof window !== 'undefined') ? (window._currentRenderer3D || window.boardRenderer3D) : null;
  if (r3d && typeof r3d.toggleFullScreen === 'function') {
    return r3d.toggleFullScreen();
  } else if (typeof CC !== 'undefined' && CC.BoardRenderer?.toggle3DRoomFullScreen) {
    return CC.BoardRenderer.toggle3DRoomFullScreen();
  } else if (typeof BoardRenderer !== 'undefined' && BoardRenderer.toggle3DRoomFullScreen) {
    return BoardRenderer.toggle3DRoomFullScreen();
  }
};
CrazyCribbageApp.prototype.is3DRoomFullScreen = function() {
  const r3d = (typeof window !== 'undefined') ? (window._currentRenderer3D || window.boardRenderer3D) : null;
  if (r3d && typeof r3d.isFullScreen === 'function') {
    return r3d.isFullScreen();
  } else if (typeof CC !== 'undefined' && CC.BoardRenderer?.is3DRoomFullScreen) {
    return CC.BoardRenderer.is3DRoomFullScreen();
  } else if (typeof BoardRenderer !== 'undefined' && BoardRenderer.is3DRoomFullScreen) {
    return BoardRenderer.is3DRoomFullScreen();
  }
  return false;
};
if (typeof window !== 'undefined') {
  window.zoomIn3DBoard = (step) => (window.crazyCribbageApp ? window.crazyCribbageApp.zoomIn3DBoard(step) : null);
  window.zoomOut3DBoard = (step) => (window.crazyCribbageApp ? window.crazyCribbageApp.zoomOut3DBoard(step) : null);
  window.togglePanMode3DBoard = (enable) => (window.crazyCribbageApp ? window.crazyCribbageApp.togglePanMode3DBoard(enable) : null);
  window.pan3DBoard = (dx, dy) => (window.crazyCribbageApp ? window.crazyCribbageApp.pan3DBoard(dx, dy) : null);
  window.toggleBoardViewMode = () => (window.crazyCribbageApp ? window.crazyCribbageApp.toggleBoardViewMode() : null);
  window.selectBoardViewMode = (mode) => (window.crazyCribbageApp ? window.crazyCribbageApp.selectBoardViewMode(mode) : null);
  window.toggleInGameBoardViewDropdown = () => (window.crazyCribbageApp ? window.crazyCribbageApp.toggleInGameBoardViewDropdown() : null);
  window.closeInGameBoardViewDropdown = () => (window.crazyCribbageApp ? window.crazyCribbageApp.closeInGameBoardViewDropdown() : null);
  window.select3DBoard = (boardId) => (window.crazyCribbageApp ? window.crazyCribbageApp.select3DBoard(boardId) : null);
  window.syncInGameBoardSelector = () => (window.crazyCribbageApp ? window.crazyCribbageApp.syncInGameBoardSelector() : null);
  window.toggleInGameBoardDropdown = () => (window.crazyCribbageApp ? window.crazyCribbageApp.toggleInGameBoardDropdown() : null);
  window.closeInGameBoardDropdown = () => (window.crazyCribbageApp ? window.crazyCribbageApp.closeInGameBoardDropdown() : null);
  window.minimizeBoard = () => (window.crazyCribbageApp ? window.crazyCribbageApp.minimizeBoard() : (CC?.BoardRenderer?.minimizeBoard ? CC.BoardRenderer.minimizeBoard() : BoardRenderer.minimizeBoard()));
  window.maximizeBoard = () => (window.crazyCribbageApp ? window.crazyCribbageApp.maximizeBoard() : (CC?.BoardRenderer?.maximizeBoard ? CC.BoardRenderer.maximizeBoard() : BoardRenderer.maximizeBoard()));
  window.restoreBoard = () => (window.crazyCribbageApp ? window.crazyCribbageApp.restoreBoard() : (CC?.BoardRenderer?.restoreBoard ? CC.BoardRenderer.restoreBoard() : BoardRenderer.restoreBoard()));
  window.toggleBoardMinimize = () => (window.crazyCribbageApp ? window.crazyCribbageApp.toggleBoardMinimize() : (CC?.BoardRenderer?.toggleBoardMinimize ? CC.BoardRenderer.toggleBoardMinimize() : BoardRenderer.toggleBoardMinimize()));
  window.toggleBoardMaximize = () => (window.crazyCribbageApp ? window.crazyCribbageApp.toggleBoardMaximize() : (CC?.BoardRenderer?.toggleBoardMaximize ? CC.BoardRenderer.toggleBoardMaximize() : BoardRenderer.toggleBoardMaximize()));
  window.toggle3DRoomFullScreen = () => (window.crazyCribbageApp ? window.crazyCribbageApp.toggle3DRoomFullScreen() : (CC?.BoardRenderer?.toggle3DRoomFullScreen ? CC.BoardRenderer.toggle3DRoomFullScreen() : (BoardRenderer?.toggle3DRoomFullScreen ? BoardRenderer.toggle3DRoomFullScreen() : (window._currentRenderer3D?.toggleFullScreen ? window._currentRenderer3D.toggleFullScreen() : null))));
  window.is3DRoomFullScreen = () => (window.crazyCribbageApp ? window.crazyCribbageApp.is3DRoomFullScreen() : (CC?.BoardRenderer?.is3DRoomFullScreen ? CC.BoardRenderer.is3DRoomFullScreen() : (BoardRenderer?.is3DRoomFullScreen ? BoardRenderer.is3DRoomFullScreen() : (window._currentRenderer3D?.isFullScreen ? window._currentRenderer3D.isFullScreen() : false))));
  window.showBoard3DModal = (options = {}) => (window.crazyCribbageApp ? window.crazyCribbageApp.showBoard3DModal(options) : BoardSelectorModal.show(options));
  window.toggleMenuSettings = toggleMenuSettings;
  window.toggleMenuVariants = toggleMenuVariants;
  window.hideMenuOptions = hideMenuOtions;
  window.hideMenuOtions = hideMenuOtions;
  window.hideMenuVariants = hideMenuVariants;
  window.cancelGame = cancelGameAndReturnToMenu;
  window.cancelGameAndReturnToMenu = cancelGameAndReturnToMenu;
  window.returnToMenu = returnToMenu;
  window.backToMenu = backToMenu;
  window.showConfirmModal = showConfirmModal;
  window.showStartGameModal = showStartGameModal;
  window.hideStartGameModal = hideStartGameModal;
  window.confirmAndStartGame = confirmAndStartGame;
  window.showTrophyRoomModal = showTrophyRoomModal;
  window.showTrophyRoom = showTrophyRoomModal;
  window.showGameSummaryModal = showGameSummaryModal;
  window.showGameWonModal = showGameWonModal;
  window.showHandOfGloryModal = showHandOfGloryModal;
  window.closeHandOfGloryModal = closeHandOfGloryModal;
  window.showPlayerOfflineModal = (seatId, seatName, onConfirm, onWait, onCancel) => ModalManager.showPlayerOfflineModal(seatId, seatName, onConfirm, onWait, onCancel);
  window.showPickAiReplacementModal = (seatId, seatName, onSelectAi) => ModalManager.showPickAiReplacementModal(seatId, seatName, onSelectAi);
  window.openClashSandbox = openClashSandbox;
  window.openPintsSandbox = (opts) => ClashSandboxModal.open({ brand: 'pints', ...opts });
  window.openAdultSandbox = (opts) => ClashSandboxModal.open({ brand: 'adult', ...opts });
  window.applyClashPreset = applyClashPreset;
  window.applyPintsPreset = (key, ctx) => (window.PintsPresets || PintsPresets).applyPreset(key, ctx);
  window.applyAdultPreset = (key, ctx) => (window.AdultPresets || AdultPresets).applyPreset(key, ctx);
  window.triggerReaction = (id) => ReactionManager.triggerReaction(id, { sfx, net, netSend });
  window.triggerBanter = (id) => ReactionManager.triggerBanter(id, { sfx, net, netSend });
  window.toggleReactionWheel = () => ReactionManager.toggleReactionWheel();
  window.ReactionManager = ReactionManager;
  window.BANTER_ITEMS = BANTER_ITEMS;
  window.SeriesTracker = SeriesTracker;
  window.TournamentManager = TournamentManager;
  window.TournamentModal = TournamentModal;
  window.showTournamentModal = (context) => TournamentModal.open(context);
  window.HandScoreTracker = CC?.HandScoreTracker || HandScoreTracker || window.HandScoreTracker;
  window.HandOfGloryCard = CC?.HandOfGloryCard || HandOfGloryCard || window.HandOfGloryCard;
  window.ClashPresets = CC?.ClashPresets || ClashPresets || window.ClashPresets;
  window.PintsPresets = CC?.PintsPresets || PintsPresets || window.PintsPresets;
  window.AdultPresets = CC?.AdultPresets || AdultPresets || window.AdultPresets;
  window.ClashSandboxModal = CC?.ClashSandboxModal || ClashSandboxModal || window.ClashSandboxModal;
  window.IndexedDBStorage = IndexedDBStorage;
  window.appStorage = appStorage;
  window.isSeatCpu = isSeatCpu;
  window.isSeatHuman = isSeatHuman;
  window.isSeatOnline = isSeatOnline;
  window.isMultiHuman = isMultiHuman;
  window.replacePlayerWithAi = replacePlayerWithAi;
  window.handlePlayerOfflinePrompt = handlePlayerOfflinePrompt;
  window.handleHostDisconnect = handleHostDisconnect;
  window.checkGameOver = checkGameOver;
  window.checkChutesAndLadders = checkChutesAndLadders;
  window.GameReplayManager = CC?.GameReplayManager || GameReplayManager || window.GameReplayManager;
  window.ReplayModal = CC?.ReplayModal || ReplayModal || window.ReplayModal;
  window.showReplaysModal = (brand = null) => {
    const b = brand || (typeof getAppBrand === 'function' ? getAppBrand() : 'all');
    return (window.ReplayModal || CC?.ReplayModal || ReplayModal)?.openLibrary?.(b);
  };
  window.showReplayPlayer = (replay) => (window.ReplayModal || CC?.ReplayModal || ReplayModal)?.open?.(replay);
  window.playCurrentGameReplay = () => (window.GameReplayManager || CC?.GameReplayManager || GameReplayManager)?.playCurrentGameReplay?.();
  window.shareCurrentGameReplay = () => (window.GameReplayManager || CC?.GameReplayManager || GameReplayManager)?.shareCurrentGameReplay?.();
  window.shareCurrentReplay = window.shareCurrentGameReplay;
  window.load3DBoardsJson = (source, opts) => Board3DCatalog.loadDefaultBoards(source, opts);
  window.reload3DBoardsJson = (opts) => Board3DCatalog.loadDefaultBoards(null, opts);
}

CrazyCribbageApp.prototype.load3DBoardsJson = function(source, opts) {
  return Board3DCatalog.loadDefaultBoards(source, opts);
};
CrazyCribbageApp.prototype.reload3DBoardsJson = function(opts) {
  return Board3DCatalog.loadDefaultBoards(null, opts);
};

